import React, { useState, useEffect, Suspense, useMemo, useRef, useCallback, useLayoutEffect, Fragment, forwardRef, Children, cloneElement, createElement } from 'react';
import classNames from 'classnames';
import Tippy from '@tippyjs/react';
import { Button as Button$1, Modal as Modal$1, ModalHeader, ModalBody, ModalFooter, Alert, Badge, NavItem, NavLink, FormFeedback, FormGroup, Label, Input as Input$1, Pagination as Pagination$1, PaginationItem, PaginationLink, InputGroupText, InputGroup, ListGroup, ListGroupItem, ListGroupItemHeading, Card as Card$1, CardHeader, CardBody, CardFooter, Nav, Row, Col, Progress } from 'reactstrap';
import { uniqueId, take, omit, pick, merge, range, debounce, isEqual, uniq, flatMap, get, constant, noop, chunk, throttle } from 'lodash';
import { NavLink as NavLink$1 } from 'react-router-dom';
import { isRequired, JarbField } from '@42.nl/jarb-final-form';
import getDisplayName from 'react-display-name';
import { useErrorsForValidator, clearErrorsForValidator } from '@42.nl/react-error-store';
import { Field, FormSpy, Form } from 'react-final-form';
import { emptyPage, pageOf } from '@42.nl/spring-connect';
import { Field as Field$1 } from '@42.nl/final-form-field-validation';
import RTMaskedInput from 'react-text-mask';
import TextareaAutosize from 'react-textarea-autosize';
import ReactQuill from 'react-quill';
import ReactDatetimeClass from 'react-datetime';
import moment from 'moment';
import { Typeahead, AsyncTypeahead } from 'react-bootstrap-typeahead';
import AvatarEditor from 'react-avatar-editor';
import picaFn from 'pica';
import { Buffer } from 'buffer';
import { SketchPicker } from 'react-color';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { isFragment } from 'react-is';

/**
 * Bootstrap-like Tooltip component based on the Tippy.js library.
 */
function Tooltip({ children, placement = 'top', content, offset = 0, distance = 7, interactive, maxWidth = 350, tag = 'span', className, style }) {
    const Tag = tag;
    return (React.createElement(Tippy, { className: "border-0", content: content, placement: placement, offset: [offset, distance], interactive: interactive, maxWidth: maxWidth },
        React.createElement(Tag, { className: className, style: Object.assign({ outline: 0 }, style), tabIndex: 0, role: "button" }, children)));
}

/**
 * Avatar is a component which shows a circular image with any element underneath.
 * Use it for instance for showing the profile image of a logged-in user.
 */
function Avatar({ size, className, alt, src, children }) {
    const sizeClass = size ? `avatar-${size}` : null;
    const classes = classNames('avatar', sizeClass, className);
    return (React.createElement("span", { className: classes },
        React.createElement(Tooltip, { placement: "top", content: alt, distance: tooltipDistanceFromSize(size) },
            React.createElement("span", { className: "img-placeholder" },
                React.createElement("img", { alt: alt, src: src })),
            children)));
}
function tooltipDistanceFromSize(size) {
    switch (size) {
        case 'lg':
            return 42;
        case 'md':
            return 32;
        case 'sm':
            return 22;
    }
    return 7;
}

/**
 * AvatarStack is a component which shows a row of overlapped avatars.
 * Use it for instance when you want to show multiple thumbnails of users in an organized way.
 */
function AvatarStack({ children, className }) {
    return (React.createElement("div", { className: classNames('avatar-stack', className) }, children));
}

/**
 * Spinner is a component which is used as a loading indicator.
 *
 * Use it for example when data is fetching during navigation to a page.
 */
function Spinner({ className, color, size }) {
    return (React.createElement("svg", { className: classNames('spinner', className), width: size, height: size, viewBox: "25 25 50 50" },
        React.createElement("circle", { className: "path", fill: "none", stroke: color, cx: "50", cy: "50", r: "20", strokeWidth: "4", strokeMiterlimit: "10" })));
}

/**
 * Returns whether the element is hovered.
 *
 * @example
 *
 * ```tsx
 * function HoveredComponent() {
 *   const { hover, hoverEvents } = useHover();
 *
 *    return (
 *      <div className={hover ? 'active' : ''} {...hoverEvents} />
 *    );
 * }
 */
function useHover() {
    const [hover, setHover] = useState(false);
    function onMouseEnter() {
        setHover(true);
    }
    function onMouseLeave() {
        setHover(false);
    }
    return [hover, { onMouseEnter, onMouseLeave }];
}

/**
 * The Icon is a small wrapper around a material design icon.
 *
 * Useful for making sure your icon is typesafe via TypeScript.
 */
function Icon({ className, onClick, icon, id, color, hoverColor, disabled, size, autoFocus, variant = 'filled' }) {
    const [hover, hoverEvents] = useHover();
    const colorCssClasses = {};
    if (hoverColor && hover && !disabled && onClick !== undefined) {
        colorCssClasses[`text-${hoverColor}`] = true;
    }
    else if (color) {
        colorCssClasses[`text-${color}`] = true;
    }
    const classes = classNames('icon', 'material-icons', `material-icons__${variant}`, colorCssClasses, className, {
        clickable: !disabled && onClick,
        'icon--disabled': disabled
    });
    const style = size ? { fontSize: size } : undefined;
    return (React.createElement("i", Object.assign({ id: id, style: style, onClick: (event) => {
            if (!disabled && onClick) {
                onClick(event);
            }
        }, onKeyUp: (event) => {
            if (event.key === 'Enter') {
                const target = event.target;
                target.click();
            }
        }, className: classes }, hoverEvents, { autoFocus: autoFocus, tabIndex: onClick ? 0 : undefined }), icon));
}

/*
  I extracted this list from this json file:
  https://fonts.google.com/metadata/icons
*/
const icons = [
    '123',
    '360',
    '10k',
    '10mp',
    '11mp',
    '12mp',
    '13mp',
    '14mp',
    '15mp',
    '16mp',
    '17mp',
    '18_up_rating',
    '18mp',
    '19mp',
    '1k',
    '1k_plus',
    '1x_mobiledata',
    '20mp',
    '21mp',
    '22mp',
    '23mp',
    '24mp',
    '2k',
    '2k_plus',
    '2mp',
    '30fps',
    '30fps_select',
    '3d_rotation',
    '3g_mobiledata',
    '3k',
    '3k_plus',
    '3mp',
    '3p',
    '4g_mobiledata',
    '4g_plus_mobiledata',
    '4k',
    '4k_plus',
    '4mp',
    '5g',
    '5k',
    '5k_plus',
    '5mp',
    '60fps',
    '60fps_select',
    '6_ft_apart',
    '6k',
    '6k_plus',
    '6mp',
    '7k',
    '7k_plus',
    '7mp',
    '8k',
    '8k_plus',
    '8mp',
    '9k',
    '9k_plus',
    '9mp',
    'abc',
    'ac_unit',
    'access_alarm',
    'access_alarms',
    'access_time',
    'access_time_filled',
    'accessibility',
    'accessibility_new',
    'accessible',
    'accessible_forward',
    'account_balance',
    'account_balance_wallet',
    'account_box',
    'account_circle',
    'account_tree',
    'ad_units',
    'adb',
    'add',
    'add_a_photo',
    'add_alarm',
    'add_alert',
    'add_box',
    'add_business',
    'add_call',
    'add_card',
    'add_chart',
    'add_circle',
    'add_circle_outline',
    'add_comment',
    'add_home',
    'add_home_work',
    'add_ic_call',
    'add_link',
    'add_location',
    'add_location_alt',
    'add_moderator',
    'add_photo_alternate',
    'add_reaction',
    'add_road',
    'add_shopping_cart',
    'add_task',
    'add_to_drive',
    'add_to_home_screen',
    'add_to_photos',
    'add_to_queue',
    'addchart',
    'adf_scanner',
    'adjust',
    'admin_panel_settings',
    'adobe',
    'ads_click',
    'agriculture',
    'air',
    'airline_seat_flat',
    'airline_seat_flat_angled',
    'airline_seat_individual_suite',
    'airline_seat_legroom_extra',
    'airline_seat_legroom_normal',
    'airline_seat_legroom_reduced',
    'airline_seat_recline_extra',
    'airline_seat_recline_normal',
    'airline_stops',
    'airlines',
    'airplane_ticket',
    'airplanemode_active',
    'airplanemode_inactive',
    'airplanemode_off',
    'airplanemode_on',
    'airplay',
    'airport_shuttle',
    'alarm',
    'alarm_add',
    'alarm_off',
    'alarm_on',
    'album',
    'align_horizontal_center',
    'align_horizontal_left',
    'align_horizontal_right',
    'align_vertical_bottom',
    'align_vertical_center',
    'align_vertical_top',
    'all_inbox',
    'all_inclusive',
    'all_out',
    'alt_route',
    'alternate_email',
    'amp_stories',
    'analytics',
    'anchor',
    'android',
    'animation',
    'announcement',
    'aod',
    'apartment',
    'api',
    'app_blocking',
    'app_registration',
    'app_settings_alt',
    'app_shortcut',
    'apple',
    'approval',
    'apps',
    'apps_outage',
    'architecture',
    'archive',
    'area_chart',
    'arrow_back',
    'arrow_back_ios',
    'arrow_back_ios_new',
    'arrow_circle_down',
    'arrow_circle_left',
    'arrow_circle_right',
    'arrow_circle_up',
    'arrow_downward',
    'arrow_drop_down',
    'arrow_drop_down_circle',
    'arrow_drop_up',
    'arrow_forward',
    'arrow_forward_ios',
    'arrow_left',
    'arrow_outward',
    'arrow_right',
    'arrow_right_alt',
    'arrow_upward',
    'art_track',
    'article',
    'aspect_ratio',
    'assessment',
    'assignment',
    'assignment_add',
    'assignment_ind',
    'assignment_late',
    'assignment_return',
    'assignment_returned',
    'assignment_turned_in',
    'assist_walker',
    'assistant',
    'assistant_direction',
    'assistant_navigation',
    'assistant_photo',
    'assured_workload',
    'atm',
    'attach_email',
    'attach_file',
    'attach_money',
    'attachment',
    'attractions',
    'attribution',
    'audio_file',
    'audiotrack',
    'auto_awesome',
    'auto_awesome_mosaic',
    'auto_awesome_motion',
    'auto_delete',
    'auto_fix_high',
    'auto_fix_normal',
    'auto_fix_off',
    'auto_graph',
    'auto_mode',
    'auto_stories',
    'autofps_select',
    'autorenew',
    'av_timer',
    'baby_changing_station',
    'back_hand',
    'backpack',
    'backspace',
    'backup',
    'backup_table',
    'badge',
    'bakery_dining',
    'balance',
    'balcony',
    'ballot',
    'bar_chart',
    'barcode_reader',
    'batch_prediction',
    'bathroom',
    'bathtub',
    'battery_0_bar',
    'battery_1_bar',
    'battery_20',
    'battery_2_bar',
    'battery_30',
    'battery_3_bar',
    'battery_4_bar',
    'battery_50',
    'battery_5_bar',
    'battery_60',
    'battery_6_bar',
    'battery_80',
    'battery_90',
    'battery_alert',
    'battery_charging_20',
    'battery_charging_30',
    'battery_charging_50',
    'battery_charging_60',
    'battery_charging_80',
    'battery_charging_90',
    'battery_charging_full',
    'battery_full',
    'battery_saver',
    'battery_std',
    'battery_unknown',
    'beach_access',
    'bed',
    'bedroom_baby',
    'bedroom_child',
    'bedroom_parent',
    'bedtime',
    'bedtime_off',
    'beenhere',
    'bento',
    'bike_scooter',
    'biotech',
    'blender',
    'blind',
    'blinds',
    'blinds_closed',
    'block',
    'block_flipped',
    'bloodtype',
    'bluetooth',
    'bluetooth_audio',
    'bluetooth_connected',
    'bluetooth_disabled',
    'bluetooth_drive',
    'bluetooth_searching',
    'blur_circular',
    'blur_linear',
    'blur_off',
    'blur_on',
    'bolt',
    'book',
    'book_online',
    'bookmark',
    'bookmark_add',
    'bookmark_added',
    'bookmark_border',
    'bookmark_outline',
    'bookmark_remove',
    'bookmarks',
    'border_all',
    'border_bottom',
    'border_clear',
    'border_color',
    'border_horizontal',
    'border_inner',
    'border_left',
    'border_outer',
    'border_right',
    'border_style',
    'border_top',
    'border_vertical',
    'boy',
    'branding_watermark',
    'breakfast_dining',
    'brightness_1',
    'brightness_2',
    'brightness_3',
    'brightness_4',
    'brightness_5',
    'brightness_6',
    'brightness_7',
    'brightness_auto',
    'brightness_high',
    'brightness_low',
    'brightness_medium',
    'broadcast_on_home',
    'broadcast_on_personal',
    'broken_image',
    'browse_gallery',
    'browser_not_supported',
    'browser_updated',
    'brunch_dining',
    'brush',
    'bubble_chart',
    'bug_report',
    'build',
    'build_circle',
    'bungalow',
    'burst_mode',
    'bus_alert',
    'business',
    'business_center',
    'cabin',
    'cable',
    'cached',
    'cake',
    'calculate',
    'calendar_month',
    'calendar_today',
    'calendar_view_day',
    'calendar_view_month',
    'calendar_view_week',
    'call',
    'call_end',
    'call_made',
    'call_merge',
    'call_missed',
    'call_missed_outgoing',
    'call_received',
    'call_split',
    'call_to_action',
    'camera',
    'camera_alt',
    'camera_enhance',
    'camera_front',
    'camera_indoor',
    'camera_outdoor',
    'camera_rear',
    'camera_roll',
    'cameraswitch',
    'campaign',
    'cancel',
    'cancel_presentation',
    'cancel_schedule_send',
    'candlestick_chart',
    'car_crash',
    'car_rental',
    'car_repair',
    'card_giftcard',
    'card_membership',
    'card_travel',
    'carpenter',
    'cases',
    'casino',
    'cast',
    'cast_connected',
    'cast_for_education',
    'castle',
    'catching_pokemon',
    'category',
    'celebration',
    'cell_tower',
    'cell_wifi',
    'center_focus_strong',
    'center_focus_weak',
    'chair',
    'chair_alt',
    'chalet',
    'change_circle',
    'change_history',
    'charging_station',
    'chat',
    'chat_bubble',
    'chat_bubble_outline',
    'check',
    'check_box',
    'check_box_outline_blank',
    'check_circle',
    'check_circle_outline',
    'checklist',
    'checklist_rtl',
    'checkroom',
    'chevron_left',
    'chevron_right',
    'child_care',
    'child_friendly',
    'chrome_reader_mode',
    'church',
    'circle',
    'circle_notifications',
    'class',
    'clean_hands',
    'cleaning_services',
    'clear',
    'clear_all',
    'close',
    'close_fullscreen',
    'closed_caption',
    'closed_caption_disabled',
    'closed_caption_off',
    'cloud',
    'cloud_circle',
    'cloud_done',
    'cloud_download',
    'cloud_off',
    'cloud_queue',
    'cloud_sync',
    'cloud_upload',
    'cloudy_snowing',
    'co2',
    'co_present',
    'code',
    'code_off',
    'coffee',
    'coffee_maker',
    'collections',
    'collections_bookmark',
    'color_lens',
    'colorize',
    'comment',
    'comment_bank',
    'comments_disabled',
    'commit',
    'commute',
    'compare',
    'compare_arrows',
    'compass_calibration',
    'compost',
    'compress',
    'computer',
    'confirmation_num',
    'confirmation_number',
    'connect_without_contact',
    'connected_tv',
    'connecting_airports',
    'construction',
    'contact_emergency',
    'contact_mail',
    'contact_page',
    'contact_phone',
    'contact_support',
    'contactless',
    'contacts',
    'content_copy',
    'content_cut',
    'content_paste',
    'content_paste_go',
    'content_paste_off',
    'content_paste_search',
    'contrast',
    'control_camera',
    'control_point',
    'control_point_duplicate',
    'conveyor_belt',
    'cookie',
    'copy',
    'copy_all',
    'copyright',
    'coronavirus',
    'corporate_fare',
    'cottage',
    'countertops',
    'create',
    'create_new_folder',
    'credit_card',
    'credit_card_off',
    'credit_score',
    'crib',
    'crisis_alert',
    'crop',
    'crop_16_9',
    'crop_3_2',
    'crop_5_4',
    'crop_7_5',
    'crop_din',
    'crop_free',
    'crop_landscape',
    'crop_original',
    'crop_portrait',
    'crop_rotate',
    'crop_square',
    'cruelty_free',
    'css',
    'currency_bitcoin',
    'currency_exchange',
    'currency_franc',
    'currency_lira',
    'currency_pound',
    'currency_ruble',
    'currency_rupee',
    'currency_yen',
    'currency_yuan',
    'curtains',
    'curtains_closed',
    'cut',
    'cyclone',
    'dangerous',
    'dark_mode',
    'dashboard',
    'dashboard_customize',
    'data_array',
    'data_exploration',
    'data_object',
    'data_saver_off',
    'data_saver_on',
    'data_thresholding',
    'data_usage',
    'dataset',
    'dataset_linked',
    'date_range',
    'deblur',
    'deck',
    'dehaze',
    'delete',
    'delete_forever',
    'delete_outline',
    'delete_sweep',
    'delivery_dining',
    'density_large',
    'density_medium',
    'density_small',
    'departure_board',
    'description',
    'deselect',
    'design_services',
    'desk',
    'desktop_access_disabled',
    'desktop_mac',
    'desktop_windows',
    'details',
    'developer_board',
    'developer_board_off',
    'developer_mode',
    'device_hub',
    'device_thermostat',
    'device_unknown',
    'devices',
    'devices_fold',
    'devices_other',
    'dew_point',
    'dialer_sip',
    'dialpad',
    'diamond',
    'difference',
    'dining',
    'dinner_dining',
    'directions',
    'directions_bike',
    'directions_boat',
    'directions_boat_filled',
    'directions_bus',
    'directions_bus_filled',
    'directions_car',
    'directions_car_filled',
    'directions_ferry',
    'directions_off',
    'directions_railway',
    'directions_railway_filled',
    'directions_run',
    'directions_subway',
    'directions_subway_filled',
    'directions_train',
    'directions_transit',
    'directions_transit_filled',
    'directions_walk',
    'dirty_lens',
    'disabled_by_default',
    'disabled_visible',
    'disc_full',
    'discord',
    'discount',
    'display_settings',
    'diversity_1',
    'diversity_2',
    'diversity_3',
    'dnd_forwardslash',
    'dns',
    'do_disturb',
    'do_disturb_alt',
    'do_disturb_off',
    'do_disturb_on',
    'do_not_disturb',
    'do_not_disturb_alt',
    'do_not_disturb_off',
    'do_not_disturb_on',
    'do_not_disturb_on_total_silence',
    'do_not_step',
    'do_not_touch',
    'dock',
    'document_scanner',
    'domain',
    'domain_add',
    'domain_disabled',
    'domain_verification',
    'done',
    'done_all',
    'done_outline',
    'donut_large',
    'donut_small',
    'door_back',
    'door_front',
    'door_sliding',
    'doorbell',
    'double_arrow',
    'downhill_skiing',
    'download',
    'download_done',
    'download_for_offline',
    'downloading',
    'drafts',
    'drag_handle',
    'drag_indicator',
    'draw',
    'drive_eta',
    'drive_file_move',
    'drive_file_move_outline',
    'drive_file_move_rtl',
    'drive_file_rename_outline',
    'drive_folder_upload',
    'dry',
    'dry_cleaning',
    'duo',
    'dvr',
    'dynamic_feed',
    'dynamic_form',
    'e_mobiledata',
    'earbuds',
    'earbuds_battery',
    'east',
    'eco',
    'edgesensor_high',
    'edgesensor_low',
    'edit',
    'edit_attributes',
    'edit_calendar',
    'edit_document',
    'edit_location',
    'edit_location_alt',
    'edit_note',
    'edit_notifications',
    'edit_off',
    'edit_road',
    'edit_square',
    'egg',
    'egg_alt',
    'eject',
    'elderly',
    'elderly_woman',
    'electric_bike',
    'electric_bolt',
    'electric_car',
    'electric_meter',
    'electric_moped',
    'electric_rickshaw',
    'electric_scooter',
    'electrical_services',
    'elevator',
    'email',
    'emergency',
    'emergency_recording',
    'emergency_share',
    'emoji_emotions',
    'emoji_events',
    'emoji_flags',
    'emoji_food_beverage',
    'emoji_nature',
    'emoji_objects',
    'emoji_people',
    'emoji_symbols',
    'emoji_transportation',
    'energy_savings_leaf',
    'engineering',
    'enhance_photo_translate',
    'enhanced_encryption',
    'equalizer',
    'error',
    'error_outline',
    'escalator',
    'escalator_warning',
    'euro',
    'euro_symbol',
    'ev_station',
    'event',
    'event_available',
    'event_busy',
    'event_note',
    'event_repeat',
    'event_seat',
    'exit_to_app',
    'expand',
    'expand_circle_down',
    'expand_less',
    'expand_more',
    'explicit',
    'explore',
    'explore_off',
    'exposure',
    'exposure_minus_1',
    'exposure_minus_2',
    'exposure_neg_1',
    'exposure_neg_2',
    'exposure_plus_1',
    'exposure_plus_2',
    'exposure_zero',
    'extension',
    'extension_off',
    'face',
    'face_2',
    'face_3',
    'face_4',
    'face_5',
    'face_6',
    'face_retouching_natural',
    'face_retouching_off',
    'face_unlock',
    'facebook',
    'fact_check',
    'factory',
    'family_restroom',
    'fast_forward',
    'fast_rewind',
    'fastfood',
    'favorite',
    'favorite_border',
    'favorite_outline',
    'fax',
    'featured_play_list',
    'featured_video',
    'feed',
    'feedback',
    'female',
    'fence',
    'festival',
    'fiber_dvr',
    'fiber_manual_record',
    'fiber_new',
    'fiber_pin',
    'fiber_smart_record',
    'file_copy',
    'file_download',
    'file_download_done',
    'file_download_off',
    'file_open',
    'file_present',
    'file_upload',
    'file_upload_off',
    'filter',
    'filter_1',
    'filter_2',
    'filter_3',
    'filter_4',
    'filter_5',
    'filter_6',
    'filter_7',
    'filter_8',
    'filter_9',
    'filter_9_plus',
    'filter_alt',
    'filter_alt_off',
    'filter_b_and_w',
    'filter_center_focus',
    'filter_drama',
    'filter_frames',
    'filter_hdr',
    'filter_list',
    'filter_list_alt',
    'filter_list_off',
    'filter_none',
    'filter_tilt_shift',
    'filter_vintage',
    'find_in_page',
    'find_replace',
    'fingerprint',
    'fire_extinguisher',
    'fire_hydrant',
    'fire_hydrant_alt',
    'fire_truck',
    'fireplace',
    'first_page',
    'fit_screen',
    'fitbit',
    'fitness_center',
    'flag',
    'flag_circle',
    'flaky',
    'flare',
    'flash_auto',
    'flash_off',
    'flash_on',
    'flashlight_off',
    'flashlight_on',
    'flatware',
    'flight',
    'flight_class',
    'flight_land',
    'flight_takeoff',
    'flip',
    'flip_camera_android',
    'flip_camera_ios',
    'flip_to_back',
    'flip_to_front',
    'flood',
    'flourescent',
    'fluorescent',
    'flutter_dash',
    'fmd_bad',
    'fmd_good',
    'foggy',
    'folder',
    'folder_copy',
    'folder_delete',
    'folder_off',
    'folder_open',
    'folder_shared',
    'folder_special',
    'folder_zip',
    'follow_the_signs',
    'font_download',
    'font_download_off',
    'food_bank',
    'forest',
    'fork_left',
    'fork_right',
    'forklift',
    'format_align_center',
    'format_align_justify',
    'format_align_left',
    'format_align_right',
    'format_bold',
    'format_clear',
    'format_color_fill',
    'format_color_reset',
    'format_color_text',
    'format_indent_decrease',
    'format_indent_increase',
    'format_italic',
    'format_line_spacing',
    'format_list_bulleted',
    'format_list_bulleted_add',
    'format_list_numbered',
    'format_list_numbered_rtl',
    'format_overline',
    'format_paint',
    'format_quote',
    'format_shapes',
    'format_size',
    'format_strikethrough',
    'format_textdirection_l_to_r',
    'format_textdirection_r_to_l',
    'format_underline',
    'format_underlined',
    'fort',
    'forum',
    'forward',
    'forward_10',
    'forward_30',
    'forward_5',
    'forward_to_inbox',
    'foundation',
    'free_breakfast',
    'free_cancellation',
    'front_hand',
    'front_loader',
    'fullscreen',
    'fullscreen_exit',
    'functions',
    'g_mobiledata',
    'g_translate',
    'gamepad',
    'games',
    'garage',
    'gas_meter',
    'gavel',
    'generating_tokens',
    'gesture',
    'get_app',
    'gif',
    'gif_box',
    'girl',
    'gite',
    'goat',
    'golf_course',
    'gpp_bad',
    'gpp_good',
    'gpp_maybe',
    'gps_fixed',
    'gps_not_fixed',
    'gps_off',
    'grade',
    'gradient',
    'grading',
    'grain',
    'graphic_eq',
    'grass',
    'grid_3x3',
    'grid_4x4',
    'grid_goldenratio',
    'grid_off',
    'grid_on',
    'grid_view',
    'group',
    'group_add',
    'group_off',
    'group_remove',
    'group_work',
    'groups',
    'groups_2',
    'groups_3',
    'h_mobiledata',
    'h_plus_mobiledata',
    'hail',
    'handshake',
    'handyman',
    'hardware',
    'hd',
    'hdr_auto',
    'hdr_auto_select',
    'hdr_enhanced_select',
    'hdr_off',
    'hdr_off_select',
    'hdr_on',
    'hdr_on_select',
    'hdr_plus',
    'hdr_strong',
    'hdr_weak',
    'headphones',
    'headphones_battery',
    'headset',
    'headset_mic',
    'headset_off',
    'healing',
    'health_and_safety',
    'hearing',
    'hearing_disabled',
    'heart_broken',
    'heat_pump',
    'height',
    'help',
    'help_center',
    'help_outline',
    'hevc',
    'hexagon',
    'hide_image',
    'hide_source',
    'high_quality',
    'highlight',
    'highlight_alt',
    'highlight_off',
    'highlight_remove',
    'hiking',
    'history',
    'history_edu',
    'history_toggle_off',
    'hive',
    'hls',
    'hls_off',
    'holiday_village',
    'home',
    'home_filled',
    'home_max',
    'home_mini',
    'home_repair_service',
    'home_work',
    'horizontal_distribute',
    'horizontal_rule',
    'horizontal_split',
    'hot_tub',
    'hotel',
    'hotel_class',
    'hourglass_bottom',
    'hourglass_disabled',
    'hourglass_empty',
    'hourglass_full',
    'hourglass_top',
    'house',
    'house_siding',
    'houseboat',
    'how_to_reg',
    'how_to_vote',
    'html',
    'http',
    'https',
    'hub',
    'hvac',
    'ice_skating',
    'icecream',
    'image',
    'image_aspect_ratio',
    'image_not_supported',
    'image_search',
    'imagesearch_roller',
    'import_contacts',
    'import_export',
    'important_devices',
    'inbox',
    'incomplete_circle',
    'indeterminate_check_box',
    'info',
    'info_outline',
    'input',
    'insert_chart',
    'insert_chart_outlined',
    'insert_comment',
    'insert_drive_file',
    'insert_emoticon',
    'insert_invitation',
    'insert_link',
    'insert_page_break',
    'insert_photo',
    'insights',
    'install_desktop',
    'install_mobile',
    'integration_instructions',
    'interests',
    'interpreter_mode',
    'inventory',
    'inventory_2',
    'invert_colors',
    'invert_colors_off',
    'invert_colors_on',
    'ios_share',
    'iron',
    'iso',
    'javascript',
    'join_full',
    'join_inner',
    'join_left',
    'join_right',
    'kayaking',
    'kebab_dining',
    'key',
    'key_off',
    'keyboard',
    'keyboard_alt',
    'keyboard_arrow_down',
    'keyboard_arrow_left',
    'keyboard_arrow_right',
    'keyboard_arrow_up',
    'keyboard_backspace',
    'keyboard_capslock',
    'keyboard_command',
    'keyboard_command_key',
    'keyboard_control',
    'keyboard_control_key',
    'keyboard_double_arrow_down',
    'keyboard_double_arrow_left',
    'keyboard_double_arrow_right',
    'keyboard_double_arrow_up',
    'keyboard_hide',
    'keyboard_option',
    'keyboard_option_key',
    'keyboard_return',
    'keyboard_tab',
    'keyboard_voice',
    'king_bed',
    'kitchen',
    'kitesurfing',
    'label',
    'label_important',
    'label_important_outline',
    'label_off',
    'label_outline',
    'lan',
    'landscape',
    'landslide',
    'language',
    'laptop',
    'laptop_chromebook',
    'laptop_mac',
    'laptop_windows',
    'last_page',
    'launch',
    'layers',
    'layers_clear',
    'leaderboard',
    'leak_add',
    'leak_remove',
    'leave_bags_at_home',
    'legend_toggle',
    'lens',
    'lens_blur',
    'library_add',
    'library_add_check',
    'library_books',
    'library_music',
    'light',
    'light_mode',
    'lightbulb',
    'lightbulb_circle',
    'lightbulb_outline',
    'line_axis',
    'line_style',
    'line_weight',
    'linear_scale',
    'link',
    'link_off',
    'linked_camera',
    'liquor',
    'list',
    'list_alt',
    'live_help',
    'live_tv',
    'living',
    'local_activity',
    'local_airport',
    'local_atm',
    'local_attraction',
    'local_bar',
    'local_cafe',
    'local_car_wash',
    'local_convenience_store',
    'local_dining',
    'local_drink',
    'local_fire_department',
    'local_florist',
    'local_gas_station',
    'local_grocery_store',
    'local_hospital',
    'local_hotel',
    'local_laundry_service',
    'local_library',
    'local_mall',
    'local_movies',
    'local_offer',
    'local_parking',
    'local_pharmacy',
    'local_phone',
    'local_pizza',
    'local_play',
    'local_police',
    'local_post_office',
    'local_print_shop',
    'local_printshop',
    'local_restaurant',
    'local_see',
    'local_shipping',
    'local_taxi',
    'location_city',
    'location_disabled',
    'location_history',
    'location_off',
    'location_on',
    'location_pin',
    'location_searching',
    'lock',
    'lock_clock',
    'lock_open',
    'lock_outline',
    'lock_person',
    'lock_reset',
    'login',
    'logo_dev',
    'logout',
    'looks',
    'looks_3',
    'looks_4',
    'looks_5',
    'looks_6',
    'looks_one',
    'looks_two',
    'loop',
    'loupe',
    'low_priority',
    'loyalty',
    'lte_mobiledata',
    'lte_plus_mobiledata',
    'luggage',
    'lunch_dining',
    'lyrics',
    'macro_off',
    'mail',
    'mail_lock',
    'mail_outline',
    'male',
    'man',
    'man_2',
    'man_3',
    'man_4',
    'manage_accounts',
    'manage_history',
    'manage_search',
    'map',
    'maps_home_work',
    'maps_ugc',
    'margin',
    'mark_as_unread',
    'mark_chat_read',
    'mark_chat_unread',
    'mark_email_read',
    'mark_email_unread',
    'mark_unread_chat_alt',
    'markunread',
    'markunread_mailbox',
    'masks',
    'maximize',
    'media_bluetooth_off',
    'media_bluetooth_on',
    'mediation',
    'medical_information',
    'medical_services',
    'medication',
    'medication_liquid',
    'meeting_room',
    'memory',
    'menu',
    'menu_book',
    'menu_open',
    'merge',
    'merge_type',
    'message',
    'messenger',
    'messenger_outline',
    'mic',
    'mic_external_off',
    'mic_external_on',
    'mic_none',
    'mic_off',
    'microwave',
    'military_tech',
    'minimize',
    'minor_crash',
    'miscellaneous_services',
    'missed_video_call',
    'mms',
    'mobile_friendly',
    'mobile_off',
    'mobile_screen_share',
    'mobiledata_off',
    'mode',
    'mode_comment',
    'mode_edit',
    'mode_edit_outline',
    'mode_fan_off',
    'mode_night',
    'mode_of_travel',
    'mode_standby',
    'model_training',
    'monetization_on',
    'money',
    'money_off',
    'money_off_csred',
    'monitor',
    'monitor_heart',
    'monitor_weight',
    'monochrome_photos',
    'mood',
    'mood_bad',
    'moped',
    'more',
    'more_horiz',
    'more_time',
    'more_vert',
    'mosque',
    'motion_photos_auto',
    'motion_photos_off',
    'motion_photos_on',
    'motion_photos_pause',
    'motion_photos_paused',
    'motorcycle',
    'mouse',
    'move_down',
    'move_to_inbox',
    'move_up',
    'movie',
    'movie_creation',
    'movie_edit',
    'movie_filter',
    'moving',
    'mp',
    'multiline_chart',
    'multiple_stop',
    'multitrack_audio',
    'museum',
    'music_note',
    'music_off',
    'music_video',
    'my_library_add',
    'my_library_books',
    'my_library_music',
    'my_location',
    'nat',
    'nature',
    'nature_people',
    'navigate_before',
    'navigate_next',
    'navigation',
    'near_me',
    'near_me_disabled',
    'nearby_error',
    'nearby_off',
    'nest_cam_wired_stand',
    'network_cell',
    'network_check',
    'network_locked',
    'network_ping',
    'network_wifi',
    'network_wifi_1_bar',
    'network_wifi_2_bar',
    'network_wifi_3_bar',
    'new_label',
    'new_releases',
    'newspaper',
    'next_plan',
    'next_week',
    'nfc',
    'night_shelter',
    'nightlife',
    'nightlight',
    'nightlight_round',
    'nights_stay',
    'no_accounts',
    'no_adult_content',
    'no_backpack',
    'no_cell',
    'no_crash',
    'no_drinks',
    'no_encryption',
    'no_encryption_gmailerrorred',
    'no_flash',
    'no_food',
    'no_luggage',
    'no_meals',
    'no_meals_ouline',
    'no_meeting_room',
    'no_photography',
    'no_sim',
    'no_stroller',
    'no_transfer',
    'noise_aware',
    'noise_control_off',
    'nordic_walking',
    'north',
    'north_east',
    'north_west',
    'not_accessible',
    'not_interested',
    'not_listed_location',
    'not_started',
    'note',
    'note_add',
    'note_alt',
    'notes',
    'notification_add',
    'notification_important',
    'notifications',
    'notifications_active',
    'notifications_none',
    'notifications_off',
    'notifications_on',
    'notifications_paused',
    'now_wallpaper',
    'now_widgets',
    'numbers',
    'offline_bolt',
    'offline_pin',
    'offline_share',
    'oil_barrel',
    'on_device_training',
    'ondemand_video',
    'online_prediction',
    'opacity',
    'open_in_browser',
    'open_in_full',
    'open_in_new',
    'open_in_new_off',
    'open_with',
    'other_houses',
    'outbond',
    'outbound',
    'outbox',
    'outdoor_grill',
    'outgoing_mail',
    'outlet',
    'outlined_flag',
    'output',
    'padding',
    'pages',
    'pageview',
    'paid',
    'palette',
    'pallet',
    'pan_tool',
    'pan_tool_alt',
    'panorama',
    'panorama_fish_eye',
    'panorama_fisheye',
    'panorama_horizontal',
    'panorama_horizontal_select',
    'panorama_photosphere',
    'panorama_photosphere_select',
    'panorama_vertical',
    'panorama_vertical_select',
    'panorama_wide_angle',
    'panorama_wide_angle_select',
    'paragliding',
    'park',
    'party_mode',
    'password',
    'paste',
    'pattern',
    'pause',
    'pause_circle',
    'pause_circle_filled',
    'pause_circle_outline',
    'pause_presentation',
    'payment',
    'payments',
    'paypal',
    'pedal_bike',
    'pending',
    'pending_actions',
    'pentagon',
    'people',
    'people_alt',
    'people_outline',
    'percent',
    'perm_camera_mic',
    'perm_contact_cal',
    'perm_contact_calendar',
    'perm_data_setting',
    'perm_device_info',
    'perm_device_information',
    'perm_identity',
    'perm_media',
    'perm_phone_msg',
    'perm_scan_wifi',
    'person',
    'person_2',
    'person_3',
    'person_4',
    'person_add',
    'person_add_alt',
    'person_add_alt_1',
    'person_add_disabled',
    'person_off',
    'person_outline',
    'person_pin',
    'person_pin_circle',
    'person_remove',
    'person_remove_alt_1',
    'person_search',
    'personal_injury',
    'personal_video',
    'pest_control',
    'pest_control_rodent',
    'pets',
    'phishing',
    'phone',
    'phone_android',
    'phone_bluetooth_speaker',
    'phone_callback',
    'phone_disabled',
    'phone_enabled',
    'phone_forwarded',
    'phone_in_talk',
    'phone_iphone',
    'phone_locked',
    'phone_missed',
    'phone_paused',
    'phonelink',
    'phonelink_erase',
    'phonelink_lock',
    'phonelink_off',
    'phonelink_ring',
    'phonelink_setup',
    'photo',
    'photo_album',
    'photo_camera',
    'photo_camera_back',
    'photo_camera_front',
    'photo_filter',
    'photo_library',
    'photo_size_select_actual',
    'photo_size_select_large',
    'photo_size_select_small',
    'php',
    'piano',
    'piano_off',
    'picture_as_pdf',
    'picture_in_picture',
    'picture_in_picture_alt',
    'pie_chart',
    'pie_chart_outline',
    'pie_chart_outlined',
    'pin',
    'pin_drop',
    'pin_end',
    'pin_invoke',
    'pinch',
    'pivot_table_chart',
    'pix',
    'place',
    'plagiarism',
    'play_arrow',
    'play_circle',
    'play_circle_fill',
    'play_circle_filled',
    'play_circle_outline',
    'play_disabled',
    'play_for_work',
    'play_lesson',
    'playlist_add',
    'playlist_add_check',
    'playlist_add_check_circle',
    'playlist_add_circle',
    'playlist_play',
    'playlist_remove',
    'plumbing',
    'plus_one',
    'podcasts',
    'point_of_sale',
    'policy',
    'poll',
    'polyline',
    'polymer',
    'pool',
    'portable_wifi_off',
    'portrait',
    'post_add',
    'power',
    'power_input',
    'power_off',
    'power_settings_new',
    'precision_manufacturing',
    'pregnant_woman',
    'present_to_all',
    'preview',
    'price_change',
    'price_check',
    'print',
    'print_disabled',
    'priority_high',
    'privacy_tip',
    'private_connectivity',
    'production_quantity_limits',
    'propane',
    'propane_tank',
    'psychology',
    'psychology_alt',
    'public',
    'public_off',
    'publish',
    'published_with_changes',
    'punch_clock',
    'push_pin',
    'qr_code',
    'qr_code_2',
    'qr_code_scanner',
    'query_builder',
    'query_stats',
    'question_answer',
    'question_mark',
    'queue',
    'queue_music',
    'queue_play_next',
    'quick_contacts_dialer',
    'quick_contacts_mail',
    'quickreply',
    'quiz',
    'quora',
    'r_mobiledata',
    'radar',
    'radio',
    'radio_button_checked',
    'radio_button_off',
    'radio_button_on',
    'radio_button_unchecked',
    'railway_alert',
    'ramen_dining',
    'ramp_left',
    'ramp_right',
    'rate_review',
    'raw_off',
    'raw_on',
    'read_more',
    'real_estate_agent',
    'rebase_edit',
    'receipt',
    'receipt_long',
    'recent_actors',
    'recommend',
    'record_voice_over',
    'rectangle',
    'recycling',
    'reddit',
    'redeem',
    'redo',
    'reduce_capacity',
    'refresh',
    'remember_me',
    'remove',
    'remove_circle',
    'remove_circle_outline',
    'remove_done',
    'remove_from_queue',
    'remove_moderator',
    'remove_red_eye',
    'remove_road',
    'remove_shopping_cart',
    'reorder',
    'repartition',
    'repeat',
    'repeat_on',
    'repeat_one',
    'repeat_one_on',
    'replay',
    'replay_10',
    'replay_30',
    'replay_5',
    'replay_circle_filled',
    'reply',
    'reply_all',
    'report',
    'report_gmailerrorred',
    'report_off',
    'report_problem',
    'request_page',
    'request_quote',
    'reset_tv',
    'restart_alt',
    'restaurant',
    'restaurant_menu',
    'restore',
    'restore_from_trash',
    'restore_page',
    'reviews',
    'rice_bowl',
    'ring_volume',
    'rocket',
    'rocket_launch',
    'roller_shades',
    'roller_shades_closed',
    'roller_skating',
    'roofing',
    'room',
    'room_preferences',
    'room_service',
    'rotate_90_degrees_ccw',
    'rotate_90_degrees_cw',
    'rotate_left',
    'rotate_right',
    'roundabout_left',
    'roundabout_right',
    'rounded_corner',
    'route',
    'router',
    'rowing',
    'rss_feed',
    'rsvp',
    'rtt',
    'rule',
    'rule_folder',
    'run_circle',
    'running_with_errors',
    'rv_hookup',
    'safety_check',
    'safety_divider',
    'sailing',
    'sanitizer',
    'satellite',
    'satellite_alt',
    'save',
    'save_alt',
    'save_as',
    'saved_search',
    'savings',
    'scale',
    'scanner',
    'scatter_plot',
    'schedule',
    'schedule_send',
    'schema',
    'school',
    'science',
    'score',
    'scoreboard',
    'screen_lock_landscape',
    'screen_lock_portrait',
    'screen_lock_rotation',
    'screen_rotation',
    'screen_rotation_alt',
    'screen_search_desktop',
    'screen_share',
    'screenshot',
    'screenshot_monitor',
    'scuba_diving',
    'sd',
    'sd_card',
    'sd_card_alert',
    'sd_storage',
    'search',
    'search_off',
    'security',
    'security_update',
    'security_update_good',
    'security_update_warning',
    'segment',
    'select_all',
    'self_improvement',
    'sell',
    'send',
    'send_and_archive',
    'send_time_extension',
    'send_to_mobile',
    'sensor_door',
    'sensor_occupied',
    'sensor_window',
    'sensors',
    'sensors_off',
    'sentiment_dissatisfied',
    'sentiment_neutral',
    'sentiment_satisfied',
    'sentiment_satisfied_alt',
    'sentiment_very_dissatisfied',
    'sentiment_very_satisfied',
    'set_meal',
    'settings',
    'settings_accessibility',
    'settings_applications',
    'settings_backup_restore',
    'settings_bluetooth',
    'settings_brightness',
    'settings_cell',
    'settings_display',
    'settings_ethernet',
    'settings_input_antenna',
    'settings_input_component',
    'settings_input_composite',
    'settings_input_hdmi',
    'settings_input_svideo',
    'settings_overscan',
    'settings_phone',
    'settings_power',
    'settings_remote',
    'settings_suggest',
    'settings_system_daydream',
    'settings_voice',
    'severe_cold',
    'shape_line',
    'share',
    'share_arrival_time',
    'share_location',
    'shelves',
    'shield',
    'shield_moon',
    'shop',
    'shop_2',
    'shop_two',
    'shopify',
    'shopping_bag',
    'shopping_basket',
    'shopping_cart',
    'shopping_cart_checkout',
    'short_text',
    'shortcut',
    'show_chart',
    'shower',
    'shuffle',
    'shuffle_on',
    'shutter_speed',
    'sick',
    'sign_language',
    'signal_cellular_0_bar',
    'signal_cellular_1_bar',
    'signal_cellular_2_bar',
    'signal_cellular_3_bar',
    'signal_cellular_4_bar',
    'signal_cellular_alt',
    'signal_cellular_alt_1_bar',
    'signal_cellular_alt_2_bar',
    'signal_cellular_connected_no_internet_0_bar',
    'signal_cellular_connected_no_internet_1_bar',
    'signal_cellular_connected_no_internet_2_bar',
    'signal_cellular_connected_no_internet_3_bar',
    'signal_cellular_connected_no_internet_4_bar',
    'signal_cellular_no_sim',
    'signal_cellular_nodata',
    'signal_cellular_null',
    'signal_cellular_off',
    'signal_wifi_0_bar',
    'signal_wifi_1_bar',
    'signal_wifi_1_bar_lock',
    'signal_wifi_2_bar',
    'signal_wifi_2_bar_lock',
    'signal_wifi_3_bar',
    'signal_wifi_3_bar_lock',
    'signal_wifi_4_bar',
    'signal_wifi_4_bar_lock',
    'signal_wifi_bad',
    'signal_wifi_connected_no_internet_0',
    'signal_wifi_connected_no_internet_1',
    'signal_wifi_connected_no_internet_2',
    'signal_wifi_connected_no_internet_3',
    'signal_wifi_connected_no_internet_4',
    'signal_wifi_off',
    'signal_wifi_statusbar_1_bar',
    'signal_wifi_statusbar_2_bar',
    'signal_wifi_statusbar_3_bar',
    'signal_wifi_statusbar_4_bar',
    'signal_wifi_statusbar_connected_no_internet',
    'signal_wifi_statusbar_connected_no_internet_1',
    'signal_wifi_statusbar_connected_no_internet_2',
    'signal_wifi_statusbar_connected_no_internet_3',
    'signal_wifi_statusbar_connected_no_internet_4',
    'signal_wifi_statusbar_not_connected',
    'signal_wifi_statusbar_null',
    'signpost',
    'sim_card',
    'sim_card_alert',
    'sim_card_download',
    'single_bed',
    'sip',
    'skateboarding',
    'skip_next',
    'skip_previous',
    'sledding',
    'slideshow',
    'slow_motion_video',
    'smart_button',
    'smart_display',
    'smart_screen',
    'smart_toy',
    'smartphone',
    'smoke_free',
    'smoking_rooms',
    'sms',
    'sms_failed',
    'snapchat',
    'snippet_folder',
    'snooze',
    'snowboarding',
    'snowing',
    'snowmobile',
    'snowshoeing',
    'soap',
    'social_distance',
    'solar_power',
    'sort',
    'sort_by_alpha',
    'sos',
    'soup_kitchen',
    'source',
    'south',
    'south_america',
    'south_east',
    'south_west',
    'spa',
    'space_bar',
    'space_dashboard',
    'spatial_audio',
    'spatial_audio_off',
    'spatial_tracking',
    'speaker',
    'speaker_group',
    'speaker_notes',
    'speaker_notes_off',
    'speaker_phone',
    'speed',
    'spellcheck',
    'splitscreen',
    'spoke',
    'sports',
    'sports_bar',
    'sports_baseball',
    'sports_basketball',
    'sports_cricket',
    'sports_esports',
    'sports_football',
    'sports_golf',
    'sports_gymnastics',
    'sports_handball',
    'sports_hockey',
    'sports_kabaddi',
    'sports_martial_arts',
    'sports_mma',
    'sports_motorsports',
    'sports_rugby',
    'sports_score',
    'sports_soccer',
    'sports_tennis',
    'sports_volleyball',
    'square',
    'square_foot',
    'ssid_chart',
    'stacked_bar_chart',
    'stacked_line_chart',
    'stadium',
    'stairs',
    'star',
    'star_border',
    'star_border_purple500',
    'star_half',
    'star_outline',
    'star_purple500',
    'star_rate',
    'stars',
    'start',
    'stay_current_landscape',
    'stay_current_portrait',
    'stay_primary_landscape',
    'stay_primary_portrait',
    'sticky_note_2',
    'stop',
    'stop_circle',
    'stop_screen_share',
    'storage',
    'store',
    'store_mall_directory',
    'storefront',
    'storm',
    'straight',
    'straighten',
    'stream',
    'streetview',
    'strikethrough_s',
    'stroller',
    'style',
    'subdirectory_arrow_left',
    'subdirectory_arrow_right',
    'subject',
    'subscript',
    'subscriptions',
    'subtitles',
    'subtitles_off',
    'subway',
    'summarize',
    'sunny',
    'sunny_snowing',
    'superscript',
    'supervised_user_circle',
    'supervisor_account',
    'support',
    'support_agent',
    'surfing',
    'surround_sound',
    'swap_calls',
    'swap_horiz',
    'swap_horizontal_circle',
    'swap_vert',
    'swap_vert_circle',
    'swap_vertical_circle',
    'swipe',
    'swipe_down',
    'swipe_down_alt',
    'swipe_left',
    'swipe_left_alt',
    'swipe_right',
    'swipe_right_alt',
    'swipe_up',
    'swipe_up_alt',
    'swipe_vertical',
    'switch_access_shortcut',
    'switch_access_shortcut_add',
    'switch_account',
    'switch_camera',
    'switch_left',
    'switch_right',
    'switch_video',
    'synagogue',
    'sync',
    'sync_alt',
    'sync_disabled',
    'sync_lock',
    'sync_problem',
    'system_security_update',
    'system_security_update_good',
    'system_security_update_warning',
    'system_update',
    'system_update_alt',
    'system_update_tv',
    'tab',
    'tab_unselected',
    'table_bar',
    'table_chart',
    'table_restaurant',
    'table_rows',
    'table_view',
    'tablet',
    'tablet_android',
    'tablet_mac',
    'tag',
    'tag_faces',
    'takeout_dining',
    'tap_and_play',
    'tapas',
    'task',
    'task_alt',
    'taxi_alert',
    'telegram',
    'temple_buddhist',
    'temple_hindu',
    'terminal',
    'terrain',
    'text_decrease',
    'text_fields',
    'text_format',
    'text_increase',
    'text_rotate_up',
    'text_rotate_vertical',
    'text_rotation_angledown',
    'text_rotation_angleup',
    'text_rotation_down',
    'text_rotation_none',
    'text_snippet',
    'textsms',
    'texture',
    'theater_comedy',
    'theaters',
    'thermostat',
    'thermostat_auto',
    'thumb_down',
    'thumb_down_alt',
    'thumb_down_off_alt',
    'thumb_up',
    'thumb_up_alt',
    'thumb_up_off_alt',
    'thumbs_up_down',
    'thunderstorm',
    'tiktok',
    'time_to_leave',
    'timelapse',
    'timeline',
    'timer',
    'timer_10',
    'timer_10_select',
    'timer_3',
    'timer_3_select',
    'timer_off',
    'tips_and_updates',
    'tire_repair',
    'title',
    'toc',
    'today',
    'toggle_off',
    'toggle_on',
    'token',
    'toll',
    'tonality',
    'topic',
    'tornado',
    'touch_app',
    'tour',
    'toys',
    'track_changes',
    'traffic',
    'train',
    'tram',
    'transcribe',
    'transfer_within_a_station',
    'transform',
    'transgender',
    'transit_enterexit',
    'translate',
    'travel_explore',
    'trending_down',
    'trending_flat',
    'trending_neutral',
    'trending_up',
    'trip_origin',
    'trolley',
    'troubleshoot',
    'try',
    'tsunami',
    'tty',
    'tune',
    'tungsten',
    'turn_left',
    'turn_right',
    'turn_sharp_left',
    'turn_sharp_right',
    'turn_slight_left',
    'turn_slight_right',
    'turned_in',
    'turned_in_not',
    'tv',
    'tv_off',
    'two_wheeler',
    'type_specimen',
    'u_turn_left',
    'u_turn_right',
    'umbrella',
    'unarchive',
    'undo',
    'unfold_less',
    'unfold_less_double',
    'unfold_more',
    'unfold_more_double',
    'unpublished',
    'unsubscribe',
    'upcoming',
    'update',
    'update_disabled',
    'upgrade',
    'upload',
    'upload_file',
    'usb',
    'usb_off',
    'vaccines',
    'vape_free',
    'vaping_rooms',
    'verified',
    'verified_user',
    'vertical_align_bottom',
    'vertical_align_center',
    'vertical_align_top',
    'vertical_distribute',
    'vertical_shades',
    'vertical_shades_closed',
    'vertical_split',
    'vibration',
    'video_call',
    'video_camera_back',
    'video_camera_front',
    'video_chat',
    'video_collection',
    'video_file',
    'video_label',
    'video_library',
    'video_settings',
    'video_stable',
    'videocam',
    'videocam_off',
    'videogame_asset',
    'videogame_asset_off',
    'view_agenda',
    'view_array',
    'view_carousel',
    'view_column',
    'view_comfortable',
    'view_comfy',
    'view_comfy_alt',
    'view_compact',
    'view_compact_alt',
    'view_cozy',
    'view_day',
    'view_headline',
    'view_in_ar',
    'view_kanban',
    'view_list',
    'view_module',
    'view_quilt',
    'view_sidebar',
    'view_stream',
    'view_timeline',
    'view_week',
    'vignette',
    'villa',
    'visibility',
    'visibility_off',
    'voice_chat',
    'voice_over_off',
    'voicemail',
    'volcano',
    'volume_down',
    'volume_down_alt',
    'volume_mute',
    'volume_off',
    'volume_up',
    'volunteer_activism',
    'vpn_key',
    'vpn_key_off',
    'vpn_lock',
    'vrpano',
    'wallet',
    'wallet_giftcard',
    'wallet_membership',
    'wallet_travel',
    'wallpaper',
    'warehouse',
    'warning',
    'warning_amber',
    'wash',
    'watch',
    'watch_later',
    'watch_off',
    'water',
    'water_damage',
    'water_drop',
    'waterfall_chart',
    'waves',
    'waving_hand',
    'wb_auto',
    'wb_cloudy',
    'wb_incandescent',
    'wb_iridescent',
    'wb_shade',
    'wb_sunny',
    'wb_twighlight',
    'wb_twilight',
    'wc',
    'web',
    'web_asset',
    'web_asset_off',
    'web_stories',
    'webhook',
    'wechat',
    'weekend',
    'west',
    'whatshot',
    'wheelchair_pickup',
    'where_to_vote',
    'widgets',
    'width_full',
    'width_normal',
    'width_wide',
    'wifi',
    'wifi_1_bar',
    'wifi_2_bar',
    'wifi_calling',
    'wifi_calling_1',
    'wifi_calling_2',
    'wifi_calling_3',
    'wifi_channel',
    'wifi_find',
    'wifi_lock',
    'wifi_off',
    'wifi_password',
    'wifi_protected_setup',
    'wifi_tethering',
    'wifi_tethering_error',
    'wifi_tethering_error_rounded',
    'wifi_tethering_off',
    'wind_power',
    'window',
    'wine_bar',
    'woman',
    'woman_2',
    'woo_commerce',
    'wordpress',
    'work',
    'work_history',
    'work_off',
    'work_outline',
    'workspace_premium',
    'workspaces',
    'workspaces_filled',
    'workspaces_outline',
    'wrap_text',
    'wrong_location',
    'wysiwyg',
    'yard',
    'youtube_searched_for',
    'zoom_in',
    'zoom_in_map',
    'zoom_out',
    'zoom_out_map'
];

/**
 * Only show the Spinner when 200 milliseconds have past.
 * This prevents very short loading periods from showing the animation,
 * seeing the Spinner very briefly looks very glitchy.
 *
 * @param show
 */
function useShowSpinner(show) {
    const [showSpinner, setShowSpinner] = useState(show);
    useEffect(() => {
        let timer = undefined;
        if (show) {
            timer = window.setTimeout(() => {
                setShowSpinner(true);
            }, 200);
        }
        else {
            setShowSpinner(false);
        }
        return () => {
            window.clearTimeout(timer);
        };
    }, [show]);
    return showSpinner;
}

/**
 * The Button component is a clickable element which can
 * either be shown as an Icon or as a ReactStrap Button.
 *
 * The Button component also has a notion of being `inProgress` when it is
 * it shows a Spinner. But only when `inProgress` has been true for more
 * than 200 milliseconds. Useful for showing that a button is indeed
 * clicked when performing some call to the back-end.
 */
function Button({ type = 'button', color = 'primary', inProgress, className = '', onClick, children, outline, size = 'md', fullWidth, disabled, icon, iconPosition = 'left', autoFocus }) {
    const showSpinner = useShowSpinner(!!inProgress);
    const handleOnClick = (event) => {
        if (!onClick) {
            return;
        }
        if (!inProgress) {
            onClick(event);
        }
    };
    // If there are children it will look like a button.
    if (children) {
        const buttonProps = {
            type,
            size,
            color,
            outline,
            block: fullWidth,
            autoFocus
        };
        const widget = showSpinner ? (React.createElement(Spinner, { size: getSpinnerSize(size), color: outline ? '' : 'white', className: iconPosition === 'left' ? 'me-2' : 'ms-2' })) : icon ? (React.createElement(Icon, { icon: icon, className: classNames('button-icon', iconPosition === 'left' ? 'me-2' : 'ms-2') })) : null;
        return (React.createElement("span", { className: classNames('button', fullWidth ? 'd-block' : 'd-inline-block', className, color) },
            React.createElement(Button$1, Object.assign({ onClick: handleOnClick, disabled: inProgress || disabled }, buttonProps),
                React.createElement("div", { className: "d-flex justify-content-center align-items-center" },
                    iconPosition === 'left' && widget,
                    children,
                    iconPosition === 'right' && widget))));
    }
    else {
        return (React.createElement("span", { className: classNames('button', className, color, fullWidth ? 'd-flex' : 'd-inline-block', {
                'justify-content-start': fullWidth && iconPosition === 'left',
                'justify-content-end': fullWidth && iconPosition === 'right'
            }) }, showSpinner ? (
        // Size is the same size as the icon.
        // Color is empty string so we can override the color
        React.createElement(Spinner, { size: getIconSize(size), color: "" })) : (React.createElement(Icon, { onClick: handleOnClick, 
            // Use block as default icon to let the user know something is wrong
            icon: icon !== null && icon !== void 0 ? icon : 'block', color: color, disabled: inProgress || disabled, size: getIconSize(size), autoFocus: autoFocus }))));
    }
}
// Based md is based on default the size of the Icon component
function getIconSize(size) {
    switch (size) {
        case 'lg':
            return 32;
        case 'md':
            return 24;
        case 'sm':
            return 16;
    }
}
// Based on the sizes in pixels of a button with text but without an icon
function getSpinnerSize(size) {
    switch (size) {
        case 'lg':
            return 19;
        case 'md':
            return 16;
        case 'sm':
            return 12;
    }
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * The `translator` which is currently active.
 *
 * Defaults to a translator which returns the `fallback`.
 */
let translator = (translation) => translation.fallback;
function getTranslator() {
    return translator;
}
/**
 * Lets you set a new translator to be used throughout `@42.nl/ui`.
 *
 * @param {Translator} _translator The new translator
 */
function setTranslator(_translator) {
    translator = _translator;
}

/**
 * The translation function (abbreviated as `t` for convenience) is
 * a small utility function that enables the user to make use of
 * the defined translation interface in @see Translation.
 *
 * @example basic example
 * ```ts
 * const someFunction(name: string): string {
 *  return t({
 *    key: 'TRANSLATION_KEY',
 *    data: { name },
 *    fallback: `${name} is already taken.`
 *    overrideText: 'Test'
 *  })
 * }
 * ```
 *
 * @export
 * @param {string} overrideText text that overrides translation functionality
 * @param {Translation} translation object used for the translation process
 * @returns The translation
 */
function t(_a) {
    var { overrideText } = _a, translation = __rest(_a, ["overrideText"]);
    if (overrideText) {
        return overrideText;
    }
    return getTranslator()(translation);
}

function Loading({ children, className, text = {} }) {
    const classes = classNames('d-flex', className);
    return (React.createElement("div", { className: classes },
        React.createElement(Spinner, { className: "align-self-center", color: "black", size: 16 }),
        React.createElement("span", { className: "ms-1" }, children
            ? children
            : t({
                key: 'Loading.LOADING',
                fallback: 'Loading...',
                overrideText: text.loading
            }))));
}

/**
 * Modal is a dialog that overlays the website. The dialog is often used to
 * ask the user a question or inform the user about something important.
 * You should use this component over the reactstrap Modal component,
 * because this component has Suspense built in to display a loader
 * and wait for its content to be fully loaded before displaying.
 */
function Modal({ onClose, header, footer, size, stickyFooter = true, className, modalHeaderClassName, modalBodyClassName, modalFooterClassName, autoFocus, children }) {
    return (React.createElement(Modal$1, { isOpen: true, toggle: onClose, size: size, className: className, scrollable: stickyFooter, autoFocus: autoFocus },
        header ? (React.createElement(ModalHeader, { toggle: onClose, className: modalHeaderClassName },
            React.createElement(Suspense, { fallback: React.createElement(Loading, null) }, header))) : null,
        React.createElement(ModalBody, { className: modalBodyClassName },
            React.createElement(Suspense, { fallback: React.createElement(Loading, null) }, children)),
        footer ? (React.createElement(ModalFooter, { className: modalFooterClassName },
            React.createElement(Suspense, { fallback: React.createElement(Loading, null) }, footer))) : null));
}

/**
 * ConfirmModal offers the user a confirmation dialog before performing an operation.
 * The main use case is a delete action, which we want the user to confirm before proceeding.
 *
 * Note: In most cases, you'll want to use {@link ConfirmButton} instead.
 * That component offers an easy-to-use button that triggers this dialog.
 * However, if it is not possible to trigger the action through a button,
 * such as when using a ButtonDropdown, you can use this component directly instead.
 */
function ConfirmModal({ onClose, onSave, label, cancelText, confirmText, modalText, className }) {
    return (React.createElement(Modal, { autoFocus: false, onClose: onClose, className: className, header: t({
            overrideText: label,
            key: 'ConfirmButton.MODAL_HEADER',
            fallback: 'Confirmation'
        }), footer: React.createElement(React.Fragment, null,
            React.createElement(Button, { className: "ms-1", color: "secondary", icon: "close", onClick: onClose }, t({
                overrideText: cancelText,
                key: 'ConfirmButton.CANCEL',
                fallback: 'Cancel'
            })),
            React.createElement(Button, { className: "ms-1", color: "primary", icon: "check", onClick: onSave, autoFocus: true }, t({
                overrideText: confirmText,
                key: 'ConfirmButton.CONFIRM',
                fallback: 'Confirm'
            }))) }, modalText));
}

/**
 * The ConfirmButton asks the user if he / she is sure he wants to
 * perform the action.
 *
 * The obvious use case is a delete button which asks the user if
 * he is sure he wants to delete something.
 */
function ConfirmButton(_a) {
    var { color = 'danger', dialogText, text = {}, onConfirm } = _a, buttonProps = __rest(_a, ["color", "dialogText", "text", "onConfirm"]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { modalHeader, confirm, cancel } = text;
    function openModal(event) {
        event.stopPropagation();
        setIsModalOpen(true);
    }
    function saveModal() {
        setIsModalOpen(false);
        onConfirm();
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, Object.assign({ color: color, onClick: openModal }, buttonProps)),
        isModalOpen ? (React.createElement(ConfirmModal, { onClose: () => setIsModalOpen(false), onSave: () => saveModal(), label: modalHeader, modalText: dialogText, cancelText: cancel, confirmText: confirm })) : null));
}

/**
 * ContentState is a component which handles various non-happy flow
 * states. The states it handles are: 'empty', 'no-results' and 'error'.
 *
 * It will display whimsical component which tells the user that things
 * did not go as planned / expected. Use this component whenever you
 * would otherwise display 'nothing'.
 */
function ContentState({ mode, title, subTitle, children, className }) {
    const icon = mode === 'empty'
        ? 'laptop'
        : mode === 'error'
            ? 'sentiment_very_dissatisfied'
            : 'search';
    return (React.createElement("div", { className: classNames('text-center', className) },
        React.createElement("div", { className: classNames('content-state', mode) },
            React.createElement("div", { className: "state-icons" },
                React.createElement(Icon, { icon: "add" }),
                React.createElement(Icon, { icon: "contrast" }),
                React.createElement(Icon, { icon: "photo" }),
                React.createElement(Icon, { icon: "folder_open" }),
                React.createElement(Icon, { icon: "radio_button_unchecked" }),
                React.createElement(Icon, { icon: "bolt" })),
            React.createElement("div", { className: "state-content" }, mode === 'loading' ? (React.createElement(Spinner, { className: "mb-2", color: "#f0ad4e", size: 75 })) : (React.createElement(Icon, { icon: icon })))),
        React.createElement("span", { className: "d-block fs-4" }, title),
        subTitle && React.createElement("span", { className: "d-block fs-6 text-dark mb-3" }, subTitle),
        children));
}

function CopyToClipboard({ id = uniqueId(), className, text, buttonColor, children }) {
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (copied) {
            const timeout = setTimeout(() => setCopied(false), 1000);
            return () => clearTimeout(timeout);
        }
    }, [copied]);
    function copyContent() {
        return __awaiter(this, void 0, void 0, function* () {
            const foundElement = document.getElementById(id);
            if (foundElement !== null) {
                yield navigator.clipboard.writeText(foundElement.innerText);
                setCopied(true);
            }
        });
    }
    return (React.createElement("div", { className: classNames('d-flex nowrap bg-dark text-white py-1 ps-3 pe-1', className) },
        React.createElement("pre", { className: "flex-grow-1 flex-shrink-1 me-3 mb-0", tabIndex: 0 },
            React.createElement("code", { id: id }, children)),
        copied ? (React.createElement("div", { className: "flex-grow-0 flex-shrink-0 me-2 text-success" }, t({
            key: 'CopyToClipboard.COPIED',
            fallback: 'Copied!',
            overrideText: text === null || text === void 0 ? void 0 : text.copied
        }))) : null,
        React.createElement(Button, { className: "flex-grow-0 flex-shrink-0 text-white", color: buttonColor, icon: "content_copy", onClick: copyContent })));
}

/**
 * A FlashMessage is a message you want to show to the user briefly.
 *
 * Use it when you want to globally show a notification / message.
 */
function FlashMessage({ className, onClose, color, children }) {
    return (React.createElement("div", { className: classNames('flash-message', className) },
        React.createElement(Alert, { color: color, open: true, toggle: onClose }, children)));
}

/**
 * InfoBadge is a badge which shows up on the top right of an element.
 *
 * Use it when you want to show a notification / message count above
 * an element.
 */
function InfoBadge({ color, value, children, className }) {
    return (React.createElement("span", { className: classNames('info-badge-container', className) },
        children,
        React.createElement(Badge, { color: color, pill: true }, value)));
}

/**
 * Returns `false` but after a predetermined `after` time returns
 * `true`.
 *
 * Useful for wanting to show content only after a time. For example
 * to not show a loading spinner to soon, only
 * after it takes a while.
 *
 * @param after The amount of time in milliseconds after which the return value should become `true`.
 */
function useShowAfter(after) {
    const [show, setShow] = useState(false);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setShow(true);
        }, after);
        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [after]);
    return show;
}

/**
 * The LoadingPage component embeds the Spinner component in a page-friendly matter.
 *
 * Use this for showing a loading indicator when navigating to pages that fetch data.
 */
function LoadingPage({ className, style, height }) {
    const size = 150;
    const showSpinner = useShowAfter(200);
    return (React.createElement("div", { className: classNames('loading-page', className), style: style },
        React.createElement("div", { className: classNames('d-flex flex-column justify-content-center align-items-center', { 'vh-100': height === undefined }), style: {
                marginTop: height === undefined ? -(size / 2) : undefined,
                height
            } }, showSpinner && React.createElement(Spinner, { size: size, color: "#f0ad4e" }))));
}

/**
 * MoreOrLess is a component which truncates a collection of elements and shows a button to toggle this behavior.
 */
function MoreOrLess({ limit, content, className, text = {} }) {
    const [isOpen, setIsOpen] = useState(false);
    const { more, less } = text;
    const amount = content.length - limit;
    function toggleOpen() {
        setIsOpen(!isOpen);
    }
    return (React.createElement("div", { className: classNames(className, 'more-or-less', {
            'more-or-less-opened': isOpen,
            'more-or-less-closed': !isOpen
        }) },
        isOpen ? content : take(content, limit),
        content.length > limit && (React.createElement("div", { role: "button", className: "more-or-less-link", onClick: toggleOpen }, isOpen
            ? t({
                key: 'MoreOrLess.LESS',
                fallback: 'Show less',
                overrideText: less
            })
            : t({
                key: 'MoreOrLess.MORE',
                fallback: `Show ${amount} more`,
                data: { amount },
                overrideText: more ? more(amount) : undefined
            })))));
}

/**
 * The NavigationItem is a wrapper around the `react-router`'s
 * `NavLink`. See https://reacttraining.com/react-router/web/api/NavLink.
 * It adds texts and icons around the `NavLink`.
 *
 * The NavigationItem enables you to guard specific links by a predicate,
 * for example by user role.
 *
 * Use it when you want to keep certain navigation items hidden
 * for specific user roles.
 *
 * By default, the `NavigationItem` will render an `exact` link. See:
 * https://reacttraining.com/react-router/web/api/Route/exact-bool.
 * But you can set it to `false` via the props.
 */
function NavigationItem({ to, icon, text, show = true, className }) {
    const shouldShow = typeof show === 'function' ? show : () => show;
    if (!shouldShow()) {
        return null;
    }
    return (React.createElement(NavItem, { className: classNames('navigation-item', className) },
        React.createElement(NavLink, { to: to, tag: NavLink$1 },
            React.createElement(Icon, { icon: icon, className: "me-3 align-bottom" }),
            text)));
}

function getState(info) {
    if (info.hasErrors && info.touched) {
        return { color: 'danger', valid: false };
    }
    else {
        return { color: '', valid: undefined };
    }
}
function doBlur(onBlur) {
    /* istanbul ignore else */
    if (onBlur) {
        onBlur();
    }
}
function alwaysTrue() {
    return true;
}
function illegalPropsDetected(component, displayName, illegalProps, managedProps) {
    const illegalPropsAsString = prettyPropsSummation(illegalProps, 'and');
    const managedPropsAsString = prettyPropsSummation(managedProps, 'or');
    throw new Error(`
        ${component}: illegal props detected on "${displayName}".
        
        The following illegal props were detected: ${illegalPropsAsString}.
        
        This happens when providing one or multiple of the following 
        managed props: ${managedPropsAsString} manually. 
        
        You should never provide these props manually instead you should
        trust that "${component}" will manage these props for you.

        Remove the following illegal props: ${illegalPropsAsString}.
      `);
}
function prettyPropsSummation(props, coordinatingConjunction) {
    const lastIndex = props.length - 1;
    return props
        .map((prop, index) => {
        const isFirst = index === 0;
        const isLast = index === lastIndex;
        const comma = isFirst || isLast ? '' : ', ';
        const conjunction = isLast ? ` ${coordinatingConjunction} ` : '';
        return `${comma}${conjunction}'${prop}'`;
    })
        .join('');
}

function useHasErrors() {
    return useState(false);
}

let config = {
    showRequiredMarkInLabel: true
};
function getConfig() {
    return config;
}
function configure(_config) {
    config = _config;
}

function useMarkedAsRequiredLabel({ label, validator }) {
    if (!label) {
        return label;
    }
    const { showRequiredMarkInLabel } = getConfig();
    if (typeof label === 'string' &&
        showRequiredMarkInLabel &&
        isRequired(validator)) {
        return label + t({
            key: 'withJarb.REQUIRED_MARK',
            fallback: ' *'
        });
    }
    return label;
}

function errorMessage(error) {
    const translator = getTranslator();
    // We consider it translated already
    if (typeof error === 'string' || React.isValidElement(error)) {
        return error;
    }
    if (error.type === undefined) {
        return translator(error);
    }
    const validationError = error;
    switch (validationError.type) {
        case 'ERROR_REQUIRED':
            return translator({
                key: 'JarbFinalForm.VALIDATION.REQUIRED',
                data: validationError,
                fallback: `${validationError.label} is required`
            });
        case 'ERROR_MINIMUM_LENGTH':
            return translator({
                key: 'JarbFinalForm.VALIDATION.MINIMUM_LENGTH',
                data: validationError,
                fallback: `${validationError.label} must be bigger than ${validationError.reasons.minimumLength} characters`
            });
        case 'ERROR_MAXIMUM_LENGTH':
            return translator({
                key: 'JarbFinalForm.VALIDATION.MAXIMUM_LENGTH',
                data: validationError,
                fallback: `${validationError.label} must be smaller than ${validationError.reasons.maximumLength} characters`
            });
        case 'ERROR_MIN_VALUE':
            return translator({
                key: 'JarbFinalForm.VALIDATION.MIN_VALUE',
                data: validationError,
                fallback: `${validationError.label} must be more than ${validationError.reasons.minValue}`
            });
        case 'ERROR_MAX_VALUE':
            return translator({
                key: 'JarbFinalForm.VALIDATION.MAX_VALUE',
                data: validationError,
                fallback: `${validationError.label} must be less than ${validationError.reasons.maxValue}`
            });
        case 'ERROR_NUMBER':
            return translator({
                key: 'JarbFinalForm.VALIDATION.NUMBER',
                data: validationError,
                fallback: `${validationError.label} is not a number`
            });
        case 'ERROR_NUMBER_FRACTION':
            return translator({
                key: 'JarbFinalForm.VALIDATION.NUMBER_FRACTION',
                data: validationError,
                fallback: `${validationError.label} is not a number. Number may have ${validationError.reasons.fractionLength} digits behind the comma`
            });
        default:
            return translator({
                key: 'FormError.UNKNOWN_ERROR',
                fallback: 'Something is wrong'
            });
    }
}

/**
 * Final form considers a form to be `valid` when it is `validating`
 * the input. Basically you are innocent until proven guilty.
 *
 * This is annoying when the user has already made an error. Each
 * time that the input is `validating`, the input is considered `valid`
 * again. Even though it was in an error state. This causes rapid
 * alternations between `valid` and `invalid`.
 *
 * When using async validation the effect becomes very pronounced,
 * you will see the errors being displayed and removed very quickly.
 * This causes the UI to jitter and move all over the place.
 *
 * The solution is `useSettledErrors`, it basically debounces the
 * errors. This means that if N changes happen in rapid succession,
 * only the last state change is shown.
 *
 * It caches the `value` and the resulting `error` for a small window
 * of time whenever an error occurs. This way, when the same value
 * is encountered, it will return the previous error during the time
 * window. So when `final-form` performs async validations,
 * the error state will not change rapidly.
 *
 * @param meta
 * @param value
 */
function useSettledErrors(meta, value) {
    const { error, submitError, active, touched, dirtySinceLastSubmit } = meta;
    const [errorCache] = useState({});
    const errors = useMemo(() => {
        return error === undefined ? [] : Array.isArray(error) ? error : [error];
    }, [error]);
    const submitErrors = useMemo(() => {
        return submitError === undefined || dirtySinceLastSubmit
            ? []
            : Array.isArray(submitError)
                ? submitError
                : [submitError];
    }, [submitError, dirtySinceLastSubmit]);
    const [settledErrors, setSettledErrors] = useState(errors);
    const hasErrors = errors.length > 0;
    useEffect(() => {
        let timeout;
        function showErrors() {
            // Store the errors for this value.
            errorCache[value] = errors;
            setSettledErrors(errors);
        }
        function hideErrors() {
            /*
              Only hide the errors when there are no errors, and the value
              did not have an error previously, this can be true when an
              async call is made and final-form considers it valid again.
              We say when that value previously has an error it is likely
              to still have an error, for at least 2 seconds.
            */
            if (settledErrors.length > 0 && errorCache[value] === undefined) {
                setSettledErrors([]);
            }
            else {
                /*
                  If after 2 seconds no new validity is reported then clear
                  the caches and hide the errors. This is necessary for when
                  the same value produces no errors at a later time.
                  
                  This can happen in the following scenario:
                  The user has to select a start and end date for a calendar.
                  First he selects for the start 2020-01-15 and for the end
                  2020-01-10. The end date will trigger an error that it must
                  be after the start date the cache will be:
                  `{ "2020-01-10": 'End date must be after start date' }`.
                  
                  If the user then selects a start date of 2020-01-01 the
                  previously wrong 2020-01-10 end date will now suddenly be
                  correct. If we do not clear the cached value after a while
                  the error would forever be presented to the user.
                */
                timeout = window.setTimeout(clearCacheAndHideErrors, 2000);
            }
        }
        function clearCacheAndHideErrors() {
            errorCache[value] = undefined;
            setSettledErrors([]);
        }
        if (active) {
            if (hasErrors) {
                // When active and there are errors delay until the final valid
                // state is reached. When the user continues "typing" the state
                // will toggle really fast between valid / invalid. We delay
                // showing the message to prevent the UI from jittering.
                timeout = window.setTimeout(showErrors, 5000);
            }
            else {
                // Remove the error when the user fixes the input.
                timeout = window.setTimeout(hideErrors, 100);
            }
        }
        else {
            if (hasErrors) {
                if (touched) {
                    // If the field is touched but not active show errors relatively quickly
                    timeout = window.setTimeout(showErrors, 100);
                }
                else {
                    hideErrors();
                }
            }
            else {
                /*
                  When there are no errors delay until the final valid state
                  is reached. Even when the field is not active other fields in
                  the form which are active will trigger the 'validating' state.
                  We delay showing the message to prevent the UI from jittering.
                */
                timeout = window.setTimeout(hideErrors, 2000);
            }
        }
        return () => {
            window.clearTimeout(timeout);
        };
    }, [
        hasErrors,
        active,
        touched,
        errors,
        submitErrors,
        settledErrors.length,
        errorCache,
        value
    ]);
    return [...settledErrors, ...submitErrors];
}

function useOnChange(hasErrors, onChange) {
    useEffect(() => {
        if (onChange) {
            onChange(hasErrors);
        }
    }, [hasErrors, onChange]);
}

/**
 * The FormError component renders errors coming from the front-end
 * and the back-end. It ties the front-end and back-end errors together
 * based on the validator.
 *
 * A validator is a string which looks like this: `User.name` the format
 * is `Entity.property`.
 *
 * To retrieve back-end errors, we use `@42.nl/react-error-store`.
 */
function FormError(props) {
    const { value, meta, validator, onChange, className } = props;
    const backEndErrors = useErrorsForValidator(validator !== null && validator !== void 0 ? validator : '');
    const frontEndErrors = useSettledErrors(meta, value);
    const hasErrors = backEndErrors.length > 0 || frontEndErrors.length > 0;
    useOnChange(hasErrors, onChange);
    if (!hasErrors) {
        return null;
    }
    return (React.createElement("div", { className: className },
        frontEndErrors.map((e, index) => (React.createElement(FormFeedback, { key: `${index}` }, errorMessage(e)))),
        backEndErrors.map((e) => (React.createElement(FormFeedback, { key: e }, errorMessage(e))))));
}

function FieldError({ name, validator, setHasErrors, errorMode }) {
    // Listen to all props on the `Meta` object.
    const errorSubscription = {
        active: true,
        touched: true,
        error: true,
        submitError: true,
        dirtySinceLastSubmit: true,
        value: true,
        validating: true
    };
    return (React.createElement(Field, { name: name, subscription: errorSubscription, render: (field) => (React.createElement(FormError, { value: field.input.value, meta: field.meta, validator: validator, onChange: setHasErrors, className: `error-${errorMode}` })) }));
}

// This is a list of props that `withJarb` will pass to the `final-form`
// Field, but not the wrapper.
const passedFieldProps$1 = ['initialValue', 'format', 'formatOnBlur', 'parse'];
// These are the props that are managed by `withJarb` and should not
// be set manually by the user.
const managedProps$1 = [
    'onChange',
    'onBlur',
    'onFocus',
    'value',
    'color',
    'valid',
    'error'
];
/**
 * withJarb is a Higher Order Component which takes an input element
 * which is `JarbFieldCompatible` as defined by the interface, and turns
 * it into a Component which uses `JarbField` so it can be used in
 * a final-form form.
 *
 * Basically all props needed in `JarbFieldCompatible` are plugged into the
 * Wrapper, by taking the `input` and `meta` from `final-form` and assigning
 * them into the correct slots.
 *
 * It also automatically adds the `FormError` component as the error
 * slot so errors are rendered, and `label` defaults to the value from `jarb.label`.
 *
 * @param Wrapper The Component which is `JarbFieldCompatible`.
 * @param defaultValidators Optional validators the field should use by default
 */
function withJarb(Wrapper, defaultValidators) {
    const displayName = `Jarb${getDisplayName(Wrapper)}`;
    WithJarb.displayName = displayName;
    function WithJarb(props) {
        var _a;
        const illegalProps = managedProps$1.filter((p) => props[p] !== undefined);
        if (illegalProps.length > 0) {
            illegalPropsDetected('withJarb', displayName, illegalProps, managedProps$1);
        }
        const [hasErrors, setHasErrors] = useHasErrors();
        const { errorMode = 'below', name, jarb, validators, asyncValidators, asyncValidatorsDebounce, allowNull } = props, rest = __rest(props, ["errorMode", "name", "jarb", "validators", "asyncValidators", "asyncValidatorsDebounce", "allowNull"]);
        // A bit magical this one but this makes TypeScript accept all other
        // props as the Props to the wrapped component.
        const wrapperProps = omit(rest, passedFieldProps$1);
        wrapperProps.label = useMarkedAsRequiredLabel({
            label: (_a = wrapperProps.label) !== null && _a !== void 0 ? _a : jarb.label,
            validator: jarb.validator
        });
        const fieldProps = pick(rest, [
            'initialValue',
            'format',
            'formatOnBlur',
            'parse'
        ]);
        const error = (React.createElement(FieldError, { name: name, validator: jarb.validator, setHasErrors: setHasErrors, errorMode: errorMode }));
        const fieldSubscription = {
            value: true, // Because value needs to be displayed.
            // Reset is due to `getState` needing them
            invalid: true,
            touched: true,
            validating: true
        };
        return (React.createElement(JarbField, Object.assign({ name: name, jarb: jarb, validators: defaultValidators ? merge(defaultValidators(props), validators) : validators, asyncValidators: asyncValidators, asyncValidatorsDebounce: asyncValidatorsDebounce, subscription: fieldSubscription, render: (field) => {
                return hasErrors && errorMode === 'tooltip' ? (React.createElement(Tooltip, { content: error, tag: "div", className: "w-100", placement: "bottom" },
                    React.createElement(Wrapper, Object.assign({}, wrapperProps, mapFieldRenderProps$1(field, jarb.validator, hasErrors))))) : (React.createElement(Wrapper, Object.assign({}, wrapperProps, mapFieldRenderProps$1(field, jarb.validator, hasErrors), { error: error })));
            } }, fieldProps)));
    }
    return WithJarb;
}
function mapFieldRenderProps$1(props, validator, hasErrors) {
    const { input, meta } = props;
    const state = getState({ hasErrors: hasErrors, touched: meta.touched });
    return Object.assign(Object.assign({}, state), { onChange: (value) => {
            input.onChange(value);
            // Remove any back-end errors now that the value has changed
            clearErrorsForValidator(validator);
        }, name: input.name, onFocus: input.onFocus, onBlur: input.onBlur, value: input.value });
}

/**
 * Returns a unique key for an option.
 *
 * Will try the following ways to get a unique key in this order:
 *
 * 1. Will use: `keyForOption` if provided. In this case we
 *    must trust the developer to implement it correctly.
 *
 * 2. If the options has an `id` we expect that id to be unique and
 *    that `id` is used as the key.
 *
 * 3. As a last resort we will use the `labelForOption` in this case
 *    the label of the option will become the key.
 */
function getKeyForOption({ option, keyForOption, labelForOption }) {
    if (keyForOption) {
        return keyForOption(option);
    }
    // @ts-expect-error Accept that the option could have an id
    if (option.id) {
        // @ts-expect-error Accept that the option could have an id
        return `${option.id}`;
    }
    return labelForOption(option);
}
/**
 * Determines if is the option is selected given the value.
 *
 * Will prefer to use the `isOptionEqual` to check if the value is
 * selected, otherwise it will fall back to checking the key.
 *
 * Note: always pass along every argument even the optional ones.
 * so `isOptionSelected` can perform its function best.
 */
function isOptionSelected(config) {
    const { value } = config;
    if (!value) {
        return false;
    }
    if (Array.isArray(value)) {
        return value.some((value) => isValueSelected(value, config));
    }
    else {
        return isValueSelected(value, config);
    }
}
// Helper for isOptionSelected
function isValueSelected(value, config) {
    const { option, isOptionEqual } = config;
    return isOptionEqual
        ? isOptionEqual(value, option)
        : compareOptions(value, option, config);
}
function compareOptions(a, b, config) {
    const { labelForOption, keyForOption } = config;
    return (getKeyForOption({ option: a, keyForOption, labelForOption }) ===
        getKeyForOption({ option: b, keyForOption, labelForOption }));
}

function useOptions(config) {
    const { options: optionsOrFetcher, value, keyForOption, labelForOption, isOptionEqual, reloadOptions, query, pageNumber, size, optionsShouldAlwaysContainValue } = config;
    const [loading, setLoading] = useState(() => !Array.isArray(optionsOrFetcher));
    const [pageFromOptions, setPageFromOptions] = useState(() => {
        if (Array.isArray(optionsOrFetcher)) {
            return pageFromOptionsArray(optionsOrFetcher);
        }
        else {
            return emptyPage();
        }
    });
    function pageFromOptionsArray(options) {
        // Filter based on the label for the option
        const content = options.filter((option) => labelForOption(option)
            .toLocaleLowerCase()
            .includes(query.toLocaleLowerCase()));
        // We expect that the caller will change the pageNumber to 1
        // when the query changes. This is not `useOptions`'s
        // responsibility.
        return pageOf(content, pageNumber, size);
    }
    // When the options are loaded make sure that the options always
    // contain the value that the user has selected when
    // `optionsShouldAlwaysContainValue` is `true`.
    function appendValueToPage(page) {
        /*
            When a form component shows the selected value in one place,
            but allows the user to select values from the another place
            `optionsShouldAlwaysContainValue` should be `false`.
      
            Take for example the `ModalPickerMultiple`. The selected values
            are rendered in the modal above the options in `Tag`'s. So there
            is no need to append the selected values, because they are already
            visible.
          */
        if (!optionsShouldAlwaysContainValue) {
            return page;
        }
        // If there is no value there is no need to add it
        if (!value) {
            return page;
        }
        if (Array.isArray(value)) {
            // When the value is an array add each value
            const prepend = value.filter((v) => {
                const isValueIncludedInPage = page.content.some((option) => isOptionSelected({
                    option,
                    keyForOption,
                    labelForOption,
                    isOptionEqual,
                    value: v
                }));
                return !isValueIncludedInPage;
            });
            return Object.assign(Object.assign({}, page), { content: [...prepend, ...page.content] });
        }
        else {
            const isValueIncludedInPage = page.content.some((option) => isOptionSelected({
                option,
                keyForOption,
                labelForOption,
                isOptionEqual,
                value: value
            }));
            // If the value is in the page there is no need to add it
            if (isValueIncludedInPage) {
                return page;
            }
            return Object.assign(Object.assign({}, page), { content: [value, ...page.content] });
        }
    }
    const watch = `${reloadOptions}-${query}-${pageNumber}-${size}`;
    const lastWatch = useRef(watch);
    useEffect(() => {
        function loadOptions(fetcher) {
            return __awaiter(this, void 0, void 0, function* () {
                setLoading(true);
                const resultPage = yield fetcher({
                    query,
                    page: pageNumber,
                    size
                });
                setPageFromOptions(resultPage);
                setLoading(false);
            });
        }
        if (!Array.isArray(optionsOrFetcher)) {
            loadOptions(optionsOrFetcher);
        }
        else if (lastWatch.current !== watch) {
            lastWatch.current = watch;
            setPageFromOptions(pageFromOptionsArray(optionsOrFetcher));
        }
        // We only want to reload when the following props change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watch]);
    const page = useMemo(() => appendValueToPage(pageFromOptions), 
    // We only want to reload when the following props change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, pageFromOptions]);
    return { loading, page };
}

// This is a list of props that `withField` will pass to the `final-form`
// Field, but not the wrapper.
const passedFieldProps = ['initialValue', 'format', 'formatOnBlur', 'parse'];
// These are the props that are managed by `withField` and should not
// be set manually by the user.
const managedProps = [
    'onChange',
    'onBlur',
    'onFocus',
    'value',
    'color',
    'valid',
    'error'
];
/**
 * withField is a Higher Order Component which takes an input element
 * which is `FieldCompatible` as defined by the interface, and turns
 * it into a Component which uses `Field` so it can be used in
 * a final-form form.
 *
 * Basically all props needed in `FieldCompatible` are plugged into the
 * Wrapper, by taking the `input` and `meta` from `final-form` and assigning
 * them into the correct slots.
 *
 * It also automatically adds the `FormError` component as the error
 * slot so errors are rendered.
 *
 * @param Wrapper The Component which is `FieldCompatible`.
 * @param defaultValidators Optional validators the field should use by default
 */
function withField(Wrapper, defaultValidators) {
    const displayName = `Field${getDisplayName(Wrapper)}`;
    WithField.displayName = displayName;
    function WithField(props) {
        const illegalProps = managedProps.filter((p) => props[p] !== undefined);
        if (illegalProps.length > 0) {
            illegalPropsDetected('withField', displayName, illegalProps, managedProps);
        }
        const [hasErrors, setHasErrors] = useHasErrors();
        const { errorMode = 'below', name, validators, asyncValidators, asyncValidatorsDebounce, allowNull } = props, rest = __rest(props, ["errorMode", "name", "validators", "asyncValidators", "asyncValidatorsDebounce", "allowNull"]);
        // A bit magical this one but this makes TypeScript accept all other
        // props as the Props to the wrapped component.
        const wrapperProps = omit(rest, passedFieldProps);
        const fieldProps = pick(rest, [
            'initialValue',
            'format',
            'formatOnBlur',
            'parse'
        ]);
        const error = (React.createElement(FieldError, { name: name, setHasErrors: setHasErrors, errorMode: errorMode }));
        const fieldSubscription = {
            value: true, // Because value needs to be displayed.
            // Reset is due to `getState` needing them
            invalid: true,
            touched: true,
            validating: true
        };
        return (React.createElement(Field$1, Object.assign({ name: name, validators: defaultValidators ? merge(defaultValidators(props), validators) : validators, asyncValidators: asyncValidators, asyncValidatorsDebounce: asyncValidatorsDebounce, subscription: fieldSubscription, render: (field) => {
                return hasErrors && errorMode === 'tooltip' ? (React.createElement(Tooltip, { content: error, tag: "div", className: "w-100", placement: "bottom" },
                    React.createElement(Wrapper, Object.assign({}, wrapperProps, mapFieldRenderProps(field, hasErrors))))) : (React.createElement(Wrapper, Object.assign({}, wrapperProps, mapFieldRenderProps(field, hasErrors), { error: error })));
            } }, fieldProps)));
    }
    return WithField;
}
function mapFieldRenderProps(props, hasErrors) {
    const { input, meta } = props;
    const state = getState({ hasErrors: hasErrors, touched: meta.touched });
    return Object.assign(Object.assign({}, state), { onChange: input.onChange, name: input.name, onFocus: input.onFocus, onBlur: input.onBlur, value: input.value });
}

/**
 * Select is a form element for which the value can be selected
 * from a limited range.
 *
 * If you pass a callback to the `options` property, be aware
 * that only 100 options will be displayed without pagination.
 * If you want to display more than 100 options,
 * you should use the ModalPickerSingle instead.
 */
function Select(props) {
    const { id = uniqueId(), label, hiddenLabel, value, error, color, text = {}, className = '', valid, placeholder, onChange, onBlur, options, isOptionEnabled = alwaysTrue, keyForOption, labelForOption, isOptionEqual, reloadOptions } = props;
    const { page, loading } = useOptions({
        options,
        value,
        keyForOption,
        isOptionEqual,
        labelForOption,
        reloadOptions,
        pageNumber: 1,
        query: '',
        size: Array.isArray(options) ? options.length : 100,
        optionsShouldAlwaysContainValue: true
    });
    function selectDefaultOption(option) {
        // select the default option when no other option is chosen.
        if (option && value === undefined) {
            option.selected = true;
        }
    }
    const inputProps = {
        id,
        valid,
        invalid: valid === false ? true : undefined,
        type: 'select',
        placeholder,
        onChange: (event) => {
            const index = parseInt(event.target.value, 10);
            onChange(page.content[index]);
        },
        onBlur,
        className: value === undefined ? 'showing-placeholder' : '',
        'aria-label': hiddenLabel && typeof label === 'string' ? label : undefined
    };
    const indexOfValue = value !== undefined
        ? page.content.findIndex((option) => isOptionSelected({
            option,
            keyForOption,
            labelForOption,
            isOptionEqual,
            value
        }))
        : undefined;
    return (React.createElement(FormGroup, { className: className, color: color },
        !hiddenLabel || typeof label !== 'string' ? React.createElement(Label, { for: id }, label) : null,
        loading ? (React.createElement(Loading, { className: "mt-2" }, t({
            key: 'Select.LOADING',
            fallback: 'Loading...',
            overrideText: text.loadingMessage
        }))) : (React.createElement(Input$1, Object.assign({ value: indexOfValue }, inputProps),
            placeholder ? (React.createElement("option", { ref: (option) => selectDefaultOption(option) }, placeholder)) : null,
            page.content.map((option, index) => {
                const optionLabel = labelForOption(option);
                const key = getKeyForOption({
                    option,
                    keyForOption,
                    labelForOption
                });
                return (React.createElement("option", { key: key, value: index, disabled: !isOptionEnabled(option) }, optionLabel));
            }))),
        error));
}
/**
 * Variant of the Select which can be used in a Jarb context.
 */
const JarbSelect = withJarb(Select);
/**
 * Variant of the Select which can be used in a final form.
 */
const FieldSelect = withField(Select);

/**
 * The Pagination component is an enhanced Bootstrap pagination component.
 * It supports working with a `Page` from `@42.nl/spring-connect` and shows
 * a fixed layout for the number of pages, indicating that multiple pages
 * exist for the min and max ranges (e.g. 1 ... 4 5 6 ... 10).
 */
function Pagination({ page, onChange, onPageSizeChange, allowedPageSizes = [5, 10, 20, 50, 100], className, showPreviousAndNextButtons = true, showTotalElements = true, text = {} }) {
    const { first, last, totalPages, totalElements, size, number: current } = page;
    const content = pagesFor$1(current, totalPages);
    // Don't bother to render if there is nothing to paginate.
    if (first &&
        last &&
        (!onPageSizeChange ||
            (allowedPageSizes && allowedPageSizes[0] >= totalElements))) {
        return null;
    }
    return (React.createElement("div", { className: classNames('d-flex justify-content-center flex-wrap flex-sm-nowrap', className) },
        React.createElement(Pagination$1, null,
            showPreviousAndNextButtons ? (React.createElement(PaginationItem, { disabled: first },
                React.createElement(PaginationLink, { onClick: () => onChange(current - 1) },
                    React.createElement(Icon, { icon: "arrow_back", size: 14 })))) : null,
            content.map((item, index) => (React.createElement(PaginationItem, { active: item === current, key: index }, item === '...' ? (React.createElement(PaginationLink, { className: "disabled", disabled: true, type: "button" }, item)) : (React.createElement(PaginationLink, { onClick: () => onChange(item), type: "button" }, item))))),
            showPreviousAndNextButtons ? (React.createElement(PaginationItem, { disabled: last },
                React.createElement(PaginationLink, { onClick: () => onChange(current + 1), type: "button" },
                    React.createElement(Icon, { icon: "arrow_forward", size: 14 })))) : null),
        showTotalElements ? (React.createElement("div", { className: "ms-3 mt-2 mt-sm-0 pagination__total-elements" }, t({
            key: 'Pagination.TOTAL_ELEMENTS',
            data: { totalElements },
            fallback: `${totalElements} records found`,
            overrideText: text.totalElements
        }))) : null,
        onPageSizeChange && allowedPageSizes ? (React.createElement(Select, { onChange: onPageSizeChange, options: allowedPageSizes, labelForOption: (pageSize) => t({
                key: 'Pagination.PAGE_SIZE',
                data: { pageSize },
                fallback: `${pageSize} items per page`
            }), value: size, className: "ms-3 mt-2 mt-sm-0 pagination__select-size", label: t({
                key: 'Pagination.PAGE_SIZE_DROPDOWN_LABEL',
                fallback: 'Select page size',
                overrideText: text.pageSizeDropdownLabel
            }), hiddenLabel: true })) : null));
}
// Calculates which pages surround the current page.
function pagesFor$1(currentPage, totalPages) {
    const ellipsis = '...';
    if (totalPages < 8) {
        return range(1, totalPages + 1);
    }
    if (currentPage < 5) {
        // {1} 2 3 ... 29 30 31
        // till
        // 1 2 3 {4} 5 ... 31
        const tillEllipsis = range(1, Math.max(4, currentPage + 2));
        const fromEllipsis = range(totalPages - (5 - tillEllipsis.length), totalPages + 1);
        return [...tillEllipsis, ellipsis, ...fromEllipsis];
    }
    if (currentPage > totalPages - 4) {
        // 1 ... 27 {28} 29 30 31
        // till
        // 1 2 3 ... 29 30 {31}
        const fromEllipsis = range(Math.min(totalPages - 2, currentPage - 1), totalPages + 1);
        const tillEllipsis = range(1, 7 - fromEllipsis.length);
        return [...tillEllipsis, ellipsis, ...fromEllipsis];
    }
    return [
        1,
        ellipsis,
        ...range(currentPage - 1, currentPage + 2),
        ellipsis,
        totalPages
    ];
}

/**
 * The Pagination component is an enhanced Bootstrap pagination component.
 * It supports working with a `Page` from `@42.nl/spring-connect` and shows
 * a number of pages next to the current page number, always keeping the
 * current page number in the center.
 */
function EpicPagination({ page, onChange, onPageSizeChange, allowedPageSizes = [5, 10, 20, 50, 100], className, showPreviousAndNextButtons = true, showTotalElements = true, text = {} }) {
    const { first, last, totalPages, totalElements, size, number: current } = page;
    // Don't bother to render if there is nothing to paginate.
    if (first &&
        last &&
        (!onPageSizeChange ||
            (allowedPageSizes && allowedPageSizes[0] >= totalElements))) {
        return null;
    }
    const content = pagesFor(current, totalPages);
    return (React.createElement("div", { className: classNames('epic-pagination d-flex justify-content-center flex-wrap flex-sm-nowrap', className) },
        React.createElement(Pagination$1, null,
            showPreviousAndNextButtons ? (React.createElement(React.Fragment, null,
                React.createElement(PaginationItem, { disabled: first },
                    React.createElement(PaginationLink, { onClick: () => onChange(1) },
                        React.createElement(Icon, { className: "center-icon", icon: "first_page" }))),
                React.createElement(PaginationItem, { disabled: first },
                    React.createElement(PaginationLink, { onClick: () => onChange(current - 1) },
                        React.createElement(Icon, { className: "center-icon", icon: "chevron_left" }))))) : null,
            content.map((item, index) => (React.createElement(PaginationItem, { active: item === current, key: index }, item === '...' ? (React.createElement(PaginationLink, { className: "disabled", disabled: true })) : (React.createElement(PaginationLink, { className: item >= 10000
                    ? 'scale-to-page-size-10000'
                    : item >= 1000
                        ? 'scale-to-page-size-1000'
                        : undefined, onClick: () => onChange(item) }, item))))),
            showPreviousAndNextButtons ? (React.createElement(React.Fragment, null,
                React.createElement(PaginationItem, { disabled: last },
                    React.createElement(PaginationLink, { onClick: () => onChange(current + 1) },
                        React.createElement(Icon, { className: "center-icon", icon: "chevron_right" }))),
                React.createElement(PaginationItem, { disabled: last },
                    React.createElement(PaginationLink, { onClick: () => onChange(totalPages) },
                        React.createElement(Icon, { className: "center-icon", icon: "last_page" }))))) : null),
        showTotalElements ? (React.createElement("div", { className: "ms-3 pagination__total-elements" },
            React.createElement("span", null, t({
                key: 'Pagination.TOTAL_ELEMENTS',
                data: { totalElements },
                fallback: `${totalElements} records found`,
                overrideText: text.totalElements
            })))) : null,
        onPageSizeChange && allowedPageSizes ? (React.createElement(Select, { onChange: onPageSizeChange, options: allowedPageSizes, labelForOption: (pageSize) => t({
                key: 'Pagination.PAGE_SIZE',
                data: { pageSize },
                fallback: `${pageSize} items per page`
            }), value: size, className: "ms-3 mt-2 mt-sm-0 pagination__select-size", label: t({
                key: 'Pagination.PAGE_SIZE_DROPDOWN_LABEL',
                fallback: 'Select page size',
                overrideText: text.pageSizeDropdownLabel
            }), hiddenLabel: true })) : null));
}
// Calculates which pages surround the current page.
function pagesFor(currentPage, totalPages) {
    const content = [];
    const prev2 = currentPage - 2;
    if (prev2 > 0) {
        content.push(prev2);
    }
    else {
        content.push('...');
    }
    const prev = currentPage - 1;
    if (prev > 0) {
        content.push(prev);
    }
    else {
        content.push('...');
    }
    content.push(currentPage);
    const next = currentPage + 1;
    if (next <= totalPages) {
        content.push(next);
    }
    else {
        content.push('...');
    }
    const next2 = currentPage + 2;
    if (next2 <= totalPages) {
        content.push(next2);
    }
    else {
        content.push('...');
    }
    return content;
}

const alwaysFalse = () => false;
function ProgressStepper(props) {
    const { steps, onClick, colorForStep, titleForStep, isStepClickable = alwaysFalse, className } = props;
    return (React.createElement("div", { className: classNames('progress-stepper', className) }, steps.map((step, index) => {
        const title = titleForStep(step, index);
        const color = colorForStep(step, index);
        const clickable = isStepClickable(step, index);
        const classes = classNames('step', color, { clickable });
        return (React.createElement("div", { key: title, className: classes },
            React.createElement("div", { className: "step-item", onClick: () => {
                    if (clickable && onClick) {
                        onClick(step, index);
                    }
                } },
                React.createElement("div", { className: `step-circle text-bg-${color}` },
                    React.createElement("span", null, index + 1)),
                React.createElement("div", { className: `step-title text-${color}` }, title)),
            React.createElement("div", { className: "step-bar-left" }),
            React.createElement("div", { className: "step-bar-right" })));
    })));
}

function useScrollToClosestError({ enabled }) {
    const [scroll, setScroll] = useState(0);
    useEffect(() => {
        // Do not scroll on the initial call of the useEffect,
        // and do not scroll when `scrollToClosestError` is `false`.
        if (scroll === 0 || !enabled) {
            return;
        }
        // Scroll after 200 milliseconds to give the FormError's time
        // to render.
        const timeoutId = setTimeout(() => {
            var _a, _b;
            const firstError = document.querySelector('.invalid-feedback');
            // First get the FormError's outer div, then the parent which
            // should be a <FormGroup> and scroll to that point.
            (_b = (_a = firstError === null || firstError === void 0 ? void 0 : firstError.parentElement) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.scrollIntoView({
                behavior: 'smooth'
            });
        }, 200);
        // Cancel the timeout when the SubmitButton is no longer rendered,
        // and when the SubmitButton is clicked rapidly, only scroll
        // for the last click (debounce).
        return () => {
            return clearTimeout(timeoutId);
        };
    }, [scroll, enabled]);
    function doScrollToClosestError() {
        setScroll((value) => value + 1);
    }
    return {
        doScrollToClosestError
    };
}

/**
 * A SubmitButton is a Button with default 'save' icon and  a button
 * of type "submit". It will scroll to the closest error when
 * `scrollToClosestError` is `true` which it is by default.
 */
function SubmitButton({ children, inProgress, size, className = undefined, onClick, scrollToClosestError = true, icon = 'save' }) {
    const { doScrollToClosestError } = useScrollToClosestError({
        enabled: scrollToClosestError
    });
    function submitAndScrollToClosestError(event) {
        doScrollToClosestError();
        if (onClick) {
            onClick(event);
        }
    }
    return (React.createElement(Button, { type: "submit", size: size, color: "primary", inProgress: inProgress, className: className, onClick: submitAndScrollToClosestError, icon: icon }, children));
}

/**
 * Tag is a component which shows a piece of text inside a colored
 * background. Under the hood the Tag component is a bootstrap badge
 * with the option to show a close button.
 *
 * An example of a good use case for using a `Tag` is for blog post
 * which belong in a category, each category could be a `Tag`. Another
 * example is someone's hobby's on a profile page, each hobby could
 * be a `Tag`.
 */
function Tag({ text, color = 'primary', onRemove, className }) {
    const canClose = onRemove !== undefined;
    const classes = classNames('badge rounded-pill', className, {
        [`text-bg-${color}`]: color
    });
    return (React.createElement("span", { className: classes },
        text,
        canClose ? (React.createElement("span", { onClick: onRemove, className: "close-button", role: "button" }, "\u00D7")) : null));
}

/**
 * AsyncContent is a component which can be used to render the result of a
 * call to `useQuery` from `@tanstack/react-query` or `useAsync` from
 * `react-async`.
 *
 * It has the following behaviors:
 *
 * 1. When the state is loading it shows a `ContentState` in the `loading` mode.
 *
 * 2. When an error occurs it shows a `ContentState` in the `error` mode.
 *    By default, it will then show a `Retry` button allowing the user
 *    to try again.
 *
 * 3. When the state has loaded successfully, it will render the `children`
 *    render function, and it provides the `state.data` for you to render.
 *
 * 4. When the state has loaded successfully, it will ask via the `isEmpty`
 *    callback if you consider the `state.data` empty. It will then
 *    render the `emptyContent` when defined or by default show
 *    a `ContentState` in the `empty` mode.
 *
 * With these behaviors you ensure that you always handle the error and
 * loading state when using `useQuery` or `useAsync`.
 */
function AsyncContent(props) {
    const { state, text = {}, showRetryButton = true, isEmpty, emptyContent } = props;
    if (state.isLoading || state.isPending) {
        return (React.createElement(ContentState, { mode: "loading", title: t({
                key: 'AsyncContent.LOADING.TITLE',
                fallback: 'Loading...',
                overrideText: text.loading
            }) }));
    }
    else if (isStateFulfilled(state)) {
        if (state.data && (!isEmpty || !isEmpty(state.data))) {
            return React.createElement(React.Fragment, null, props.children(state.data));
        }
        else if (emptyContent) {
            return React.createElement(React.Fragment, null, emptyContent(state.data));
        }
        else {
            return (React.createElement(ContentState, { mode: "empty", title: t({
                    key: 'AsyncContent.EMPTY.TITLE',
                    fallback: 'No results found',
                    overrideText: text.empty
                }) }));
        }
    }
    else {
        console.error(state.error);
        return (React.createElement(ContentState, { mode: "error", title: t({
                key: 'AsyncContent.ERROR.TITLE',
                fallback: 'Oops something went wrong!',
                overrideText: text.error
            }) }, showRetryButton ? (React.createElement(Button, { icon: "refresh", onClick: () => stateIsAsyncState(state) ? state.reload() : state.refetch() }, t({
            key: 'AsyncContent.ERROR.RETRY',
            fallback: 'Retry',
            overrideText: text.retry
        }))) : null));
    }
}
function isStateFulfilled(state) {
    return stateIsAsyncState(state) ? state.isFulfilled : !state.isError;
}
function stateIsAsyncState(state) {
    return state.hasOwnProperty('isFulfilled');
}

function isEmpty$1(page) {
    return page.totalElements === 0;
}
/**
 * The `AsyncPage` is a variant of `AsyncContent` which expects the
 * result of a call to `useQuery` from `@tanstack/react-query` or
 * `useAsync` from `react-async` to be a `Page` from
 * `@42.nl/spring-connect`.
 *
 * It has all the behaviors of the `AsyncContent` but provides a
 * standard `isEmpty` function which checks if the `totalElements`
 * of the `Page` is zero.
 */
function AsyncPage(props) {
    return React.createElement(AsyncContent, Object.assign({}, props, { isEmpty: isEmpty$1 }));
}

function isEmpty(list) {
    return list.length === 0;
}
/**
 * The `AsyncList` is a variant of the `AsyncContent` which expects
 * the result of a call to `useQuery` from `react-query` or `useAsync`
 * from `react-async` to be an array.
 *
 * It has all the behaviors of the `AsyncContent` but provides a
 * standard `isEmpty` function which checks if the array has the
 * `length` zero.
 */
function AsyncList(props) {
    return React.createElement(AsyncContent, Object.assign({}, props, { isEmpty: isEmpty }));
}

/**
 * Defines an addon to use with an Input element which is a readonly
 * Icon.
 */
function AddonIcon({ icon, className }) {
    return (React.createElement(InputGroupText, null,
        React.createElement(Icon, { icon: icon, className: className })));
}

/**
 * SearchInput is a component which shows an input field which has
 * the onChange debounced by a number of milliseconds. Useful for
 * when you want to run search queries on your back-end, and you
 * don't want to spam the back-end for every keystroke.
 *
 * For the debounce logic it uses lodash.
 */
function SearchInput(props) {
    var _a;
    const { id = uniqueId(), label, hiddenLabel, debounce: debounce$1 = 500, debounceSettings, placeholder, defaultValue, onChange, showIcon = true, className = '', children, size, canClear = true } = props;
    const inputRef = useRef(null);
    const handleChange = useRef(debounce(onChange, debounce$1, debounceSettings));
    // When the onChange changes update the handleChange
    useEffect(() => {
        handleChange.current = debounce(onChange, debounce$1, debounceSettings);
    }, [onChange, debounce$1, debounceSettings]);
    function handleKeyUp(event) {
        if (event.key === 'Enter') {
            onChange(event.currentTarget.value);
        }
    }
    function setValue(value) {
        if (inputRef.current) {
            inputRef.current.value = value;
            onChange(value);
            // if you change the value externally within the debounce time,
            // the debounced callback should be cancelled to prevent an override
            handleChange.current.cancel();
        }
    }
    // We use the defaultValue so this component is completely
    // controlled by us. Otherwise, the value of the <Input> will only
    // update after the onChange. Which would never work because it is
    // debounced.
    const inputProps = {
        id,
        innerRef: inputRef,
        defaultValue,
        onChange: (event) => {
            handleChange.current(event.target.value);
        },
        onKeyUp: handleKeyUp,
        placeholder: placeholder,
        type: 'search',
        'aria-label': hiddenLabel && typeof label === 'string' ? label : undefined
    };
    function getInput() {
        if (showIcon) {
            return (React.createElement(InputGroup, { className: className, size: size },
                React.createElement(AddonIcon, { icon: "search" }),
                React.createElement(Input$1, Object.assign({}, inputProps))));
        }
        return React.createElement(Input$1, Object.assign({ className: className }, inputProps));
    }
    const searchInputWrapper = (React.createElement("div", { className: "search-input" },
        ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.value) && canClear ? (React.createElement(Icon, { icon: "close", className: "cancel-search", onClick: () => setValue('') })) : null,
        children ? React.createElement(React.Fragment, null, children(getInput(), { setValue })) : getInput()));
    return !hiddenLabel || typeof label !== 'string' ? (React.createElement(FormGroup, null,
        React.createElement(Label, { for: id }, label),
        searchInputWrapper)) : (searchInputWrapper);
}

/**
 * The Pager component shows a small variant of bootstraps pagination,
 * it only shows a previous and next button.
 *
 * It supports working with a `Page` from `@42.nl/spring-connect`.
 */
function Pager({ page, onChange, className, text = {} }) {
    const { first, last } = page;
    const { next, previous } = text;
    // Don't bother to render if there is nothing to paginate.
    if (first && last) {
        return null;
    }
    return (React.createElement("div", { className: classNames('pager', className) },
        React.createElement(Button, { className: "me-1", outline: true, icon: "arrow_back", disabled: first, onClick: () => onChange(page.number - 1) }, t({
            overrideText: previous,
            key: 'Pager.PREVIOUS',
            fallback: 'Previous'
        })),
        React.createElement(Button, { outline: true, className: "pager-next", icon: "arrow_forward", iconPosition: "right", disabled: last, onClick: () => onChange(page.number + 1) }, t({
            overrideText: next,
            key: 'Pager.NEXT',
            fallback: 'Next'
        }))));
}

/**
 * Often a modal is content with a header and a cancel and save button
 * in the footer. To make it easier for you developers, we included the
 * OpenCloseModal which already includes the cancel and save button to
 * save you some code duplication for all those similar modals. If you
 * want a more advanced display, you should use the `<Modal>` component.
 */
function OpenCloseModal(props) {
    const { inProgress, onClose, onSave, children, label, text, size, className, saveIcon = 'save', cancelIcon = 'cancel', stickyFooter = true, modalHeaderClassName, modalBodyClassName, modalFooterClassName } = props;
    const header = label ? (React.createElement(ModalHeader, { toggle: onClose, className: modalHeaderClassName }, label)) : null;
    const body = (React.createElement(ModalBody, { className: modalBodyClassName },
        React.createElement(Suspense, { fallback: React.createElement(Loading, null) }, children)));
    return (React.createElement(Modal$1, { toggle: onClose, className: className, isOpen: true, size: size, scrollable: stickyFooter }, onSave ? (React.createElement("form", { onSubmit: onSave, className: "d-flex flex-column overflow-hidden" },
        header,
        body,
        React.createElement(ModalFooter, { className: modalFooterClassName },
            React.createElement(Button, { className: "ms-1", color: "secondary", icon: cancelIcon, onClick: onClose }, t({
                overrideText: text === null || text === void 0 ? void 0 : text.cancel,
                key: 'OpenCloseModal.CANCEL',
                fallback: 'Cancel'
            })),
            React.createElement(SubmitButton, { className: "ms-1", inProgress: !!inProgress, icon: saveIcon }, t({
                overrideText: text === null || text === void 0 ? void 0 : text.save,
                key: 'OpenCloseModal.SAVE',
                fallback: 'Save'
            }))))) : (React.createElement(React.Fragment, null,
        header,
        body))));
}

/**
 * Bootstrap-like Popover component based on the Tippy.js library.
 */
function Popover({ children, placement = 'top', target, offset = 0, distance = 7, tag = 'span', className, isOpen, onClick, openOnClick, onClickOutside, style, maxWidth }) {
    const Tag = tag;
    return (React.createElement(Tippy, { visible: isOpen, onClickOutside: onClickOutside, className: "border-0 tippy-popover", content: children, placement: placement, offset: [offset, distance], interactive: true, zIndex: 1049, maxWidth: maxWidth, trigger: isOpen !== undefined
            ? undefined
            : openOnClick
                ? 'click'
                : 'mouseenter focus' },
        React.createElement(Tag, { className: className, style: style, tabIndex: 0, role: "button", onClick: onClick }, target)));
}

/**
 * The TextButton component is a special type of button which shows
 * like a styled text.
 */
function TextButton({ onClick, children, className }) {
    return (React.createElement("u", { role: "button", className: classNames('align-self-center clickable fw-lighter', className), onClick: onClick }, children));
}

/**
 * InfoTooltip is an info icon that displays a tooltip on mouseover.
 */
function InfoTooltip(props) {
    const { tooltip, size = 16, className } = props;
    return (React.createElement(Tooltip, { content: tooltip, className: className },
        React.createElement(Icon, { icon: "info", size: size })));
}

/**
 * The BooleanIcon is a uniform non-interactive way to display whether
 * a value is either true or false.
 */
function BooleanIcon({ value, size, color, activeColor = 'primary', hoverColor, onChange, className }) {
    const props = {
        icon: value ? 'check_box' : 'check_box_outline_blank',
        size,
        className
    };
    if (onChange) {
        return (React.createElement(Icon, Object.assign({ color: value ? activeColor : color, hoverColor: hoverColor ? hoverColor : activeColor, onClick: (e) => {
                e.preventDefault();
                onChange();
            } }, props)));
    }
    return React.createElement(Icon, Object.assign({ color: color }, props));
}

/**
 * SuccessIcon is used to display whether a process succeeded or not
 * based on a boolean value.
 */
function SuccessIcon({ value, size, color = 'dark', activeColor = 'primary', hoverColor, onChange, className }) {
    const props = {
        icon: value ? 'done' : 'clear',
        size,
        className
    };
    if (onChange) {
        return (React.createElement(Icon, Object.assign({ color: value ? activeColor : color, hoverColor: hoverColor ? hoverColor : activeColor, onClick: (e) => {
                e.preventDefault();
                onChange();
            } }, props)));
    }
    return React.createElement(Icon, Object.assign({ color: color }, props));
}

/**
 * Renders:
 *
 * --- Or ---
 */
function OrSeparator({ className }) {
    return (React.createElement("div", { className: classNames('my-2 px-5 d-flex align-items-center', className) },
        React.createElement("div", { className: "border-top flex-grow-1 position-relative" }),
        React.createElement("em", { className: "mx-2" }, t({
            key: 'OrSeparator.OR',
            fallback: 'OR'
        })),
        React.createElement("div", { className: "border-top flex-grow-1 position-relative" })));
}

/**
 * AttributeList when used with AttributeView is a way to display
 * object properties in a clear and beautiful list.
 */
function AttributeList({ children, className }) {
    return (React.createElement(ListGroup, { flush: true, className: className }, children));
}

/**
 * AttributeView is used inside AttributeList to display object properties
 * in a clear and beautiful list.
 */
function AttributeView({ label, children, className }) {
    return (React.createElement(ListGroupItem, { className: classNames('pl-0', className) },
        React.createElement(ListGroupItemHeading, { className: "small text-primary" }, label),
        children));
}

/**
 * Open and close icon with a nice animation to display whether a collapsable
 * element is open or closed. This is often used in the header of a card with
 * a collapsable body.
 */
function OpenClose({ open, className }) {
    const classes = classNames('open-close', open ? 'is-open' : 'is-closed', className);
    return React.createElement(Icon, { icon: "expand_more", className: classes });
}

/**
 * FavoriteIcon displays a star icon to show the user
 * whether an item is favorite or not. The component
 * includes toggle functionality.
 */
function FavoriteIcon({ value, onChange, className = '', size, color = 'secondary', activeColor = 'primary', hoverColor }) {
    function onClick(e) {
        e.preventDefault();
        e.stopPropagation();
        onChange(!value);
    }
    return (React.createElement(Icon, { color: value ? activeColor : color, hoverColor: hoverColor ? hoverColor : activeColor, className: className, icon: value ? 'star' : 'star_border', onClick: onClick, size: size }));
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        console.error(error, errorInfo.componentStack);
    }
    render() {
        var _a;
        if (this.state.hasError) {
            return (React.createElement(ContentState, { mode: "error", title: t({
                    key: 'ErrorBoundary.ERROR.TITLE',
                    fallback: 'Oops something went wrong!',
                    overrideText: (_a = this.props.text) === null || _a === void 0 ? void 0 : _a.error
                }) }));
        }
        return this.props.children;
    }
}

/**
 * Card is a content container with an optional header and footer.
 * You should use this component over the reactstrap Card component,
 * because this component has Suspense built in to display a loader
 * and wait for its content to be fully loaded before displaying.
 */
function Card({ header, footer, children, className, cardHeaderClassName, cardBodyClassName, cardFooterClassName }) {
    return (React.createElement(Card$1, { className: className },
        header ? (React.createElement(CardHeader, { className: cardHeaderClassName },
            React.createElement(Suspense, { fallback: React.createElement(Loading, null) }, header))) : null,
        React.createElement(CardBody, { className: cardBodyClassName },
            React.createElement(Suspense, { fallback: React.createElement(Loading, null) },
                React.createElement(ErrorBoundary, null, children))),
        footer ? (React.createElement(CardFooter, { className: cardFooterClassName },
            React.createElement(Suspense, { fallback: React.createElement(Loading, null) }, footer))) : null));
}

/**
 * CardOpenClose is a collapsable bootstrap card that you can use to only display
 * important details and hide other details in a collapsable body to prevent extra
 * long pages that cause the user to scroll a lot.
 */
function CardOpenClose(props) {
    const { header, isOpen, toggle, className } = props;
    return (React.createElement(Card$1, { className: classNames('my-2', className) },
        React.createElement(CardHeader, { className: "d-flex justify-content-between align-content-center clickable", onClick: toggle },
            header,
            React.createElement(OpenClose, { open: isOpen })),
        !isOpen
            ? null
            : (React.createElement(Suspense, { fallback: React.createElement(Loading, null) }, props.children()))));
}

/**
 * The Tab component displays a tab navigation item and holds the content
 * that will only be displayed when the tab is active.
 * This component should only be used inside the Tabs component.
 */
function Tab({ icon, label, onClick, active, show = alwaysTrue, disabled, iconColor, className }) {
    if (!show()) {
        return null;
    }
    return (React.createElement(NavItem, { className: classNames({ disabled }, className) },
        React.createElement(NavLink, { active: active, onClick: onClick, disabled: disabled },
            React.createElement("div", { className: "d-flex p-2 justify-content-center" },
                icon ? (React.createElement(Icon, { icon: icon, color: iconColor, className: "me-1" })) : null,
                label))));
}

function TabContent({ show = alwaysTrue, active, children, hideInactiveTabsBy }) {
    if (!show()) {
        return null;
    }
    if (hideInactiveTabsBy === 'excluding-from-dom' && !active) {
        return null;
    }
    return (React.createElement("div", { className: classNames('tab-pane', {
            active
        }) }, children()));
}

/**
 * The Tabs component is a wrapper for multiple containers that will
 * be displayed only one at a time. A container can be activated by
 * clicking the related navigation item on top.
 * This component has Suspense built in to display a loader
 * and wait for its content to be fully loaded before displaying.
 */
function Tabs({ hideInactiveTabsBy = 'excluding-from-dom', children, className }) {
    React.Children.forEach(children, (child) => {
        if (child.type === Tab) {
            return;
        }
        throw new Error('Tabs: expecting children of Tabs to be of type Tab but got something else', 
        // @ts-expect-error accept that the type is not known here
        child.type);
    });
    return (React.createElement("div", { className: classNames('tabs', className) },
        React.createElement(Nav, { tabs: true, fill: true, style: { overflowY: 'hidden' }, className: "flex-nowrap" }, children),
        React.createElement("div", { className: "mt-3 tab-content" },
            React.createElement(Suspense, { fallback: React.createElement(LoadingPage, { height: 200, className: "bg-white" }) }, React.Children.map(children, (_a) => {
                var _b = _a.props, { children } = _b, rest = __rest(_b, ["children"]);
                return (React.createElement(TabContent, Object.assign({ hideInactiveTabsBy: hideInactiveTabsBy }, rest), children));
            })))));
}

/**
 * The AsyncActionButton is a button that triggers a request when it is
 * clicked and is disabled until the request is finished. This button should
 * be used to trigger resource-heavy actions that should not be triggered
 * multiple times asynchronously.
 */
function AsyncActionButton(_a) {
    var { action, children } = _a, buttonProps = __rest(_a, ["action", "children"]);
    const [inProgress, setInProgress] = useState(false);
    function onClick() {
        return __awaiter(this, void 0, void 0, function* () {
            setInProgress(true);
            try {
                yield action();
                setInProgress(false);
            }
            catch (_a) {
                setInProgress(false);
            }
        });
    }
    return (React.createElement(Button, Object.assign({ onClick: onClick, inProgress: inProgress }, buttonProps), children));
}

/**
 * A component which helps you debug things by rendering them as
 * JSON on the screen.
 *
 * Useful for when debugging forms:
 *
 * @example
 *
 * ```tsx
 * <Form
 *   onSubmit={save}
 *   initialValues={initialValues}
 *   render={({ handleSubmit, submitting, values }) => (
 *     <form onSubmit={handleSubmit}>
 *       <Debug value={values} />
 *     </form>
 *   )}
 * />
 * ```
 */
function Debug({ value, defaultOpen = true }) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (React.createElement(CardOpenClose, { header: "DEBUG", isOpen: isOpen, toggle: () => setIsOpen(!isOpen) }, () => React.createElement("pre", null, JSON.stringify(value, null, 2))));
}

function useActive() {
    return useState();
}

function usePromise() {
    return useRef();
}

function AutoSave(props) {
    const { onSave, initialValues } = props;
    const [active, setActive] = useActive();
    const promise = usePromise();
    function onChange(formState) {
        return __awaiter(this, void 0, void 0, function* () {
            if (active === formState.active) {
                return;
            }
            const activeAsString = formState.active;
            setActive(activeAsString);
            if (!active) {
                return;
            }
            if (promise.current) {
                yield promise.current;
            }
            if (!isEqual(formState.values, initialValues)) {
                promise.current = onSave(formState.values);
            }
        });
    }
    return (React.createElement(FormSpy, { subscription: { values: true, active: true }, onChange: onChange }));
}

/**
 * Input is a basic form element which allows the user to enter text.
 *
 * Supports addons and masks.
 */
function Input(_a) {
    var { id = uniqueId(), label, hiddenLabel, onChange, type = 'text', error, color, valid, mask, addon, addonPosition = 'left', className = '' } = _a, htmlInputProps = __rest(_a, ["id", "label", "hiddenLabel", "onChange", "type", "error", "color", "valid", "mask", "addon", "addonPosition", "className"]);
    const inputProps = Object.assign(Object.assign({}, htmlInputProps), { id,
        type,
        valid, invalid: valid === false ? true : undefined, onChange: (event) => onChange(event.target.value), 'aria-label': hiddenLabel && typeof label === 'string' ? label : undefined });
    // Cannot pass a reference when the input requires a mask
    const withoutRef = __rest(inputProps, ["innerRef"]);
    const input = mask === undefined ? (React.createElement(Input$1, Object.assign({ key: "input" }, inputProps))) : (React.createElement(RTMaskedInput, Object.assign({ key: "mask", mask: mask }, withoutRef, { render: reactStrapInput$1 })));
    let content = input;
    if (addon !== undefined) {
        if (addonPosition === 'left') {
            content = (React.createElement(InputGroup, null,
                addon,
                input));
        }
        else {
            content = (React.createElement(InputGroup, null,
                input,
                addon));
        }
    }
    return (React.createElement(FormGroup, { className: className, color: color },
        !hiddenLabel ? React.createElement(Label, { for: id }, label) : null,
        content,
        error));
}
function reactStrapInput$1(ref, props) {
    return React.createElement(Input$1, Object.assign({ innerRef: ref }, props));
}
/**
 * Variant of the Input which can be used in a Jarb context.
 */
const JarbInput = withJarb(Input);
/**
 * Variant of the Input which can be used in a final form.
 */
const FieldInput = withField(Input);

/**
 * Textarea is a basic form element which allows the user to enter large
 * texts, auto-grows when the user enters a lot of text.
 */
function Textarea(props) {
    const { id = uniqueId(), label, hiddenLabel, placeholder, onBlur, onFocus, onChange, value, color, error, valid, className = '' } = props;
    const inputProps = {
        id,
        value,
        placeholder,
        onChange: (event) => onChange(event.target.value),
        onFocus,
        onBlur,
        'aria-label': hiddenLabel && typeof label === 'string' ? label : undefined
    };
    const classes = classNames('form-control', { 'is-invalid': valid === false });
    return (React.createElement(FormGroup, { className: className, color: color },
        !hiddenLabel || typeof label !== 'string' ? React.createElement(Label, { for: id }, label) : null,
        React.createElement(TextareaAutosize, Object.assign({ className: classes }, inputProps)),
        error));
}
/**
 * Variant of the Textarea which can be used in a Jarb context.
 */
const JarbTextarea = withJarb(Textarea);
/**
 * Variant of the Textarea which can be used in a final form.
 */
const FieldTextarea = withField(Textarea);

/*
  When module.toolbar exists it will generate the formats for Quill
  based on the items in the toolbar.

  When module or toolbar is empty the default formats are returned.
*/
function formatsFromToolbarModule(modules) {
    // If there are no modules use the default formats
    if (!modules) {
        return defaultFormats();
    }
    let { toolbar } = modules;
    // If there is no toolbar defined use the default formats
    if (!toolbar) {
        return defaultFormats();
    }
    // When advanced toolbars are used we want to take the container.
    if (toolbar.container) {
        toolbar = toolbar.container;
    }
    if (!Array.isArray(toolbar)) {
        throw new Error('TextEditor: expecting modules.toolbar to be an array');
    }
    return uniq(formatsForToolbar(toolbar));
}
/*
  Recursive function which can generate the formats based
  on the toolbar
*/
function formatsForToolbar(toolbarItem) {
    if (typeof toolbarItem === 'string') {
        // toolbar strings are also the formats, so we can just return
        // them as is.
        return toolbarItem;
    }
    else if (Array.isArray(toolbarItem)) {
        // Further reduce the array of formats, it could be an array of
        // strings or objects or mixed at this point.
        return flatMap(toolbarItem, formatsForToolbar);
    }
    else {
        // When an object the keys of the objects are the formats
        return formatsForToolbar(Object.keys(toolbarItem));
    }
}
/*
  Helper to create the default formats. These are the formats of
  the default toolbar of the TextEditor component.
  
  Is a function to prevent accidental mutations.
*/
function defaultFormats() {
    return ['header', 'list', 'bold', 'italic', 'underline', 'link'];
}

/**
 * Textarea is a form element which allows the user to enter large
 * formatted text.
 *
 * Disclaimer: when using the TextEditor you must sanitize the output
 * when rendering the output in the browser. If you do not do this you
 * risk an XSS attack.
 *
 * The 42 way of dealing with this problem is by using jsoup and to use the
 * sanitizer, https://jsoup.org/cookbook/cleaning-html/whitelist-sanitizer,
 * with a whitelist. The whitelist should only contain elements which the
 * TextEditor generates. The sanitizer should be applied before sending
 * the content to the browser.
 */
function TextEditor(props) {
    const { id = uniqueId(), label, hiddenLabel, placeholder, onChange, onBlur, onFocus, valid, error, color, value, className = '', modules, formats } = props;
    const inputProps = {
        id,
        placeholder,
        onChange: (content) => onChange(content),
        onBlur: () => doBlur(onBlur),
        onFocus,
        value,
        modules,
        formats: formats ? formats : formatsFromToolbarModule(modules)
    };
    const classes = classNames({ 'is-invalid': valid === false });
    return (React.createElement(FormGroup, { className: className, color: color },
        !hiddenLabel ? React.createElement(Label, null, label) : null,
        React.createElement(ReactQuill, Object.assign({ className: classes }, inputProps)),
        error));
}
/**
 * Variant of the TextEditor which can be used in a Jarb context.
 */
const JarbTextEditor = withJarb(TextEditor);
/**
 * Variant of the TextEditor which can be used in a final form.
 */
const FieldTextEditor = withField(TextEditor);

function combineFormat(dateFormat, timeFormat) {
    if (dateFormat !== false && timeFormat !== false) {
        return `${dateFormat} ${timeFormat}`;
    }
    else if (dateFormat !== false) {
        return dateFormat;
    }
    else if (timeFormat !== false) {
        return timeFormat;
    }
    throw new Error('DateTimeInput: dateFormat and timeFormat cannot both be false');
}
function formatToMask(dateFormat, timeFormat) {
    const dateMask = dateFormatToMask(dateFormat);
    const timeMask = timeFormatToMask(timeFormat);
    const dateMaskIsDefined = dateMask.length > 0;
    if (dateMaskIsDefined && timeMask.length > 0) {
        return [...dateMask, ' ', ...timeMask];
    }
    else if (dateMaskIsDefined) {
        return dateMask;
    }
    else {
        return timeMask;
    }
}
function timeFormatToMask(timeFormat) {
    if (timeFormat === false) {
        return [];
    }
    return timeFormat.split('').map((char) => (char === ':' ? ':' : /\d/));
}
function dateFormatToMask(dateFormat) {
    if (dateFormat === false) {
        return [];
    }
    const separator = extractSeparator(dateFormat);
    return dateFormat
        .split('')
        .map((char) => (char === separator ? separator : /\d/));
}
function extractSeparator(dateFormat) {
    if (dateFormat.includes('-')) {
        return '-';
    }
    if (dateFormat.includes('/')) {
        return '/';
    }
    if (dateFormat.includes('.')) {
        return '.';
    }
    throw new Error('DateTimeInput: cannot extract separator from dateFormat');
}

function DateTimeModal(props) {
    const { onClose, onSave, label, dateFormat, timeFormat, locale, utc = true, text } = props;
    const isDateAllowed = get(props, 'isDateAllowed', constant(true));
    const [value, setValue] = useState(props.defaultValue ? moment(props.defaultValue) : '');
    return (React.createElement(Modal$1, { isOpen: true, onClose: onClose, className: "date-time-modal", size: "sm" },
        React.createElement(ModalHeader, { toggle: onClose }, label),
        React.createElement(ReactDatetimeClass, { input: false, open: true, closeOnSelect: false, onChange: setValue, value: value, dateFormat: dateFormat, timeFormat: timeFormat, locale: locale, utc: utc, isValidDate: (date, current) => isDateAllowed(date, current) }),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { className: "ms-1", color: "secondary", icon: "cancel", onClick: onClose }, t({
                overrideText: text === null || text === void 0 ? void 0 : text.cancel,
                key: 'OpenCloseModal.CANCEL',
                fallback: 'Cancel'
            })),
            React.createElement(Button, { className: "ms-1", color: "primary", icon: "save", onClick: () => onSave(value) }, t({
                overrideText: text === null || text === void 0 ? void 0 : text.select,
                key: 'DateTimeModal.SELECT',
                fallback: 'Select'
            })))));
}

/**
 * Creates a function which accept a date and returns whether
 * the date lies before the end date.
 *
 * Useful for the `DateTimeInput` components `isDateAllowed` prop.
 *
 * @param {MomentInput} end The end date to which the date must be before.
 * @param {IsDateBeforeConfig} config The configuration of the isDateBefore function.
 * @returns {DateChecker} A date validator function which checks if the date lies before the end date.
 */
function isDateBefore(end, config = { inclusive: false }) {
    const { inclusive } = config;
    if (!end) {
        return alwaysTrue;
    }
    return (date) => {
        if (!date) {
            return true;
        }
        if (inclusive) {
            return moment(date).isSameOrBefore(end);
        }
        else {
            return moment(date).isBefore(end);
        }
    };
}
/**
 * Creates a function which accept a date and returns whether
 * the date lies after the end date.
 *
 * Useful for the `DateTimeInput` components `isDateAllowed` prop.
 *
 * @param {MomentInput} start The start date to which the date must be after.
 * @param {IsDateAfterConfig} config The configuration of the isDateAfter function.
 * @returns {DateChecker} A date validator function which checks if the date lies after the start date.
 */
function isDateAfter(start, config = { inclusive: false }) {
    const { inclusive } = config;
    if (!start) {
        return alwaysTrue;
    }
    return (date) => {
        if (!date) {
            return true;
        }
        if (inclusive) {
            return moment(date).isSameOrAfter(start);
        }
        else {
            return moment(date).isAfter(start);
        }
    };
}
/**
 * Creates a function which accepts a start and end date and returns
 * whether the date lies between the start and end date.
 *
 * Useful for the `DateTimeInput` components `isDateAllowed` prop.
 *
 * @param {MomentInput} start The start date to which the date must be before.
 * @param {MomentInput} end The end date to which the date must be after.
 * @param {IsDateBetweenConfig} config The configuration of the isDateBetween function.
 * @returns {DateChecker} A date validator function which checks if the date is between the start and end date
 */
function isDateBetween(start, end, config = {
    startInclusive: false,
    endInclusive: false
}) {
    // If there is no start and end allow the user to pick all dates
    if (!start && !end) {
        return alwaysTrue;
    }
    const { startInclusive, endInclusive } = config;
    if (!end) {
        // If there is no end but a start then the date must at least
        // lie after the start.
        return isDateAfter(start, { inclusive: startInclusive });
    }
    if (!start) {
        // If there is no start but an end then the date must at least
        // lie before the end.
        return isDateBefore(end, { inclusive: endInclusive });
    }
    return (date) => {
        if (!date) {
            return true;
        }
        const startChar = startInclusive ? '[' : '(';
        const endChar = endInclusive ? ']' : ')';
        return moment(date).isBetween(start, end, undefined, `${startChar}${endChar}`);
    };
}
/**
 * Creates a function which returns whether the date is an actual date.
 *
 * Useful for the `DateTimeInput` component.
 *
 * @param {IsDateConfig} config The configuration of the isDateBetween function.
 * @returns {DateChecker} A date validator function which checks if the date is an actual date
 */
function isDate(config) {
    return (date) => {
        if (!date) {
            return false;
        }
        // _ indicates an input mask
        if (typeof date === 'string' && date.includes('_')) {
            return false;
        }
        return moment(date, combineFormat(config.dateFormat, config.timeFormat)).isValid();
    };
}

/**
 * Creates a final form date validator function which validates if
 * the date is an actual date that matches the provided format.
 *
 * Useful for the `JarbDateTimeInput` components `validators` prop.
 *
 * @param {IsDateValidatorConfig} config The configuration for the isDateBeforeValidator
 * @returns {FieldValidator<MomentInput>} A date validator function which checks if the date lies before the end date.
 */
function isDateValidator(config) {
    const { dateFormat, timeFormat, label } = config;
    return (value) => {
        if (!value || isDate({ dateFormat, timeFormat })(value)) {
            return undefined;
        }
        return t({
            key: 'DateTimeInput.INVALID_DATE',
            fallback: `Must match required format "${combineFormat(dateFormat, timeFormat)}"`,
            data: {
                format: combineFormat(dateFormat, timeFormat),
                label
            },
            overrideText: config.overrideErrorText
        });
    };
}
/**
 * Creates a final form date validator function which validates if
 * the date lies before the end date.
 *
 * Useful for the `JarbDateTimeInput` components `validators` prop.
 *
 * @param {IsDateAfterValidatorConfig} config The configuration for the isDateBeforeValidator
 * @returns {FieldValidator<MomentInput>} A date validator function which checks if the date lies before the end date.
 */
function isDateBeforeValidator(config) {
    const { end, label, overrideErrorText } = config;
    return (value, allValues) => {
        if (!value) {
            return undefined;
        }
        const before = get(allValues, end.path);
        const result = isDateBefore(before, { inclusive: end.inclusive })(value);
        if (result) {
            return undefined;
        }
        else {
            return dateBeforeError({ label, end, overrideErrorText });
        }
    };
}
/**
 * Creates a final form date validator function which validates if
 * the date lies after the start date.
 *
 * Useful for the `JarbDateTimeInput` components `validators` prop.
 *
 * @param {IsDateAfterValidatorConfig} config The configuration for the isDateBeforeValidator
 * @returns {FieldValidator<MomentInput>} A date validator function which checks if the date lies after the start date.
 */
function isDateAfterValidator(config) {
    const { start, label, overrideErrorText } = config;
    return (value, allValues) => {
        if (!value) {
            return undefined;
        }
        const after = get(allValues, start.path);
        const result = isDateAfter(after, { inclusive: start.inclusive })(value);
        if (result) {
            return undefined;
        }
        else {
            return dateAfterError({ label, start, overrideErrorText });
        }
    };
}
/**
 * Creates a final form date validator function which validates if
 * the date lies after the start date, and before the end date.
 *
 * Useful for the `JarbDateTimeInput` components `validators` prop.
 *
 * @param {IsDateBetweenValidatorConfig} config The configuration for the isDateBetweenValidatorConfig
 * @returns {FieldValidator<MomentInput>} A date validator function which checks if the date lies after the start date, and before the end date.
 */
function isDateBetweenValidator(config) {
    const { start, end, label, overrideErrorText } = config;
    return (value, allValues) => {
        if (!value) {
            return undefined;
        }
        const after = get(allValues, start.path);
        const before = get(allValues, end.path);
        const result = isDateBetween(after, before, {
            startInclusive: start.inclusive,
            endInclusive: end.inclusive
        })(value);
        if (result) {
            return undefined;
        }
        else {
            if (!before) {
                return dateAfterError({ label, start, overrideErrorText });
            }
            if (!after) {
                return dateBeforeError({ label, end, overrideErrorText });
            }
            return t({
                key: 'DateTimeInput.DATE_BETWEEN_ERROR',
                fallback: `The "${label}" must be between the "${start.label}" and "${end.label}"`,
                data: { start: start.label, label, end: end.label },
                overrideText: overrideErrorText
            });
        }
    };
}
function dateAfterError({ label, start, overrideErrorText }) {
    return t({
        key: 'DateTimeInput.DATE_AFTER_ERROR',
        fallback: `The "${label}" must be after the "${start.label}"`,
        data: { start: start.label, end: label },
        overrideText: overrideErrorText
    });
}
function dateBeforeError({ label, end, overrideErrorText }) {
    return t({
        key: 'DateTimeInput.DATE_BEFORE_ERROR',
        fallback: `The "${label}" must be before the "${end.label}"`,
        data: { start: label, end: end.label },
        overrideText: overrideErrorText
    });
}

var _a;
// istanbul ignore next
// @ts-expect-error This is only a temporary hacky fix for rollup related issues https://github.com/arqex/react-datetime/issues/843
const Datetime = (_a = ReactDatetimeClass.default) !== null && _a !== void 0 ? _a : ReactDatetimeClass;
/**
 * DateTimeInput is a form element which allows the user to select:
 * date and times, times, and dates.
 */
function DateTimeInput(props) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { id = uniqueId(), label, hiddenLabel, placeholder, valid, onFocus, dateFormat, timeFormat, color, value, error, locale, utc = true, mode = 'default', text, className = '' } = props;
    const isDateAllowed = get(props, 'isDateAllowed', constant(true));
    if (!dateFormat && !timeFormat) {
        throw new Error('DateTimeInput: dateFormat and timeFormat cannot both be false. This is a programmer error.');
    }
    function onChange(newValue) {
        // newValue is either a valid Moment object when selected via
        // picker or the user typed in a valid date, or it is a string
        // when the user typed in a partial or invalid date manually.
        if (moment.isMoment(newValue)) {
            props.onChange(newValue.toDate());
        }
        else {
            props.onChange(newValue);
        }
    }
    function onBlur(currentValue) {
        onChange(currentValue);
        doBlur(props.onBlur);
        setIsModalOpen(false);
    }
    const formGroupClassName = classNames('date-time-input', className, {
        'with-modal': mode === 'modal'
    });
    return (React.createElement(FormGroup, { className: formGroupClassName, color: color },
        !hiddenLabel || typeof label !== 'string' ? (React.createElement(Label, { for: id },
            label,
            React.createElement("span", { className: "date-time-input-format text-muted" },
                "(",
                combineFormat(dateFormat, timeFormat),
                ")"))) : null,
        React.createElement(Datetime, { inputProps: {
                mask: formatToMask(dateFormat, timeFormat),
                placeholder,
                invalid: valid === false,
                id,
                autoComplete: 'off',
                'aria-label': hiddenLabel && typeof label === 'string' ? label : undefined
            }, open: mode === 'modal' ? false : undefined, renderInput: (inputProps) => maskedInputGroup(inputProps, mode === 'modal' ? () => setIsModalOpen(true) : undefined), onChange: onChange, onBlur: onBlur, onOpen: onFocus, onClose: onBlur, value: value, dateFormat: dateFormat, timeFormat: timeFormat, closeOnSelect: true, locale: locale, utc: utc, isValidDate: isDateAllowed }),
        error,
        mode === 'modal' && isModalOpen ? (React.createElement(DateTimeModal, { onClose: () => setIsModalOpen(false), onSave: onBlur, dateFormat: dateFormat, timeFormat: timeFormat, defaultValue: value instanceof Date || isDate({ dateFormat, timeFormat })(value)
                ? value
                : undefined, isDateAllowed: isDateAllowed, label: placeholder, locale: locale, utc: utc, text: text })) : null));
}
function maskedInputGroup(props, onClick) {
    return (React.createElement(InputGroup, null,
        React.createElement(RTMaskedInput, Object.assign({}, props, { render: reactStrapInput })),
        props.value ? (React.createElement("div", { className: "input-group-text" },
            React.createElement(Icon, { icon: "close", onClick: () => props.onChange({ target: { value: '' } }), className: "date-time-input__clear-icon" }))) : null,
        onClick ? (React.createElement(Button, { onClick: onClick },
            React.createElement(Icon, { icon: "calendar_today" }))) : null));
}
function reactStrapInput(ref, props) {
    return React.createElement(Input$1, Object.assign({ innerRef: ref }, props));
}
/**
 * Variant of the DateTimeInput which can be used in a Jarb context.
 */
const JarbDateTimeInput = withJarb(DateTimeInput, (props) => [isDateValidator(Object.assign(Object.assign({}, props), { label: props.jarb.label }))]);
/**
 * Variant of the DateTimeInput which can be used in a final form.
 */
const FieldDateTimeInput = withField(DateTimeInput, (props) => [
    isDateValidator(Object.assign(Object.assign({}, props), { label: typeof props.label === 'string' ? props.label : undefined }))
]);

/**
 * The EmptyModal is a component which is rendered when a
 * Modal is empty. Can either display a message saying that
 * it is empty when there is nothing to be selected. Or a
 * message saying that there is no result for the current
 * search query when the user has searched.
 */
function EmptyModal(props) {
    const { userHasSearched, title, subTitle } = props;
    if (userHasSearched) {
        return (React.createElement(ContentState, { mode: "no-results", title: t({
                key: 'EmptyModal.NO_RESULTS.TITLE',
                fallback: 'Empty',
                overrideText: title
            }), subTitle: t({
                key: 'EmptyModal.NO_RESULTS.SUBTITLE',
                fallback: 'No results were found please try again with a different query.',
                overrideText: subTitle
            }) }));
    }
    else {
        return (React.createElement(ContentState, { mode: "empty", title: t({
                key: 'EmptyModal.EMPTY.TITLE',
                fallback: 'Empty',
                overrideText: title
            }), subTitle: t({
                key: 'EmptyModal.EMPTY.SUBTITLE',
                fallback: 'There is nothing here yet, the collection is empty.',
                overrideText: subTitle
            }) }));
    }
}

/**
 * The ModalPicker component is an abstraction used by the ModalPickerMultiple
 * and ModalPickerSingle to render a modal. It contains the code which
 * could be shared by the two components.
 *
 * It supports working with a `Page` from `@42.nl/spring-connect`.
 */
function ModalPicker(props) {
    const { placeholder, children, canSearch = true, canSearchSync, closeModal, modalSaved, addButton, options, renderOptionsConfig, pageSize = 10, value, isOptionEqual, labelForOption, reloadOptions, text = {}, multiple, renderSelection } = props;
    const [pageNumber, setPageNumber] = useState(1);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(value);
    const { page, loading } = useOptions({
        options,
        value,
        isOptionEqual,
        labelForOption,
        reloadOptions,
        pageNumber,
        query,
        size: pageSize,
        optionsShouldAlwaysContainValue: false
    });
    const shouldRenderPagination = !(page.first && page.last);
    return (React.createElement(Modal$1, { isOpen: true, toggle: closeModal },
        React.createElement(ModalHeader, { toggle: closeModal }, placeholder),
        React.createElement(ModalBody, null,
            renderSelection
                ? // @ts-expect-error Type of selected is always the same as Value
                    renderSelection(setSelected, selected)
                : null,
            canSearch ? (React.createElement(SearchInput, { defaultValue: query, placeholder: t({
                    overrideText: text.placeholder,
                    key: 'ModalPicker.SEARCH',
                    fallback: 'Search...'
                }), label: t({
                    overrideText: text.searchLabel,
                    key: 'ModalPicker.SEARCH_LABEL',
                    fallback: 'Start typing to search'
                }), hiddenLabel: true, debounce: canSearchSync ? 0 : 500, onChange: setQuery, className: "mb-3" })) : null,
            renderChildren()),
        shouldRenderPagination ? (React.createElement(ModalFooter, { className: "d-flex justify-content-center" },
            React.createElement(Pagination, { page: page, onChange: (page) => setPageNumber(page), showTotalElements: false }))) : null,
        React.createElement(ModalFooter, { className: "d-flex flex-row justify-content-between" },
            addButton ? (React.createElement(Button$1, { color: "primary", onClick: () => addButton.onClick() }, addButton.label)) : (React.createElement("div", null)),
            React.createElement("div", null,
                React.createElement(Button$1, { className: "ms-1", color: "secondary", onClick: () => closeModal() }, t({
                    overrideText: text.cancel,
                    key: 'ModalPicker.CANCEL',
                    fallback: 'Cancel'
                })),
                React.createElement(Button$1, { className: "ms-1", color: "primary", 
                    // @ts-expect-error Type of selected is always equal to what modalSaved expects
                    onClick: () => modalSaved(selected) }, t({
                    overrideText: text.select,
                    key: 'ModalPicker.SELECT',
                    fallback: 'Select'
                }))))));
    function renderChildren() {
        if (loading) {
            return React.createElement(ContentState, { mode: "loading", title: "" });
        }
        if (page.totalElements === 0) {
            return React.createElement(EmptyModal, { userHasSearched: query !== '' });
        }
        if (renderOptionsConfig.renderOptions) {
            return renderOptionsConfig.renderOptions(mapOptions(renderOptionsConfig, setSelected));
        }
        return React.createElement(React.Fragment, null, children(mapOptions(renderOptionsConfig, setSelected)));
    }
    function mapOptions(renderOptionsConfig, setSelected) {
        const { isOptionEqual, keyForOption, isOptionEnabled } = renderOptionsConfig;
        return page.content.map((option) => {
            const isOptionSelectedConfig = {
                option,
                labelForOption,
                isOptionEqual,
                keyForOption,
                value: selected
            };
            const isSelected = isOptionSelected(isOptionSelectedConfig);
            const enabled = isOptionEnabled ? isOptionEnabled(option) : true;
            function toggle() {
                if (multiple) {
                    const selectedWithoutOption = Array.isArray(selected)
                        ? selected.filter((value) => isOptionEqual
                            ? !isOptionEqual(value, option)
                            : !compareOptions(value, option, isOptionSelectedConfig))
                        : [];
                    const newSelected = isSelected
                        ? selectedWithoutOption
                        : [...selectedWithoutOption, option];
                    setSelected(newSelected);
                }
                else {
                    // @ts-expect-error Value is of type T
                    setSelected(isSelected ? undefined : option);
                }
            }
            return {
                option,
                key: getKeyForOption({ option, keyForOption, labelForOption }),
                label: labelForOption(option),
                isSelected,
                enabled,
                toggle: enabled ? () => toggle() : () => undefined
            };
        });
    }
}

function ModalPickerOpener({ openModal, label, icon, alignButton, onClear, text = {}, renderValue, value }) {
    const hasValue = !!value;
    const wrapperClassName = classNames('d-flex', 'align-items-center', {
        'flex-row-reverse': alignButton === 'left',
        'justify-content-between': alignButton === 'right' && hasValue,
        'justify-content-end': alignButton === 'left' || (alignButton === 'right' && !hasValue)
    });
    const buttonClassName = classNames('flex-grow-0', 'flex-shrink-0', {
        'me-3': hasValue && alignButton === 'left',
        'ms-3': hasValue && alignButton !== 'left'
    });
    // @ts-expect-error Accept that DisplayValues is sometimes an array and sometimes not
    const displayValue = renderValue(value);
    return (React.createElement("div", { className: wrapperClassName },
        displayValue,
        hasValue && onClear ? (React.createElement(TextButton, { onClick: onClear, className: "ms-3" }, t({
            overrideText: text.clear,
            key: 'ModalPickerOpener.CLEAR',
            fallback: 'Clear'
        }))) : null,
        React.createElement(Button$1, { color: "primary", onClick: openModal, className: buttonClassName },
            icon ? React.createElement(Icon, { icon: icon, className: "me-2 align-bottom" }) : null,
            label)));
}

function useResizeObserver(handleResize) {
    return typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver((entries) => entries.forEach(handleResize))
        : undefined;
}

function useComponentOverflow(ref, values) {
    const [componentOverflow, setComponentOverflow] = useState(isOverflowing(ref.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleResize = useCallback(handleResizeCallback(ref, setComponentOverflow), [ref]);
    const resizeObserver = useResizeObserver(handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(layoutEffect(ref, handleResize, resizeObserver), [
        ref,
        handleResize,
        values,
        resizeObserver
    ]);
    return componentOverflow;
}
function isOverflowing(el) {
    return el ? el.clientWidth < el.scrollWidth : false;
}
function handleResizeCallback(ref, setComponentOverflow) {
    return () => {
        if (ref.current) {
            setComponentOverflow(isOverflowing(ref.current));
        }
    };
}
function layoutEffect(ref, handleResize, resizeObserver) {
    return () => {
        if (!ref.current) {
            return;
        }
        handleResize();
        const el = ref.current;
        if (resizeObserver) {
            resizeObserver.observe(el);
            return () => {
                resizeObserver.unobserve(el);
            };
        }
        el.addEventListener('resize', handleResize, { passive: true });
        return () => {
            el.removeEventListener('resize', handleResize);
        };
    };
}

function ModalPickerValueTruncator({ value, labelForOption }) {
    const ref = useRef(null);
    const isOverflowing = useComponentOverflow(ref, value);
    if (!value) {
        return null;
    }
    const text = Array.isArray(value) ? (React.createElement(React.Fragment, null, value.map(labelForOption).join(', '))) : (labelForOption(value));
    return (React.createElement("span", { className: "text-truncate position-relative", ref: ref },
        isOverflowing ? (React.createElement(Tooltip, { content: text, className: "w-100 h-100 position-absolute", tag: "div" }, ' ')) : null,
        text));
}

/**
 * The ModalPickerSingle is a form element which allows the user
 * to select an option from a modal.
 *
 * The use case is that when there are too many options to render
 * in a simple Select you can use the ModalPickerSingle.
 *
 * Use the ModalPickerSingle when the user does not precisely know
 * which option he / she is going to select. Otherwise, use the
 * TypeaheadSingle which is useful when the user is an expert and
 * can type in the selection quicker than he can select it from the
 * modal.
 */
function ModalPickerSingle(props) {
    const { label, hiddenLabel, color, placeholder, icon, className = '', value, onChange, addButton, canSearch, pageSize, options, keyForOption, isOptionEqual, isOptionEnabled, labelForOption, reloadOptions, renderValue, alignButton, renderOptions, error, canClear = true, text } = props;
    const [isOpen, setIsOpen] = useState(false);
    function modalSaved(selected) {
        setIsOpen(false);
        onChange(selected);
    }
    function closeModal() {
        setIsOpen(false);
    }
    const openModal = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(true);
    };
    const modalPickerOpenerProps = {
        openModal,
        label: placeholder,
        icon,
        alignButton,
        renderValue: renderValue
            ? renderValue
            : (value) => (React.createElement(ModalPickerValueTruncator, { value: value, labelForOption: labelForOption })),
        onClear: canClear ? () => onChange(undefined) : undefined,
        value
    };
    return (React.createElement(FormGroup, { className: className, color: color },
        !hiddenLabel ? React.createElement(Label, null, label) : null,
        React.createElement(ModalPickerOpener, Object.assign({}, modalPickerOpenerProps)),
        error,
        isOpen ? renderModal() : null));
    function renderModal() {
        return (React.createElement(ModalPicker, { placeholder: placeholder, canSearch: canSearch, canSearchSync: Array.isArray(options), closeModal: closeModal, modalSaved: modalSaved, addButton: addButton, value: value, renderOptionsConfig: {
                isOptionEqual,
                keyForOption,
                isOptionEnabled,
                renderOptions
            }, text: text, pageSize: pageSize, options: options, labelForOption: labelForOption, reloadOptions: reloadOptions }, (options) => options.map((option) => (React.createElement(FormGroup, { key: option.key, check: true, disabled: !option.enabled },
            React.createElement(Label, { check: true },
                React.createElement(Input$1, { type: "radio", checked: option.isSelected, disabled: !option.enabled, onChange: option.toggle }),
                option.label))))));
    }
}
/**
 * Variant of the ModalPickerSingle which can be used in a Jarb context.
 */
const JarbModalPickerSingle = withJarb(ModalPickerSingle);
/**
 * Variant of the ModalPickerSingle which can be used in a final form.
 */
const FieldModalPickerSingle = withField(ModalPickerSingle);

/**
 * The ModalPickerMultiple is a form element which allows the user
 * to select multiple options from a modal.
 *
 * The use case is that when there are too many options to render
 * in a simple Select you can use the ModalPickerMultiple.
 *
 * Use the ModalPickerMultiple when the user does not precisely know
 * which options he / she is going to select. Otherwise, use the
 * TypeaheadMultiple which is useful when the user is an expert and
 * can type in the selections quicker than he can select it from
 * the modal.
 */
function ModalPickerMultiple(props) {
    const { label, hiddenLabel, color, placeholder, icon, className = '', value, onChange, addButton, canSearch = true, pageSize = 10, options, keyForOption, isOptionEqual, isOptionEnabled, labelForOption, reloadOptions, renderValue, alignButton, renderOptions, error, onBlur, canClear = true, text } = props;
    const [isOpen, setIsOpen] = useState(false);
    function modalSaved(selected) {
        setIsOpen(false);
        onChange(selected);
        doBlur(onBlur);
    }
    function closeModal() {
        setIsOpen(false);
    }
    const openModal = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(true);
    };
    const modalPickerOpenerProps = {
        openModal,
        label: placeholder,
        icon,
        alignButton,
        renderValue: renderValue
            ? renderValue
            : (value) => (React.createElement(ModalPickerValueTruncator, { value: value, labelForOption: labelForOption })),
        onClear: canClear ? () => onChange(undefined) : undefined,
        // Only render the clear button when it has a value
        value: (value === null || value === void 0 ? void 0 : value.length) ? value : undefined
    };
    return (React.createElement(FormGroup, { className: className, color: color },
        !hiddenLabel ? React.createElement(Label, null, label) : null,
        React.createElement(ModalPickerOpener, Object.assign({}, modalPickerOpenerProps)),
        error,
        isOpen ? (React.createElement(ModalPicker, { multiple: true, placeholder: placeholder, canSearch: canSearch, canSearchSync: Array.isArray(options), closeModal: closeModal, modalSaved: modalSaved, addButton: addButton, value: value, renderOptionsConfig: {
                isOptionEqual,
                keyForOption,
                isOptionEnabled,
                renderOptions
            }, renderSelection: renderModalCurrentSelection, text: text, pageSize: pageSize, options: options, labelForOption: labelForOption, reloadOptions: reloadOptions }, (options) => options.map((option) => (React.createElement(FormGroup, { key: option.key, check: true, disabled: !option.enabled },
            React.createElement(Label, { check: true },
                React.createElement(Input$1, { type: "checkbox", checked: option.isSelected, disabled: !option.enabled, onChange: option.toggle }),
                option.label)))))) : null));
    function renderModalCurrentSelection(setSelected, selected) {
        return (React.createElement(Row, { className: "mb-3 py-2 bg-dark bg-opacity-10" },
            React.createElement(Col, { className: "bg-opacity-100" }, (selected === null || selected === void 0 ? void 0 : selected.length) ? (selected.map((value) => {
                const label = labelForOption(value);
                return (React.createElement(Tag, { key: label, onRemove: () => optionClicked(selected, setSelected, value), text: label }));
            })) : (React.createElement("span", { className: "text-muted" }, t({
                key: 'ModalPickerMultiple.NO_OPTION_SELECTED',
                fallback: 'No option selected'
            }))))));
    }
    function optionClicked(selected, setSelected, option) {
        setSelected(selected.filter((value) => !isOptionSelected({
            option,
            keyForOption,
            labelForOption,
            isOptionEqual,
            value
        })));
    }
}
/**
 * Variant of the ModalPickerMultiple which can be used in a Jarb context.
 */
const JarbModalPickerMultiple = withJarb(ModalPickerMultiple);
/**
 * Variant of the ModalPickerMultiple which can be used in a final form.
 */
const FieldModalPickerMultiple = withField(ModalPickerMultiple);

function optionToTypeaheadOption(option, labelForOption) {
    const label = labelForOption(option);
    return { label, value: option };
}
function isTypeaheadCustomOption(option) {
    return option.customOption;
}

function useAutoSelectOptionWhenQueryMatchesExactly({ typeaheadOptions, onChange, query }) {
    // When value matches the query exactly select it.
    useEffect(() => {
        const lowerCasedQuery = query.toLowerCase();
        const selectedValue = typeaheadOptions.find(({ label }) => label.toLowerCase() === lowerCasedQuery);
        if (selectedValue) {
            onChange(selectedValue.value);
        }
    }, [typeaheadOptions, onChange, query]);
}

/**
 * The TypeaheadSingle is a form element which allows the user
 * to select an option by searching for them and selecting
 * them.
 *
 * The use case is that when there are too many options to render
 * in a simple Select you can use the TypeaheadSingle.
 *
 * Use the TypeaheadSingle when the user knows which option he / she
 * wants to select beforehand. Scenario: you are building a system
 * for a trading company, the users need to enter some trading code
 * from a big list. The users know each code by heart since they
 * work with the system daily. This is a nice use case for the TypeaheadSingle
 * because the user can type in faster than the can select from a
 * ModalPickerSingle.
 */
function TypeaheadSingle(props) {
    const { id = uniqueId(), label, hiddenLabel, placeholder, error, value, color, labelForOption, onFocus, valid, className = '', onChange, onBlur, options, keyForOption, isOptionEqual, reloadOptions, isOptionEnabled = alwaysTrue, pageSize = 10, maxResults = 100, allowNew, text = {} } = props;
    const [query, setQuery] = useState('');
    const { page, loading } = useOptions({
        options,
        value,
        keyForOption,
        isOptionEqual,
        labelForOption,
        reloadOptions,
        query,
        pageNumber: 1,
        size: Array.isArray(options) ? options.length : pageSize,
        optionsShouldAlwaysContainValue: false
    });
    const typeaheadOptions = page.content
        .filter((option) => isOptionEnabled(option))
        .map((option) => optionToTypeaheadOption(option, labelForOption));
    function doOnChange(values) {
        if (values.length === 0) {
            onChange(undefined);
        }
        else {
            const selectedOption = values[0];
            // @ts-expect-error Custom options are only available when allowNew is true, which causes the type of onChange param to match correctly
            onChange(isTypeaheadCustomOption(selectedOption) ? selectedOption : selectedOption.value);
        }
        // Due this: https://github.com/ericgio/react-bootstrap-typeahead/issues/224
        // If we invoke the input.onBlur at the typeahead onblur you would get this:
        // onBlur -> onChange, but it should be the other way around.
        // onBlur Should be called when the user navigates away from the input.
        // In this case when the user selects an item (onChange).
        doBlur(onBlur);
    }
    useAutoSelectOptionWhenQueryMatchesExactly({
        typeaheadOptions,
        onChange,
        query
    });
    const selected = [];
    if (value) {
        const option = optionToTypeaheadOption(value, labelForOption);
        selected.push(option);
    }
    const classes = classNames(className, {
        'is-invalid': valid === false
    });
    const typeaheadProps = {
        id: `${id}-options`,
        filterBy: alwaysTrue,
        multiple: false,
        placeholder,
        selected,
        options: typeaheadOptions,
        onChange: doOnChange,
        onFocus,
        paginationText: t({
            key: 'TypeaheadSingle.PAGINATION_TEXT',
            fallback: 'Display additional results...',
            overrideText: text.paginationText
        }),
        inputProps: {
            id,
            className: classNames({
                'is-invalid': valid === false
            }),
            'aria-label': hiddenLabel && typeof label === 'string' ? label : undefined
        },
        maxResults,
        allowNew,
        paginate: true
    };
    return (React.createElement(FormGroup, { className: classes, color: color },
        !hiddenLabel || typeof label !== 'string' ? React.createElement(Label, { for: id }, label) : null,
        React.createElement("div", { className: selected.length === 0 ? 'showing-placeholder' : '' }, Array.isArray(options) ? (React.createElement(Typeahead, Object.assign({}, typeaheadProps, { onInputChange: setQuery }))) : (React.createElement(AsyncTypeahead, Object.assign({}, typeaheadProps, { isLoading: loading, delay: 200, onSearch: setQuery })))),
        error));
}
/**
 * Variant of the TypeaheadSingle which can be used in a Jarb context.
 */
const JarbTypeaheadSingle = withJarb(TypeaheadSingle);
/**
 * Variant of the TypeaheadSingle which can be used in a final form.
 */
const FieldTypeaheadSingle = withField(TypeaheadSingle);

/**
 * The TypeaheadMultiple is a form element which allows the user
 * to select multiple options by searching for them and selecting
 * them.
 *
 * The use case is that when there are too many options to render
 * in a simple Select you can use the TypeaheadMultiple.
 *
 * Use the TypeaheadMultiple when the user knows which options he / she
 * wants to select beforehand. Scenario: you are building a system
 * for a trading company, the users need to enter some trading code
 * from a big list. The users know each code by heart since they
 * work with the system daily. This is a nice use case for the TypeaheadMultiple
 * because the user can type in faster than the can select from a
 * ModalPickerMultiple.
 */
function TypeaheadMultiple(props) {
    const { id = uniqueId(), label, hiddenLabel, placeholder, value, color, error, labelForOption, onFocus, valid, className = '', onChange, onBlur, options, keyForOption, isOptionEqual, reloadOptions, isOptionEnabled = alwaysTrue, pageSize = 10, maxResults = 100, allowNew, text = {} } = props;
    const [query, setQuery] = useState('');
    const { page, loading } = useOptions({
        options,
        value,
        keyForOption,
        isOptionEqual,
        labelForOption,
        reloadOptions,
        query,
        pageNumber: 1,
        size: Array.isArray(options) ? options.length : pageSize,
        optionsShouldAlwaysContainValue: false
    });
    const typeaheadOptions = page.content
        .filter((option) => isOptionEnabled(option))
        .filter((option) => !isOptionSelected({
        option,
        keyForOption,
        labelForOption,
        isOptionEqual,
        value
    }))
        .map((option) => optionToTypeaheadOption(option, labelForOption));
    function doOnChange(values) {
        if (values.length === 0) {
            onChange(undefined);
        }
        else {
            // @ts-expect-error Custom options are only available when allowNew is true, which causes the type of onChange param to match correctly
            onChange(values.map((option) => isTypeaheadCustomOption(option) ? option : option.value));
        }
        // Due this: https://github.com/ericgio/react-bootstrap-typeahead/issues/224
        // If we invoke the input.onBlur at the typeahead onblur you would get this:
        // onBlur -> onChange, but it should be the other way around.
        // onBlur Should be called when the user navigates away from the input.
        // In this case when the user selects an item (onChange).
        doBlur(onBlur);
    }
    function renderToken(option, props, index) {
        return (React.createElement(Tag, { key: index, text: option.label, onRemove: () => props.onRemove(option), className: "align-self-center" }));
    }
    let selected = [];
    if (value && value.length) {
        selected = value.map((v) => optionToTypeaheadOption(v, labelForOption));
    }
    const classes = classNames(className, {
        'is-invalid': valid === false
    });
    const typeaheadProps = {
        id: `${id}-options`,
        filterBy: alwaysTrue,
        paginationText: t({
            key: 'TypeaheadMultiple.PAGINATION_TEXT',
            fallback: 'Display additional results...',
            overrideText: text.paginationText
        }),
        multiple: true,
        placeholder,
        selected,
        options: typeaheadOptions,
        onChange: doOnChange,
        onFocus,
        inputProps: {
            id,
            className: classNames({
                'is-invalid': valid === false
            }),
            'aria-label': hiddenLabel && typeof label === 'string' ? label : undefined
        },
        maxResults,
        allowNew,
        paginate: true
    };
    return (React.createElement(FormGroup, { className: classes, color: color },
        !hiddenLabel || typeof label !== 'string' ? React.createElement(Label, { for: id }, label) : null,
        React.createElement("div", { className: selected.length === 0 ? 'showing-placeholder' : '' }, Array.isArray(options) ? (React.createElement(Typeahead, Object.assign({}, typeaheadProps, { 
            // @ts-expect-error onRemove is always present
            renderToken: renderToken, onInputChange: setQuery }))) : (React.createElement(AsyncTypeahead, Object.assign({}, typeaheadProps, { 
            // @ts-expect-error onRemove is always present
            renderToken: renderToken, isLoading: loading, delay: 200, onSearch: setQuery })))),
        error));
}
/**
 * Variant of the TypeaheadMultiple which can be used in a Jarb context.
 */
const JarbTypeaheadMultiple = withJarb(TypeaheadMultiple);
/**
 * Variant of the TypeaheadMultiple which can be used in a final form.
 */
const FieldTypeaheadMultiple = withField(TypeaheadMultiple);

function FileInput(props) {
    const { id = uniqueId(), label, hiddenLabel, accept, placeholder, error, color, value, valid, className = '', onBlur, onFocus } = props;
    const inputRef = useRef(null);
    function onChange(event) {
        const files = event.target.files;
        /* istanbul ignore else */
        if (files) {
            const file = files.item(0);
            props.onChange(file);
            doBlur(onBlur);
        }
    }
    function onClick(event) {
        // The 'delete' button is clicked in this case.
        if (value) {
            event.preventDefault();
            props.onChange(null);
            doBlur(onBlur);
            if (inputRef.current) {
                inputRef.current.value = '';
            }
        }
        // The input should now trigger the browsers file dialog
    }
    const name = get(value, 'name', '');
    const inputProps = {
        placeholder,
        value: name,
        invalid: valid === false ? true : undefined,
        onChange: () => console.error('The placeholder input of FileInput should not be changeable'),
        'aria-label': 'This is a placeholder input that should not be reachable. Use the file input instead.',
        tabIndex: -1
    };
    return (React.createElement(FormGroup, { className: `file-upload ${className}`, color: color },
        !hiddenLabel || typeof label !== 'string' ? React.createElement(Label, { for: id }, label) : null,
        React.createElement("input", { id: id, accept: accept, ref: inputRef, type: "file", onClick: onClick, onChange: onChange, onFocus: onFocus, "aria-label": hiddenLabel && typeof label === 'string' ? label : undefined }),
        React.createElement(InputGroup, null,
            React.createElement(Input$1, Object.assign({}, inputProps)),
            React.createElement(Button, { color: value || valid === false ? 'danger' : 'primary', onClick: noop },
                React.createElement(Icon, { icon: value ? 'delete' : 'cloud_upload', className: "d-block" }))),
        error));
}
/**
 * Variant of the FileInput which can be used in a Jarb context.
 */
const JarbFileInput = withJarb(FileInput);
/**
 * Variant of the FileInput which can be used in a final form.
 */
const FieldFileInput = withField(FileInput);
/**
 * Takes a `label` and returns a validator which can check if the
 * there is a `File`.
 *
 * @export
 * @param {string} label The label to display in the error message
 * @returns {(FieldValidator<File>)}
 */
function requireFile(label) {
    return (value) => {
        if (value) {
            return undefined;
        }
        return {
            key: 'FileInput.REQUIRED',
            data: { label },
            fallback: `${label} is required`
        };
    };
}
/**
 * Takes a `size` and `label` and returns a validator which can check if the
 * file is past the size limit.
 *
 * @export
 * @param {string} size The maximum size of the file in MB.
 * @param {string} label The label to display in the error message
 * @returns {(FieldValidator<File | null | undefined>)}
 */
function limitFileSize(size, label) {
    return (value) => {
        if (!(value instanceof File)) {
            return undefined;
        }
        // Older browsers will not support this, so we skip the check.
        /* istanbul ignore if */
        if (value.size === undefined) {
            return undefined;
        }
        const maxSizeInBytes = size * 1048576;
        if (value.size < maxSizeInBytes) {
            return undefined;
        }
        const fileSize = (value.size / 1048576).toFixed(1);
        const maxSizeDisplay = size.toFixed(1);
        return {
            key: 'FileInput.SIZE_TOO_LARGE',
            data: { label, size: maxSizeDisplay, fileSize },
            fallback: `${label} file is to large. Max size is ${maxSizeDisplay} MB file size is ${fileSize} MB`
        };
    };
}

Buffer.from('anything', 'base64');
let picaInstance;
function getPicaInstance() {
    if (picaInstance) {
        return picaInstance;
    }
    picaInstance = picaFn();
    return picaInstance;
}
function dataUrlToFile(dataUrl, fileName) {
    const byteString = Buffer.from(dataUrl.split(',')[1], 'base64');
    // separate out the mime component
    const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
    // write the bytes of the string to a typed array
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
        bytes[i] = byteString[i];
    }
    return new File([bytes], fileName, { type: mimeString });
}
function cropToAvatarEditorConfig(crop) {
    if (crop.type === 'rect') {
        const { width, height } = crop;
        return { borderRadius: 0, width, height };
    }
    else {
        const { size } = crop;
        /*
          When Crop is 'circle' a ridiculously high border radius is needed
          so crop.size of different sizes show a perfect circle. Not sure
          why this occurs.
        */
        const borderRadius = 9000000;
        return { borderRadius, width: size, height: size };
    }
}
// Calculate the next 'scale' to use based on how hard the user scrolled
function calculateScale(scale, delta) {
    const zoomFactor = Math.max(Math.abs(delta) / 1000, 0.1);
    const nextScale = delta > 0 ? scale - zoomFactor : scale + zoomFactor;
    return Math.min(10, Math.max(1, nextScale));
}
// Replace any part after the last dot in the filename with .png
function replaceFileExtension(fileName) {
    return fileName.replace(/\.\w+$/, '.png');
}
function readFile(file, callback) {
    const reader = new FileReader();
    reader.onloadend = () => {
        /* istanbul ignore else */
        if (typeof reader.result === 'string') {
            callback(reader.result);
        }
    };
    reader.readAsDataURL(file);
}

function ImageUpload(props) {
    const { crop, text, keepOriginalFileExtension = false, id = uniqueId(), value, onChange, onBlur, className, error, color, label, hiddenLabel } = props;
    const [mode, setMode] = useState('no-file');
    const [image, setImage] = useState();
    const inputRef = useRef(null);
    const editorRef = useRef(null);
    useEffect(() => {
        if (value && !image) {
            if (typeof value === 'string') {
                setImage({
                    src: value,
                    fileName: value.replace(/^.*[\\\/]/, ''),
                    rotate: 0,
                    scale: 1
                });
                setMode('file-selected');
            }
            else {
                readFile(value, (result) => {
                    setImage({
                        src: result,
                        fileName: value.name,
                        rotate: 0,
                        scale: 1
                    });
                    setMode('file-selected');
                });
            }
        }
    }, [value, image]);
    function imgSelected({ target: { files } }) {
        if (files) {
            const file = files[0];
            readFile(file, (result) => {
                setImage({
                    src: result,
                    fileName: file.name,
                    rotate: 0,
                    scale: 1
                });
                setMode('edit');
            });
        }
    }
    const cropStep = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        /* istanbul ignore if */
        if (!editorRef.current) {
            return;
        }
        const canvas = editorRef.current.getImage();
        const config = cropToAvatarEditorConfig(crop);
        const offScreenCanvas = document.createElement('canvas');
        offScreenCanvas.width = config.width < canvas.width ? config.width : canvas.width;
        offScreenCanvas.height = config.height < canvas.height ? config.height : canvas.height;
        // Let pica generate the cropped image as pica uses a far
        // better compression algorithm than the browsers do by default!
        return getPicaInstance().resize(canvas, offScreenCanvas, {
            alpha: true
        });
    }), [crop]);
    function onCrop() {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield cropImage();
            if (data) {
                afterCrop(data);
            }
        });
    }
    const cropImage = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!image) {
            return undefined;
        }
        const picaCanvas = yield cropStep();
        const dataUrl = picaCanvas.toDataURL('image/png', 1.0);
        const newFileName = keepOriginalFileExtension ? image.fileName : replaceFileExtension(image.fileName);
        const file = dataUrlToFile(dataUrl, newFileName);
        onChange(file);
        doBlur(onBlur);
        return { src: dataUrl, fileName: newFileName };
    }), [image, onChange, onBlur, cropStep, keepOriginalFileExtension]);
    function afterCrop({ src, fileName }) {
        setMode('file-selected');
        setImage({
            src,
            fileName,
            rotate: 0,
            scale: 1
        });
    }
    useEffect(() => {
        function imgSelectCrop() {
            return __awaiter(this, void 0, void 0, function* () {
                const data = yield cropImage();
                if (!data || !editorRef.current) {
                    return;
                }
                const canvas = editorRef.current.getImage();
                const config = cropToAvatarEditorConfig(crop);
                if (config.width >= canvas.width && config.height >= canvas.height) {
                    afterCrop(data);
                }
            });
        }
        if (mode === 'edit') {
            const timeout = setTimeout(() => {
                imgSelectCrop().catch(console.error);
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [mode, crop, cropImage]);
    function rotateLeft() {
        if (image) {
            setImage(Object.assign(Object.assign({}, image), { rotate: image.rotate - 90 }));
        }
    }
    function rotateRight() {
        if (image) {
            setImage(Object.assign(Object.assign({}, image), { rotate: image.rotate + 90 }));
        }
    }
    function changeScale(event) {
        event.preventDefault();
        if (image) {
            setImage(Object.assign(Object.assign({}, image), { scale: calculateScale(image.scale, event.deltaY) }));
        }
    }
    function resetFileInput() {
        setImage(undefined);
        setMode('no-file');
        onChange(null);
        doBlur(onBlur);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }
    function triggerFileInput() {
        resetFileInput();
        // Wait for the input to re-appear and click it
        setTimeout(() => {
            /* istanbul ignore else */
            if (inputRef.current) {
                inputRef.current.click();
            }
        }, 200);
    }
    return (React.createElement("div", { className: className },
        React.createElement(FormGroup, { color: color, className: "img-upload" },
            !hiddenLabel ? React.createElement(Label, { for: id }, label) : null,
            mode === 'file-selected' && image ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "d-flex justify-content-center" },
                    React.createElement("img", { style: { borderRadius: crop.type === 'rect' ? 0 : '50%' }, className: "img-fluid elevated-3", alt: typeof label === 'string' ? label : undefined, src: image.src })),
                React.createElement(FileSelectedButtons, { text: text, triggerFileInput: triggerFileInput, resetFileInput: resetFileInput }))) : mode === 'edit' && image ? (React.createElement(React.Fragment, null,
                React.createElement(Editor, { crop: crop, editorRef: editorRef, image: image, changeScale: changeScale }),
                React.createElement(EditButtons, { rotateLeft: rotateLeft, rotateRight: rotateRight, resetFileInput: resetFileInput, onCrop: onCrop }))) : (React.createElement(Fragment, null,
                React.createElement("input", { id: id, onChange: imgSelected, type: "file", accept: "image/*", ref: inputRef, "aria-label": hiddenLabel && typeof label === 'string' ? label : undefined }),
                React.createElement("div", { className: "img-upload-wrapper bg-faded text-muted" },
                    React.createElement(Icon, { icon: "add_a_photo" })))),
            error)));
}
function Editor({ crop, editorRef, image, changeScale }) {
    const config = cropToAvatarEditorConfig(crop);
    return (React.createElement("div", { className: "d-flex justify-content-center" },
        React.createElement("div", { "data-testid": "scroll-scale", onWheel: changeScale },
            React.createElement(AvatarEditor, { ref: editorRef, image: image.src, width: config.width, height: config.height, borderRadius: config.borderRadius, rotate: image.rotate, scale: image.scale }))));
}
function FileSelectedButtons({ text = {}, triggerFileInput, resetFileInput }) {
    return (React.createElement(FormGroup, { className: "text-center mt-1" },
        React.createElement(Button, { onClick: triggerFileInput, color: "primary", icon: "camera_roll" }, t({
            key: 'ImageUpload.CHANGE',
            fallback: 'Change',
            overrideText: text.change
        })),
        React.createElement(Button, { className: "ms-1", onClick: resetFileInput, color: "danger", icon: "delete" }, t({
            key: 'ImageUpload.REMOVE',
            fallback: 'Remove',
            overrideText: text.remove
        }))));
}
function EditButtons({ text = {}, rotateLeft, rotateRight, resetFileInput, onCrop }) {
    return (React.createElement(FormGroup, { className: "d-flex justify-content-center mt-1" },
        React.createElement(Button, { className: "mt-2", onClick: rotateLeft, color: "secondary", icon: "rotate_left" }),
        React.createElement(Button, { className: "ms-1 mt-2", onClick: rotateRight, color: "secondary", icon: "rotate_right" }),
        React.createElement(Button, { className: "ms-3", onClick: resetFileInput, color: "secondary", icon: "cancel" }, t({
            key: 'ImageUpload.CANCEL',
            fallback: 'Cancel',
            overrideText: text.cancel
        })),
        React.createElement(Button, { className: "ms-1", onClick: onCrop, color: "primary", icon: "done" }, t({
            key: 'ImageUpload.DONE',
            fallback: 'Done',
            overrideText: text.done
        }))));
}
/**
 * Variant of the ImageUpload which can be used in a Jarb context.
 */
const JarbImageUpload = withJarb(ImageUpload);
/**
 * Variant of the ImageUpload which can be used in a final form.
 */
const FieldImageUpload = withField(ImageUpload);
/**
 * Takes a `label` and returns a validator which can check if the
 * there is an image.
 *
 * @export
 * @param {string} label The label to display in the error message
 * @returns {ImageValidator}
 */
function requireImage(label) {
    return (value) => {
        if (value) {
            return undefined;
        }
        return {
            key: 'ImageUpload.REQUIRED',
            data: { label },
            fallback: `${label} is required`
        };
    };
}
/**
 * Takes a `size` and `label` and returns a validator which can check if the
 * image is past the size limit.
 *
 * @export
 * @param {string} size The maximum size of the file in MB.
 * @param {string} label The label to display in the error message
 * @returns {ImageValidator}
 */
function limitImageSize(size, label) {
    return (value) => {
        if (!(value instanceof File)) {
            return undefined;
        }
        // Older browsers will not support this, so we skip the check.
        /* istanbul ignore if */
        if (value.size === undefined) {
            return undefined;
        }
        const maxSizeInBytes = size * 1048576;
        if (value.size < maxSizeInBytes) {
            return undefined;
        }
        const fileSize = (value.size / 1048576).toFixed(1);
        const maxSizeDisplay = size.toFixed(1);
        return {
            key: 'ImageUpload.SIZE_TOO_LARGE',
            data: { label, size: maxSizeDisplay, fileSize },
            fallback: `${label} image is to large. Max size is ${maxSizeDisplay} MB image size is ${fileSize} MB`
        };
    };
}

/**
 * Toggle is a component which looks like a switch. Use the Toggle
 * instead of a checkbox, when you want the changes to take effect
 * immediately.
 */
function Toggle({ className, color, id = uniqueId(), value = false, onChange, onBlur, label, hiddenLabel }) {
    const toggleClasses = classNames('toggle-container', `toggle-${color}`, className);
    return (React.createElement("span", { className: toggleClasses },
        !hiddenLabel || typeof label !== 'string' ? (React.createElement("label", { className: "d-inline-block toggle-label me-2", htmlFor: id }, label)) : null,
        React.createElement("input", { id: id, type: "checkbox", onChange: (event) => onChange(event.target.checked), checked: value, onBlur: onBlur, "aria-label": hiddenLabel && typeof label === 'string' ? label : undefined })));
}

/**
 * CheckboxMultipleSelect is a form element for which the values can
 * be selected from a limited range. Is shown a grid of options as
 * checkboxes from which the user can select multiple values.
 *
 * If you pass a callback to the `options` property, be aware
 * that only 100 options will be displayed without pagination.
 * If you want to display more than 100 options,
 * you should use the ModalPickerMultiple instead.
 */
function CheckboxMultipleSelect(props) {
    const { label, hiddenLabel, error, color, text = {}, className = '', placeholder, value, isOptionEqual, labelForOption, reloadOptions, keyForOption, horizontal = false, isOptionEnabled = alwaysTrue, optionsShouldAlwaysContainValue = true, onChange, onBlur } = props;
    const { page, loading } = useOptions({
        options: props.options,
        value,
        isOptionEqual,
        labelForOption,
        reloadOptions,
        pageNumber: 1,
        query: '',
        size: Array.isArray(props.options) ? props.options.length : 100,
        optionsShouldAlwaysContainValue
    });
    function optionClicked(option, isSelected) {
        // Always copy the `value` so the `selected` is a fresh array.
        // Otherwise, the selection will be the same as the value, which
        // causes values to be committed and the cancel button will not
        // do anything.
        let selected = Array.isArray(value) ? [...value] : [];
        if (isSelected) {
            selected = selected.filter((value) => !isOptionSelected({
                option,
                keyForOption,
                labelForOption,
                isOptionEqual,
                value
            }));
        }
        else {
            selected.push(option);
        }
        onChange(selected);
        doBlur(onBlur);
    }
    return (React.createElement(FormGroup, { className: className, color: color },
        !hiddenLabel ? React.createElement(Label, null, label) : null,
        placeholder ? (React.createElement("p", null,
            React.createElement("em", null, placeholder))) : null,
        loading ? (React.createElement(Loading, null, t({
            key: 'Select.LOADING',
            fallback: 'Loading...',
            overrideText: text.loadingMessage
        }))) : (renderCheckboxes()),
        error));
    function renderCheckboxes() {
        if (horizontal) {
            return renderOptions({ options: page.content, horizontal: true });
        }
        else {
            const chunks = chunk(page.content, 10);
            return (React.createElement("div", { className: "d-flex flex-wrap" }, chunks.map((options, index) => {
                return (React.createElement("div", { className: "pe-3", key: index, style: { flexBasis: '300px' } }, renderOptions({ options, horizontal: false })));
            })));
        }
    }
    function renderOptions({ options, horizontal }) {
        return options.map((option, index) => {
            const label = labelForOption(option);
            const key = getKeyForOption({ option, keyForOption, labelForOption });
            const isSelected = isOptionSelected({
                option,
                keyForOption,
                labelForOption,
                isOptionEqual,
                value
            });
            return (React.createElement(FormGroup, { check: true, key: key, inline: horizontal, disabled: !isOptionEnabled(option) },
                React.createElement(Label, { check: true },
                    React.createElement(Input$1, { type: "checkbox", checked: isSelected, value: index, disabled: !isOptionEnabled(option), onChange: () => optionClicked(option, isSelected) }),
                    ' ',
                    label)));
        });
    }
}
/**
 * Variant of the CheckboxMultipleSelect which can be used in a Jarb context.
 */
const JarbCheckboxMultipleSelect = withJarb(CheckboxMultipleSelect);
/**
 * Variant of the CheckboxMultipleSelect which can be used in a final form.
 */
const FieldCheckboxMultipleSelect = withField(CheckboxMultipleSelect);

/**
 * RadioGroup is a form element for which the value can be selected
 * from a limited range.
 *
 * If you pass a callback to the `options` property, be aware
 * that only 100 options will be displayed without pagination.
 * If you want to display more than 100 options,
 * you should use the ModalPickerSingle instead.
 */
function RadioGroup(props) {
    const { label, hiddenLabel, value, error, color, text = {}, className = '', placeholder, onChange, onBlur, options, keyForOption, labelForOption, isOptionEqual, horizontal = false, canClear = false, isOptionEnabled = alwaysTrue, reloadOptions } = props;
    const { page, loading } = useOptions({
        options,
        value,
        isOptionEqual,
        labelForOption,
        reloadOptions,
        pageNumber: 1,
        query: '',
        size: Array.isArray(options) ? options.length : 100,
        optionsShouldAlwaysContainValue: true
    });
    function onRadioClicked(option) {
        onChange(option);
        doBlur(onBlur);
    }
    return (React.createElement(FormGroup, { tag: "fieldset", className: 'radio-group ' + className, color: color },
        !hiddenLabel ? React.createElement("legend", null, label) : null,
        placeholder ? (React.createElement("p", null,
            React.createElement("em", null, placeholder))) : null,
        loading ? (React.createElement(Loading, null, t({
            key: 'RadioGroup.LOADING',
            fallback: 'Loading...',
            overrideText: text.loadingMessage
        }))) : (React.createElement(React.Fragment, null,
            canClear && value ? (React.createElement("div", { className: "mb-1" },
                React.createElement(TextButton, { onClick: () => onChange(undefined) }, t({
                    key: 'RadioGroup.CLEAR',
                    fallback: 'Clear',
                    overrideText: text.clear
                })))) : null,
            page.content.map((option) => {
                const label = labelForOption(option);
                const key = getKeyForOption({
                    option,
                    keyForOption,
                    labelForOption
                });
                const isSelected = isOptionSelected({
                    option,
                    keyForOption,
                    labelForOption,
                    isOptionEqual,
                    value
                });
                return (React.createElement(FormGroup, { key: key, check: true, inline: horizontal, disabled: !isOptionEnabled(option) },
                    React.createElement(Label, { check: true },
                        React.createElement(Input$1, { type: "radio", value: label, checked: isSelected, disabled: !isOptionEnabled(option), onChange: () => onRadioClicked(option) }),
                        ' ',
                        label)));
            }))),
        error));
}
/**
 * Variant of the RadioGroup which can be used in a Jarb context.
 */
const JarbRadioGroup = withJarb(RadioGroup);
/**
 * Variant of the RadioGroup which can be used in a final form.
 */
const FieldRadioGroup = withField(RadioGroup);

/**
 *
 * The `ValuePicker` component is an abstraction which automatically
 * selects the best component to use when the user has to select a value
 * from a pre-defined list.
 *
 * This is the decision matrix:
 *
 * ```
 * | multiple | items <=3              | items <= 10             | items > 10
 * | true     | CheckboxMultipleSelect | CheckboxMultipleSelect  | ModalPickerMultiple
 * | false    | RadioGroup             | Select                  | ModalPickerSingle
 * ```
 *
 * `ValuePicker` starts in a booting state in which a spinner is shown.
 * During the booting state it will call `FetchOptionsCallback` and ask
 * for a `Page` of size `1` so it can get the `totalElements`.
 */
function ValuePicker(props) {
    const { text = {}, options, canClear = true } = props;
    const [booting, setBooting] = useState(true);
    const [totalElements, setTotalElements] = useState(0);
    useEffect(() => {
        function boot() {
            return __awaiter(this, void 0, void 0, function* () {
                if (Array.isArray(options)) {
                    setTotalElements(options.length);
                }
                else {
                    const page = yield options({ query: '', page: 1, size: 1 });
                    setTotalElements(page.totalElements);
                }
                setBooting(false);
            });
        }
        boot();
    }, [options]);
    // Until we know the number of totalElements, the ValuePicker is booting,
    // and we do not show anything to the user yet.
    if (booting) {
        return (React.createElement(Loading, { text: { loading: text.loadingMessage } }));
    }
    if (props.multiple) {
        const rest = __rest(props, ["multiple", "text"]);
        if (totalElements < 11) {
            return React.createElement(CheckboxMultipleSelect, Object.assign({}, rest));
        }
        else {
            return React.createElement(ModalPickerMultiple, Object.assign({ canClear: canClear }, rest));
        }
    }
    else {
        const rest = __rest(props, ["multiple", "text"]);
        if (totalElements < 4) {
            return React.createElement(RadioGroup, Object.assign({ canClear: canClear }, rest));
        }
        else if (totalElements < 11) {
            return React.createElement(Select, Object.assign({}, rest));
        }
        else {
            return React.createElement(ModalPickerSingle, Object.assign({ canClear: canClear }, rest));
        }
    }
}
/**
 * Variant of the ValuePicker which can be used in a Jarb context.
 */
const JarbValuePicker = withJarb(ValuePicker);
/**
 * Variant of the ValuePicker which can be used in a final form.
 */
const FieldValuePicker = withField(ValuePicker);

/**
 * IconPicker is a form element which allows the user to select one
 * of the material design icons. It is a popover which shows all
 * the icons and lets the user select one.
 */
function IconPicker(props) {
    const { label, hiddenLabel, icon, value, color, placeholder, className, error, onChange, onBlur, text = {}, canClear = true } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [query, setQuery] = useState('');
    const filteredIcons = icons.filter((icon) => icon.toLowerCase().startsWith(query.toLowerCase()));
    const iconsPage = pageOf(filteredIcons, pageNumber, 36);
    function onIconSelected(icon) {
        onChange(icon);
        doBlur(onBlur);
        setIsOpen(false);
    }
    function onSearch(term) {
        setQuery(term);
        setPageNumber(1);
    }
    const isEmpty = filteredIcons.length === 0;
    const classes = classNames('icon-picker', className);
    return (React.createElement(SearchInput, { defaultValue: query, onChange: onSearch, debounce: 0, label: t({
            key: 'IconPicker.SEARCH_INPUT_LABEL',
            fallback: 'Start typing to search an icon',
            overrideText: text.searchLabel
        }), placeholder: t({
            key: 'IconPicker.SEARCH',
            fallback: 'Search...',
            overrideText: text.searchPlaceholder
        }), hiddenLabel: true }, (searchInput, searchInputApi) => (React.createElement(FormGroup, { className: classes, color: color },
        !hiddenLabel ? React.createElement(Label, null, label) : null,
        React.createElement("div", { className: "d-flex" },
            value ? (React.createElement("div", { className: "d-flex justify-content-between" },
                React.createElement(Icon, { id: "icon-picker-value", className: "pt-2 me-3", icon: value }),
                canClear ? (React.createElement(TextButton, { onClick: () => onIconSelected(), className: "me-3" }, t({
                    key: 'IconPicker.CLEAR',
                    fallback: 'Clear',
                    overrideText: text.clear
                }))) : null)) : null,
            React.createElement(Tippy, { visible: isOpen, className: "border-0 tippy-popover", placement: "bottom", offset: [0, 7], interactive: true, zIndex: 1049, content: React.createElement(Card, { header: searchInput, footer: iconsPage.totalPages > 1 ? (React.createElement(Pager, { page: iconsPage, onChange: setPageNumber })) : undefined, cardFooterClassName: "bg-white" },
                    React.createElement("div", { style: { width: 240, height: 240, margin: 'auto' } }, !isEmpty ? (iconsPage.content.map((icon) => (React.createElement("span", { key: icon, className: "d-inline-block" },
                        React.createElement(Tooltip, { placement: "top", distance: 18, content: icon },
                            React.createElement(Icon, { className: "m-2", icon: icon, onClick: () => onIconSelected(icon) })))))) : (React.createElement(ContentState, { mode: "no-results", title: t({
                            key: 'IconPicker.NO_RESULTS.TITLE',
                            fallback: 'No icons found',
                            overrideText: text.noResultsTitle
                        }), subTitle: t({
                            key: 'IconPicker.NO_RESULTS.SUBTITLE',
                            fallback: 'No icons were found, please try again with a different query.',
                            overrideText: text.noResultsSubtitle
                        }) })))) },
                React.createElement(IconPickerButtonRef, { onClick: () => {
                        if (isOpen) {
                            searchInputApi.setValue('');
                        }
                        setIsOpen(!isOpen);
                    }, icon: icon, placeholder: placeholder }))),
        error))));
}
function IconPickerButton({ onClick, icon, placeholder }, ref) {
    return (React.createElement("button", { type: "button", className: "btn btn-primary", onClick: onClick, ref: ref },
        icon ? React.createElement(Icon, { icon: icon, className: "me-2 align-bottom" }) : null,
        placeholder));
}
const IconPickerButtonRef = forwardRef(IconPickerButton);
/**
 * Variant of the IconPicker which can be used in a Jarb context.
 */
const JarbIconPicker = withJarb(IconPicker);
/**
 * Variant of the IconPicker which can be used in a final form.
 */
const FieldIconPicker = withField(IconPicker);

/**
 * ColorPicker is a form element which allows the user to select a
 * color from a color picker. The color's format is in hex.
 */
function ColorPicker(props) {
    const { id = uniqueId(), label, hiddenLabel, value, color, placeholder, icon, className, error, onChange, onBlur, canClear = true, text = {} } = props;
    const [colorValue, setColorValue] = useState(value ? value : '#ffffff');
    const [isOpen, setIsOpen] = useState(false);
    function onColorSelected(newColor) {
        onChange(newColor);
        doBlur(onBlur);
        setIsOpen(false);
    }
    const classes = classNames('color-picker', className);
    return (React.createElement(FormGroup, { className: classes, color: color },
        !hiddenLabel || typeof label !== 'string' ? (React.createElement(Label, { for: id }, label)) : null,
        React.createElement("div", { className: "d-flex" },
            value ? (React.createElement("div", { className: "d-flex justify-content-between" },
                React.createElement("div", { className: "border p-1 me-3" },
                    React.createElement("div", { id: "color-picker-value", className: "h-100", style: { backgroundColor: value, width: 42 } })),
                canClear ? (React.createElement(TextButton, { onClick: () => onColorSelected(), className: "me-3" }, t({
                    key: 'ColorPicker.CLEAR',
                    fallback: 'Clear',
                    overrideText: text.clear
                }))) : null)) : null,
            React.createElement(Tippy, { visible: isOpen, className: "border-0 tippy-popover", placement: "bottom", offset: [0, 7], interactive: true, zIndex: 1049, content: React.createElement(Card, { cardBodyClassName: "p-0", footer: React.createElement("div", { className: "d-flex justify-content-between my-1 p-1" },
                        React.createElement(Button$1, { color: "secondary", onClick: () => {
                                const resetColorValue = value ? value : '#ffffff';
                                setColorValue(resetColorValue);
                                setIsOpen(false);
                            } }, t({
                            key: 'ColorPicker.CANCEL',
                            fallback: 'Cancel',
                            overrideText: text.cancel
                        })),
                        React.createElement(Button$1, { color: "primary", onClick: () => onColorSelected(colorValue) }, t({
                            key: 'ColorPicker.SELECT',
                            fallback: 'Select',
                            overrideText: text.select
                        }))) },
                    React.createElement(SketchPicker, { color: colorValue, onChange: (result) => setColorValue(result.hex) })) },
                React.createElement(ColorPickerButtonRef, { onClick: () => setIsOpen(!isOpen), icon: icon, placeholder: placeholder }))),
        error));
}
function ColorPickerButton({ onClick, icon, placeholder }, ref) {
    return (React.createElement("button", { type: "button", className: "btn btn-primary", onClick: onClick, ref: ref },
        icon ? React.createElement(Icon, { icon: icon, className: "me-2 align-bottom" }) : null,
        placeholder));
}
const ColorPickerButtonRef = forwardRef(ColorPickerButton);
/**
 * Variant of the ColorPicker which can be used in a Jarb context.
 */
const JarbColorPicker = withJarb(ColorPicker);
/**
 * Variant of the ColorPicker which can be used in a final form.
 */
const FieldColorPicker = withField(ColorPicker);

/**
 * Checkbox is a form element for when the value is a boolean.
 */
function Checkbox(props) {
    const { id = uniqueId(), onChange, onBlur, error, color, label, hiddenLabel, className = '', placeholder, value, valid, allowIndeterminate } = props;
    const checked = !!value;
    const invalid = valid === false ? true : undefined;
    function onClick() {
        onChange(!checked);
        doBlur(onBlur);
    }
    return (React.createElement(FormGroup, { check: true, className: className, color: color },
        React.createElement(Label, { for: id, check: true },
            React.createElement(Input$1, { id: id, type: "checkbox", checked: checked, onChange: onClick, valid: valid, invalid: invalid, innerRef: (e) => {
                    if (e && allowIndeterminate) {
                        // Value will be an empty string when coming from
                        // final-form. So we do a ts-expect-error to go around
                        // the fact that we say value is boolean | undefined.
                        // @ts-expect-error Accept that a checkbox event has an indeterminate
                        e.indeterminate = value === undefined || value === '';
                    }
                }, className: "mr-2", "aria-label": hiddenLabel && typeof label === 'string' ? label : undefined }),
            !hiddenLabel || typeof label !== 'string' ? label : null),
        placeholder ? (React.createElement("p", { className: "mb-0" },
            React.createElement("em", null, placeholder))) : null,
        error));
}
/**
 * Variant of the Checkbox which can be used in a Jarb context.
 */
const JarbCheckbox = withJarb(Checkbox);
/**
 * Variant of the Checkbox which can be used in a final form.
 */
const FieldCheckbox = withField(Checkbox);

function useMeterWidth(compliant) {
    const [meterWidth, setMeterWidth] = useState(0);
    useEffect(() => {
        const values = Object.values(compliant);
        const noCompliantRules = values.filter((value) => value).length;
        const noRules = values.length;
        setMeterWidth((noCompliantRules / noRules) * 100);
    }, [compliant, setMeterWidth]);
    return meterWidth;
}

function hasLowercase(value) {
    return !!value && /(?=.*[a-z])/.test(value);
}
function hasUppercase(value) {
    return !!value && /(?=.*[A-Z])/.test(value);
}
function hasNumber(value) {
    return !!value && /(?=.*[0-9])/.test(value);
}
function hasSpecialChar(value) {
    return !!value && /(?=.*[@#$%^&+=.,?!])/.test(value);
}
function hasMinimumLength(length, value) {
    return !!value && new RegExp(`.{${length},}`).test(value);
}
function hasNoSpaces(value) {
    return /^\S*$/.test(value !== null && value !== void 0 ? value : '');
}

function useRules(rules, password, minimumLength) {
    const [compliant, setCompliant] = useState(rules.reduce((obj, rule) => checkRule(obj, rule, password, minimumLength), {}));
    useEffect(() => {
        setCompliant(rules.reduce((obj, rule) => checkRule(obj, rule, password, minimumLength), {}));
    }, [password, rules, setCompliant, minimumLength]);
    return compliant;
}
function checkRule(obj, rule, value, minimumLength) {
    switch (rule) {
        case 'lowercase':
            return Object.assign(Object.assign({}, obj), { [rule]: hasLowercase(value) });
        case 'uppercase':
            return Object.assign(Object.assign({}, obj), { [rule]: hasUppercase(value) });
        case 'number':
            return Object.assign(Object.assign({}, obj), { [rule]: hasNumber(value) });
        case 'specialChar':
            return Object.assign(Object.assign({}, obj), { [rule]: hasSpecialChar(value) });
        case 'minimumLength':
            return Object.assign(Object.assign({}, obj), { [rule]: hasMinimumLength(minimumLength, value) });
        case 'noSpace':
            return Object.assign(Object.assign({}, obj), { [rule]: hasNoSpaces(value) });
    }
}

function PasswordStrength(props) {
    const { password, rules, minimumLength = 10, showMeter = true, text = {} } = props;
    const compliant = useRules(rules, password, minimumLength);
    const meterWidth = useMeterWidth(compliant);
    const id = uniqueId();
    return (React.createElement(React.Fragment, null,
        showMeter ? (React.createElement(React.Fragment, null,
            React.createElement("span", { className: "visually-hidden", id: id }, t({
                key: 'PasswordStrength.PROGRESS',
                fallback: 'Percentage of rules the password matches',
                overrideText: text.currentProgress
            })),
            React.createElement(Progress, { color: meterWidth >= 100
                    ? 'success'
                    : meterWidth >= 75
                        ? 'warning'
                        : 'danger', value: meterWidth, className: "mb-2", barAriaLabelledBy: id }))) : null,
        React.createElement("div", { className: "mb-2" }, rules.map((rule) => {
            const isCompliant = compliant[rule];
            return (React.createElement("div", { key: rule, className: "d-flex" },
                React.createElement(Icon, { icon: isCompliant ? 'check_circle' : 'cancel', color: isCompliant ? 'success' : 'danger', size: 16, className: "align-bottom mt-1 mb-1 me-3" }),
                React.createElement("div", null, t(labelForRule(rule, minimumLength)))));
        }))));
}
function labelForRule(rule, minimumLength) {
    switch (rule) {
        case 'lowercase':
            return {
                key: 'PasswordStrength.LOWERCASE',
                fallback: 'Must contain at least one lowercase letter'
            };
        case 'uppercase':
            return {
                key: 'PasswordStrength.UPPERCASE',
                fallback: 'Must contain at least one uppercase letter'
            };
        case 'number':
            return {
                key: 'PasswordStrength.NUMBER',
                fallback: 'Must contain at least one number'
            };
        case 'specialChar':
            return {
                key: 'PasswordStrength.SPECIAL_CHAR',
                data: { specialCharacters: '@#$%^&+=.,?!' },
                fallback: 'Must contain at least one special character (@#$%^&+=.,?!)'
            };
        case 'minimumLength':
            return {
                key: 'PasswordStrength.MINIMUM_LENGTH',
                data: { minimumLength },
                fallback: `Must contain at least ${minimumLength} characters`
            };
        case 'noSpace':
            return {
                key: 'PasswordStrength.NO_SPACE',
                fallback: 'Must not contain any space'
            };
    }
}

function NewPasswordInput(props) {
    var _a;
    const rules = getRulesFromProps(props);
    const inputProps = omit(props, [
        'lowercase',
        'uppercase',
        'number',
        'specialCharacter',
        'minimumLength',
        'noSpace',
        'showMeter'
    ]);
    inputProps.type = 'password';
    const passwordStrengthProps = pick(props, ['minimumLength', 'showMeter']);
    return (React.createElement("div", { className: "new-password-input" },
        React.createElement(Input, Object.assign({}, inputProps)),
        React.createElement(PasswordStrength, Object.assign({ password: (_a = inputProps.value) !== null && _a !== void 0 ? _a : '', rules: rules }, passwordStrengthProps))));
}
/**
 * Variant of the FileInput which can be used in a Jarb context.
 */
const JarbNewPasswordInput = withJarb(NewPasswordInput);
/**
 * Variant of the FileInput which can be used in a final form.
 */
const FieldNewPasswordInput = withField(NewPasswordInput);
function getRulesFromProps(props) {
    const { lowercase = true, uppercase = true, number = true, specialCharacter = true, minimumLength = 10, noSpace = true } = props;
    const rules = [];
    if (lowercase) {
        rules.push('lowercase');
    }
    if (uppercase) {
        rules.push('uppercase');
    }
    if (number) {
        rules.push('number');
    }
    if (specialCharacter) {
        rules.push('specialChar');
    }
    if (minimumLength > 0) {
        rules.push('minimumLength');
    }
    if (noSpace) {
        rules.push('noSpace');
    }
    return rules;
}
/**
 * Takes `rules` and a minimum length and returns a validator which can check if
 * the password complies to the rules.
 *
 * @export
 * @param {Rule[]} rules The rules the password should comply to.
 * @param {number} minimumLength The minimum length of the password.
 * @returns {(FieldValidator<string>)}
 */
function isStrongPassword(rules, minimumLength) {
    return (value) => {
        const compliant = rules.map((rule) => {
            switch (rule) {
                case 'lowercase':
                    return hasLowercase(value);
                case 'uppercase':
                    return hasUppercase(value);
                case 'number':
                    return hasNumber(value);
                case 'specialChar':
                    return hasSpecialChar(value);
                case 'minimumLength':
                    return hasMinimumLength(minimumLength, value);
                case 'noSpace':
                    return hasNoSpaces(value);
            }
        });
        if (compliant.every((value) => value)) {
            return undefined;
        }
        return {
            key: 'NewPasswordInput.NOT_STRONG_ENOUGH',
            fallback: 'Password does not follow rules'
        };
    };
}

/**
 * The FormButton component is used to submit forms from a distance.
 * Distance meaning that the FormButton is not rendered inside a <form>.
 * Sometimes this happens when doing more advanced layouts.
 *
 * The FormButton will when clicked search for the <form> with the
 * `formId` prop and will submit that form.
 *
 * It is used when using the EpicForm component.
 */
function FormButton(_a) {
    var { formId, type } = _a, rest = __rest(_a, ["formId", "type"]);
    function onClick() {
        var _a;
        (_a = document
            .getElementById(formId)) === null || _a === void 0 ? void 0 : _a.dispatchEvent(new Event(type, { cancelable: true, bubbles: true }));
    }
    const buttonProps = Object.assign({ type, onClick }, rest);
    return React.createElement(Button, Object.assign({}, buttonProps));
}

/**
 * Not all users know that an interaction design element can have
 * a disabled state. Disabled elements have poor contrast and thus
 * are not as readable as they should. Also, screen readers don't
 * give users as much information about the state of the element
 * and keyboard navigation gets a little weird, causing confusion to
 * those using a screen reader. Users might also think the form
 * element might be enabled by changing another form element or in
 * a different use case. So to prevent users confusion or thinking
 * the form is invalid, you should display the value without letting
 * it look like a form element. To prevent alignment issues, the
 * PlainTextFormControl uses spacing as if it were a form element,
 * without the looks of a form element.
 */
function PlainTextFormControl({ label, children, className, valueClassName, labelClassName }) {
    const valueClasses = classNames('form-control-plaintext', valueClassName);
    return (React.createElement(FormGroup, { className: className },
        label ? React.createElement(Label, { className: labelClassName }, label) : null,
        React.createElement("div", { className: valueClasses }, children)));
}

/* istanbul ignore file */
// The EpicTable is really large and difficult to test via unit tests.
// Therefore, there are a ton of stories for e2e testing instead. So
// that is why the EpicTable is ignored by istanbul.
/**
 * GooeyCenter is a table which has a fixed left or right or both
 * left and right, and a gooey center which takes up the remaining
 * space. No matter how little content is left for the center
 * the left and right are always visible.
 *
 * The center gets a horizontal scrollbar for when the content
 * is too large to fit in the center.
 *
 * GooeyCenter is not meant for consumers of the library and is
 * a private component.
 */
function GooeyCenter({ left, right, center, onScroll, showScrollbar = true, onCenterWidthChanged, forceCenterAlwaysVisible = false }) {
    const wrapperEl = useRef(null);
    const centerEl = useRef(null);
    const [centerWidth, setCenterWidth] = useState(0);
    useEffect(function trackCenterWidthBasedOnResize() {
        const calculateCenterWidth = debounce(() => {
            if (wrapperEl.current) {
                const left = wrapperEl.current.children[0];
                const right = wrapperEl.current.children[2];
                const rightWidth = right ? right.clientWidth : 0;
                const centerWidth = wrapperEl.current.clientWidth - left.clientWidth - rightWidth;
                setCenterWidth(centerWidth);
                if (onCenterWidthChanged) {
                    onCenterWidthChanged(centerWidth);
                }
            }
        }, 200);
        window.addEventListener('resize', calculateCenterWidth);
        window.addEventListener('resizeEpicTables', calculateCenterWidth);
        // Calculate at least once on startup
        calculateCenterWidth();
        return () => {
            window.removeEventListener('resize', calculateCenterWidth);
            window.removeEventListener('resizeEpicTables', calculateCenterWidth);
        };
    }, [setCenterWidth, onCenterWidthChanged, left, center, right]);
    function handleScroll(instance, event) {
        if (onScroll && event) {
            onScroll(event.target.scrollLeft);
        }
    }
    const centerWrapper = (React.createElement("div", { ref: centerEl, 
        // Making it invisible when not fully calculated, prevents
        // the content from instantly resizing and flickering.
        // Also, the width must be undefined for this to work.
        className: centerWidth === 0 && !forceCenterAlwaysVisible ? 'invisible' : '', style: { width: centerWidth !== 0 ? centerWidth : undefined } }, center));
    return (React.createElement("div", { className: "d-flex", ref: wrapperEl },
        left,
        center && showScrollbar ? (React.createElement(OverlayScrollbarsComponent, { className: "w-100", options: {
                scrollbars: {
                    visibility: 'visible'
                }
            }, events: {
                scroll: handleScroll
            } }, centerWrapper)) : (centerWrapper),
        right));
}

/* istanbul ignore file */
// The EpicTable is really large and difficult to test via unit tests.
// Therefore, there are a ton of stories for e2e testing instead. So
// that is why the EpicTable is ignored by istanbul.
/**
 * Calculates which header is rendered closest to the FixedHeader by
 * index
 *
 * Used to determine which header the FixedHeader should take as
 * its appearance.
 */
function useClosestHeaderIndex(fixedHeaderEl, headers) {
    const [index, setIndex] = useState(-1);
    useEffect(() => {
        function onScroll() {
            if (fixedHeaderEl.current && headers.length > 0) {
                const fakeRect = fixedHeaderEl.current.getBoundingClientRect();
                const headersAboveFakeHeader = headers.filter(({ ref }) => {
                    const headerRect = ref.getBoundingClientRect();
                    return fakeRect.bottom > headerRect.bottom;
                });
                if (headersAboveFakeHeader && headersAboveFakeHeader.length > 0) {
                    const lastIndex = headersAboveFakeHeader.length - 1;
                    setIndex(headersAboveFakeHeader[lastIndex].index);
                }
                else {
                    setIndex(-1);
                }
            }
        }
        window.addEventListener('scroll', onScroll, true);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [fixedHeaderEl, headers]);
    return index;
}

/* istanbul ignore file */
// The EpicTable is really large and difficult to test via unit tests.
// Therefore, there are a ton of stories for e2e testing instead. So
// that is why the EpicTable is ignored by istanbul.
/**
 * The FixedHeader is a header which follows the user as he scrolls.
 * The FixedHeader is not meant for consumers of this library and
 * is an internal component.
 */
function FixedHeader({ centerWidth, leftScroll, layout, headers }) {
    const fixedHeaderEl = useRef(null);
    const fixedCenterEl = useRef(null);
    useEffect(() => {
        if (fixedCenterEl.current) {
            fixedCenterEl.current.scrollLeft = leftScroll;
        }
    }, [fixedCenterEl, leftScroll]);
    const { left, center, right } = layout;
    const index = useClosestHeaderIndex(fixedHeaderEl, headers);
    const showFixedHeader = index !== -1;
    return (React.createElement("div", { className: "epic-table-fixed-header", ref: fixedHeaderEl }, showFixedHeader ? (React.createElement(GooeyCenter, { forceCenterAlwaysVisible: true, left: left && left[index] ? left[index].header : null, center: center && center[index] ? (React.createElement("div", { ref: fixedCenterEl, className: "d-flex justify-content-between overflow-hidden", style: { width: centerWidth } }, center[index].header)) : null, right: right && right[index] ? right[index].header : null, showScrollbar: false })) : null));
}

/**
 * The EpicExpanderRow is a row inside an EpicTable which can be used
 * to show details whenever a row is clicked. It displays itself over
 * under the cell which is expanded.
 *
 * Often used in combination with the EpicExpander widget component.
 */
function EpicExpanderRow(_a) {
    var { children, active, height } = _a, rest = __rest(_a, ["children", "active", "height"]);
    if (!active) {
        return null;
    }
    const { width } = rest;
    return (React.createElement("div", { className: "epic-table-expander-row", style: {
            minWidth: width,
            width,
            height
        } }, children()));
}

function epicDetailRow(_a, ref) {
    var { children, active, left } = _a, rest = __rest(_a, ["children", "active", "left"]);
    if (!active) {
        return null;
    }
    const { width, top, height } = rest;
    return (React.createElement("div", { ref: ref, className: "epic-table-detail-row", style: {
            minWidth: width,
            width,
            height,
            left,
            top
        } }, children()));
}
/**
 * The EpicDetailRow is a row inside an EpicTable which can be used
 * to show details whenever a row is clicked. It displays itself over
 * the rest of the EpicTable on the left.
 *
 * Often used in combination with the EpicDetail widget component.
 */
const EpicDetailRow = forwardRef(epicDetailRow);

/**
 * The EpicCell is used inside a EpicRow to render content in.
 * It can be seen as the EpicTable's variant of the `<td>` element.
 */
function EpicCell(_a) {
    var { children, width, height = 44 } = _a, rest = __rest(_a, ["children", "width", "height"]);
    const ref = useRef(null);
    const { odd, onRowClick, hover, onHoverChanged } = rest;
    const classes = classNames('epic-table-cell border-bottom py-1 px-2', {
        'epic-table-cell--odd': odd,
        'epic-table-cell--hover': hover
    });
    function handleOnRowClick(event) {
        /*
          Due to our mangeling of the DOM for the EpicTable to work. The
          normal way to prevent events from bubbling will not work via
          event.preventDefault. This is because of Reacts SyntheticEvent
          events not working like normal DOM events:
          https://github.com/facebook/react/issues/1691
    
          So in order to prevent `onRowClick` from responding to events
          which came from its children, we only call `onRowClick` when
          the `div` of the `EpicCell` itself is clicked.
        */
        if (onRowClick && ref.current === event.target) {
            onRowClick(event);
        }
    }
    return (React.createElement("div", { ref: ref, onMouseEnter: onRowClick ? () => onHoverChanged(true) : undefined, onMouseLeave: onRowClick ? () => onHoverChanged(false) : undefined, onClick: onRowClick ? handleOnRowClick : undefined, className: classes, style: {
            minWidth: width,
            width,
            height
        } }, children));
}

/* istanbul ignore file */
// The EpicTable is really large and difficult to test via unit tests.
// Therefore, there are a ton of stories for e2e testing instead. So
// that is why the EpicTable is ignored by istanbul.
/**
 * The user is given a semantic based API by the EpicTable. This api
 * for the user of the library feels very nice (at least that is the goal).
 *
 * Unfortunately the semantic UI is not a data structure which can be
 * used to render the table directly. The `epicTableLayout` therefore
 * takes the semantic UI and transforms it into a data structure which
 * can actually be rendered.
 *
 * The problem is this: to create a fixed left, a gooey center and
 * a fixed right, you need three <div>`s next to each other. One
 * job of the `epicTableLayout` is to bucket the semantic structure
 * in a left, right and center.
 *
 * So given this structure:
 *
 * ```tsx
 *  <EpicTable>
 *    <EpicRow header>
 *      <EpicHeader width={300} height={44}>
 *        Name
 *      </EpicHeader>
 *      <EpicHeader width={100} height={44}>
 *        Age
 *      </EpicHeader>
 *      <EpicHeader width={100} height={44}>
 *        Eye color
 *      </EpicHeader>
 *      <EpicHeader width={300} height={44}>
 *        Actions
 *      </EpicHeader>
 *    </EpicRow>
 *    <EpicRow>
 *      <EpicCell width={300} height={44}>
 *        Maarten
 *      </EpicCell>
 *      <EpicCell width={100} height={44}>
 *        30
 *      </EpicCell>
 *      <EpicCell width={100} height={44}>
 *        Brown
 *      </EpicCell>
 *      <EpicCell width={300} height={44}>
 *        <Button icon="delete" /> <Button icon="edit" />
 *      </EpicCell>
 *    </EpicRow>
 *     <EpicRow>
 *      <EpicCell width={300} height={44}>
 *        Jeffrey
 *      </EpicCell>
 *      <EpicCell width={100} height={44}>
 *        28
 *      </EpicCell>
 *      <EpicCell width={100} height={44}>
 *        Greenish
 *      </EpicCell>
 *      <EpicCell width={300} height={44}>
 *        <Button icon="delete" /> <Button icon="edit" />
 *      </EpicCell>
 *    </EpicRow>
 *  </EpicTable>;
 * ```
 * It should create the following EpicTableLayout:
 *
 * ```tsx
 * {
 *   containsActiveDetailRow: false,
 *   left: [{
 *     header: [(
 *       <EpicHeader width={300} height={44}>
 *         Name
 *       </EpicHeader>
 *     )],
 *     contents: [[(
 *       <EpicCell width={300} height={44}>
 *         Maarten
 *       </EpicCell>
 *     )], [(
 *       <EpicCell width={300} height={44}>
 *         Jeffrey
 *       </EpicCell>
 *     )]]
 *   }],
 *   center: [{
 *     headers: [(
 *       <EpicHeader width={100} height={44}>
 *         Age
 *       </EpicHeader>
 *     ), (
 *       <EpicHeader width={100} height={44}>
 *         Eye color
 *       </EpicHeader>
 *     )]
 *     contents: [[(
 *       <EpicCell width={100} height={44}>
 *          30
 *        </EpicCell>
 *     ), (
 *       <EpicCell width={100} height={44}>
 *          Brown
 *       </EpicCell>
 *     )], [(
 *       <EpicCell width={100} height={44}>
 *          28
 *        </EpicCell>
 *     ), (
 *       <EpicCell width={100} height={44}>
 *          Greenish
 *       </EpicCell>
 *     )]]
 *   }],
 *   right: [{
 *     header: [(
 *       <EpicHeader width={300} height={44}>
 *         Actions
 *       </EpicHeader>
 *     )],
 *     contents: [[(
 *       <EpicCell width={300} height={44}>
 *         <Button icon="delete" /> <Button icon="edit" />
 *       </EpicCell>
 *     )], [(
 *       <EpicCell width={300} height={44}>
 *         <Button icon="delete" /> <Button icon="edit" />
 *       </EpicCell>
 *     )]]
 *   }]
 * }
 *
 * To bucket the rows it will hunt in the EpicTable's children for the
 * following rows: `EpicRow`, `EpicExpanderRow` and `EpicDetailRow`.
 * Each row type will be processed in a different way, see the helper
 * functions on how.
 */
function epicTableLayout(config) {
    const { children, epicTableRect, hasRight, activeDetailRowChanged, hoveredRow, hoverChanged } = config;
    // Will contain all the first columns as sections.
    const left = [];
    // Will contain the middle columns as sections.
    const center = [];
    // Will contain all the last columns as sections.
    const right = [];
    let leftSection = { header: [], contents: [] };
    let centerSection = { header: [], contents: [] };
    let rightSection = { header: [], contents: [] };
    let totalDesiredCenterWidth = -1;
    let totalDesiredCenterHeight = -1;
    // Increases whenever an epic-row is encountered, used to determine
    // if the row is striped. Needs a separate counter because other
    // rows can be mixed with epic-row's.
    let epicRowNumber = 1;
    Children.forEach(getRows(children), (row) => {
        if (row.type === EpicExpanderRow && epicTableRect !== null) {
            return handleExpanderRow(row, epicTableRect);
        }
        if (row.type === EpicDetailRow && epicTableRect !== null) {
            return handleEpicDetailRow(row, epicTableRect);
        }
        handleEpicRow(row, epicRowNumber);
        epicRowNumber += 1;
    });
    left.push(leftSection);
    if (centerSection.header.length > 0 || centerSection.contents.length > 0) {
        center.push(centerSection);
    }
    if (rightSection.header.length > 0 || rightSection.contents.length > 0) {
        right.push(rightSection);
    }
    // Calculate the total desired height by looping through all left
    // headers and cells and sum their height
    const totalDesiredHeight = left.reduce((totalHeight, section) => {
        const header = section.header[0];
        if (header) {
            // @ts-expect-error Accept that header has props
            totalHeight += header.props.height;
        }
        const cellHeight = section.contents.reduce((cellsHeight, cells) => {
            const cell = cells[0];
            // @ts-expect-error Accept that the cell has a type
            if (cell && cell.type === EpicCell) {
                // @ts-expect-error Accept that the cell has props
                return cellsHeight + cell.props.height;
            }
            else {
                // In this case the cell is an EpicDetailRow or
                // an EpicExpander spacer. We ignore these heights for now.
                // In case of the expander this is tricky, but you should
                // not use expanders and details as the same time for now.
                return cellsHeight;
            }
        }, 0);
        return totalHeight + cellHeight;
    }, 0);
    return {
        left,
        center,
        right,
        totalDesiredCenterWidth,
        totalDesiredHeight
    };
    // Impure helper function for handling EpicRow's
    function handleEpicRow(row, index) {
        // The children of the row are the cells of the row.
        const cells = row.props.children;
        const lastCellInRowIndex = Children.count(cells) - 1;
        const { header: isHeader, onClick } = row.props;
        // When new header starts end the section and start a new one.
        if (isHeader) {
            if (leftSection !== null) {
                if (sectionHasData(leftSection)) {
                    left.push(leftSection);
                }
                if (sectionHasData(centerSection)) {
                    center.push(centerSection);
                }
                if (sectionHasData(rightSection)) {
                    right.push(rightSection);
                }
            }
            // Reset for the next section.
            leftSection = { header: [], contents: [] };
            centerSection = { header: [], contents: [] };
            rightSection = { header: [], contents: [] };
            epicRowNumber = 1;
        }
        // These will contain all non header cells
        const leftRow = [];
        const centerRow = [];
        const rightRow = [];
        const isRowOdd = index % 2 === 1;
        // Put all cells in the correct bucket
        Children.forEach(cells, (cell, cellIndex) => {
            const left = cellIndex === 0;
            const right = hasRight && cellIndex === lastCellInRowIndex;
            const clone = cloneElement(cell, {
                key: cellIndex,
                odd: isRowOdd,
                hover: hoveredRow === index,
                onRowClick: onClick,
                onHoverChanged: (value) => hoverChanged(index, value)
            });
            // The first cell should be bucketed on the left
            if (left) {
                if (isHeader) {
                    leftSection.header.push(clone);
                }
                else {
                    leftRow.push(clone);
                }
            }
            // The last cell should be bucketed on the right, if there is a right
            else if (right) {
                if (isHeader) {
                    rightSection.header.push(clone);
                }
                else {
                    rightRow.push(clone);
                }
            }
            // All cells in the center (or when there is no right) should be
            // bucketed in the center.
            else {
                if (isHeader) {
                    centerSection.header.push(clone);
                }
                else {
                    centerRow.push(clone);
                }
            }
        });
        if (leftRow.length > 0) {
            leftSection.contents.push(leftRow);
        }
        if (centerRow.length > 0) {
            // Only calculate it once for the first center row encountered.
            if (totalDesiredCenterWidth === -1) {
                totalDesiredCenterWidth = centerRow.reduce((acc, cell) => {
                    return acc + cell.props.width;
                }, 0);
            }
            if (totalDesiredCenterHeight === -1) {
                totalDesiredCenterHeight = centerRow.reduce((acc, cell) => {
                    return acc + cell.props.width;
                }, 0);
            }
            centerSection.contents.push(centerRow);
        }
        if (rightRow.length > 0) {
            rightSection.contents.push(rightRow);
        }
    }
    // Impure helper function for handling EpicDetailRow's
    function handleEpicDetailRow(row, rect) {
        // Clone the element because we are injecting extra props.
        const clone = cloneElement(row, {
            width: rect.width - row.props.left,
            top: 0,
            left: row.props.left,
            key: 42,
            ref: activeDetailRowChanged
        });
        // Now add the detail row on the left section. Even thought it
        // is only added on the left, it will actually not be displayed
        // on the left, but instead it will cover the entire EpicTable
        // sans the `left` property of the `EpicDetailRow`
        // It needs to be added somewhere :P so why not on the left
        leftSection.contents.push([clone]);
    }
    // Impure helper function for handling ExpanderRow's
    function handleExpanderRow(row, rect) {
        // Clone the element because we are injecting extra props.
        const clone = cloneElement(row, { width: rect.width, key: 1337 });
        // Now add the expander row on the left section. Even thought it
        // is only added on the left, it will take up the space of the
        // center and right as well because it is as large as the width
        // of the rect.
        // It needs to be added somewhere :P so why not on the left
        leftSection.contents.push([clone]);
        if (row.props.active) {
            // Whenever the ExpanderRow is active, we need to add a fake
            // row which pushes all other rows down. This is achieved by
            // pushing in `div`'s with the same height as the ExpanderRow.
            // If we do not add the spacer, it will overlap existing rows
            // and the user cannot see them anymore.
            const spacer = createElement('div', {
                style: { height: row.props.height }
            });
            leftSection.contents.push([cloneElement(spacer, { key: 1 })]);
            rightSection.contents.push([cloneElement(spacer, { key: 2 })]);
            centerSection.contents.push([cloneElement(spacer, { key: 3 })]);
        }
    }
}
// Function which gathers all Row's inside the EpicTable.
// Also unpacks fragments at one level deep to help the user map
// over array's
function getRows(children) {
    const rows = [];
    Children.forEach(children, (child) => {
        if (isFragment(child)) {
            child.props.children.forEach((row) => {
                rows.push(row);
            });
        }
        else {
            rows.push(child);
        }
    });
    return rows;
}
function sectionHasData(section) {
    return section.contents.length > 0 || section.header.length > 0;
}

/* istanbul ignore file */
// The EpicTable is really large and difficult to test via unit tests.
// Therefore, there are a ton of stories for e2e testing instead. So
// that is why the EpicTable is ignored by istanbul.
function useEpicTableRect(epicTableEl) {
    const [epicTableRect, setEpicTableRect] = useState(null);
    useEffect(() => {
        requestAnimationFrame(() => {
            if (epicTableEl.current) {
                const rect = epicTableEl.current.getBoundingClientRect();
                if (JSON.stringify(rect) !== JSON.stringify(epicTableRect)) {
                    setEpicTableRect(rect);
                }
            }
        });
    });
    return epicTableRect;
}

/* istanbul ignore file */
// The EpicTable is really large and difficult to test via unit tests.
// Therefore, there are a ton of stories for e2e testing instead. So
// that is why the EpicTable is ignored by istanbul.
/**
 * Calculates the height of the EpicTable. Taking into account
 * the existence of an active EpicDetailRow or overlay.
 *
 * When an EpicDetailRow exists it may be larger than the actual
 * EpicTable. So we increase the height of the EpicTable in that
 * case to be the height of the EpicDetailRow.
 *
 * Also, the minHeight can be lower than the totalDesiredHeight. In
 * that case the totalDesiredHeight should be used instead. As the
 * minHeight is merely a suggestion.
 */
function useCalculateActualHeight({ minHeight, totalDesiredHeight, activeDetailRow, overlayEl, headerHeight }) {
    const [actualHeight, setActualHeight] = useState(minHeight);
    useEffect(() => {
        function resize() {
            const detailRowHeight = activeDetailRow
                ? activeDetailRow.scrollHeight
                : 0;
            const overlayHeight = overlayEl
                ? overlayEl.scrollHeight + headerHeight
                : 0;
            const height = Math.max(minHeight, detailRowHeight, totalDesiredHeight, overlayHeight);
            setActualHeight(height);
        }
        /*
          When there is an active DetailRow then resize the content
          after every 20 milliseconds. This needs to be done so that
          when the DetailRow becomes larger after it has been rendered
          the EpicTable scales with it.
     
          Unfortunately when the detail row becomes smaller later on we
          lose the ability to shrink the detail row. This is because when
          we set the `style.height` of the detail row we cannot access
          the needed size again via the `scrollHeight`.
        */
        let interval = -1;
        if (activeDetailRow) {
            interval = window.setInterval(resize, 20);
        }
        resize();
        return () => {
            window.clearInterval(interval);
        };
    }, [minHeight, totalDesiredHeight, activeDetailRow, overlayEl, headerHeight]);
    return actualHeight;
}

/* istanbul ignore file */
// The EpicTable is really large and difficult to test via unit tests.
// Therefore, there are a ton of stories for e2e testing instead. So
// that is why the EpicTable is ignored by istanbul.
/**
 * Changes the height of the detailRow, so it always fills up to
 * the height of the EpicTable.
 */
function useAdjustHeightOfActiveDetailRow({ activeDetailRow, actualHeight }) {
    useEffect(() => {
        if (activeDetailRow) {
            activeDetailRow.style.height = `${actualHeight}px`;
        }
    }, [actualHeight, activeDetailRow]);
}

/* istanbul ignore file */
// The EpicTable is really large and difficult to test via unit tests.
// Therefore, there are a ton of stories for e2e testing instead. So
// that is why the EpicTable is ignored by istanbul.
/**
 * The EpicTable is a table based on Andrew Colyle's article
 * "Design better data tables" which can be found here:
 * https://uxdesign.cc/design-better-data-tables-4ecc99d23356
 *
 * The features the EpicTable supports:
 *
 *   1. Fixed headers which follow the user.
 *   2. A fixed left column which follows the user.
 *   3. Optionally a fixed right column which follows the user.
 *   4. Expanding rows which can contain extra data.
 *   5. Click to go to details inside the table.
 *   6. Selection of rows, and a select all.
 *   7. Filtering per column.
 *   8. Resizing of columns.
 *   9. Multiple headers
 *  10. Zebra stripes.
 *
 * See the stories in the documentation for detailed examples.
 *
 * That said there are a couple of rules:
 *
 *   1. Do not render anything inside the EpicTable which is not
 *      one of the row's. The EpicTable will not understand those, and
 *      it will error.
 *
 *   2. The EpicTable can contain fragments, and will unpack those, but
 *      only one level deep. Those fragments should contain only row's.
 */
function EpicTable({ children, minHeight = 200, hasRight = true, overlay, striped = true }) {
    const epicTableEl = useRef(null);
    const overlayEl = useRef(null);
    const [centerWidth, setCenterWidth] = useState(0);
    const [leftScroll, setLeftScroll] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [activeDetailRow, setActiveDetailRow] = useState(null);
    const [hoveredRow, setHoveredRow] = useState(-1);
    const headerRefs = [];
    function registerHeader(ref, index) {
        if (ref) {
            headerRefs.push({ ref, index });
            setHeaderHeight(ref.clientHeight);
        }
    }
    function activeDetailRowChanged(detailRowRef) {
        setActiveDetailRow(detailRowRef);
    }
    function hoverChanged(index, value) {
        if (value) {
            setHoveredRow(index);
        }
        else {
            setHoveredRow(-1);
        }
    }
    const epicTableRect = useEpicTableRect(epicTableEl);
    const layout = epicTableLayout({
        children,
        epicTableRect,
        hasRight,
        activeDetailRowChanged,
        hoveredRow,
        hoverChanged
    });
    const { center, right, left, totalDesiredCenterWidth, totalDesiredHeight } = layout;
    const desiredWidthMet = totalDesiredCenterWidth < centerWidth;
    const classes = classNames('epic-table', {
        'epic-table--striped': striped,
        // Making it invisible when not fully calculated, prevents
        // the content from instantly resizing and flickering.
        invisible: centerWidth === 0
    });
    const containsActiveDetailRow = activeDetailRow !== null;
    const leftAndContainerHaveShadow = !desiredWidthMet && !overlay;
    const rightHasShadow = leftAndContainerHaveShadow && !containsActiveDetailRow;
    const actualHeight = useCalculateActualHeight({
        minHeight,
        totalDesiredHeight,
        activeDetailRow,
        overlayEl: overlayEl.current,
        headerHeight
    });
    useAdjustHeightOfActiveDetailRow({ activeDetailRow, actualHeight });
    return (React.createElement("div", { ref: epicTableEl, className: classes, style: { minHeight: actualHeight } },
        React.createElement("div", { className: `epic-table-container ${leftAndContainerHaveShadow ? 'shadow' : ''}` },
            overlay && epicTableRect ? (React.createElement("div", { ref: overlayEl, className: "epic-table-overlay shadow", style: {
                    top: headerHeight,
                    minHeight: epicTableRect.height - headerHeight,
                    width: epicTableRect.width
                } }, overlay)) : null,
            React.createElement(FixedHeader, { centerWidth: centerWidth, headers: headerRefs, leftScroll: leftScroll, layout: layout }),
            React.createElement(GooeyCenter, { onCenterWidthChanged: setCenterWidth, onScroll: setLeftScroll, left: React.createElement("div", { className: leftAndContainerHaveShadow ? 'shadow' : '', style: { minHeight: actualHeight } }, left.map((section, index) => (React.createElement(Fragment, { key: index },
                    React.createElement("div", { ref: (ref) => registerHeader(ref, index), tabIndex: 0 }, section.header),
                    section.contents)))), center: 
                // Do not render a center when there is an activeDetailRow
                // to prevent scrollbars from rendering
                center && center.length > 0 && !activeDetailRow
                    ? center.map((section, index) => (React.createElement(Fragment, { key: index },
                        React.createElement("div", { className: "d-flex justify-content-between" }, section.header),
                        section.contents.map((row, index) => (React.createElement("div", { key: index, className: "d-flex justify-content-between", tabIndex: 0 }, row))))))
                    : null, right: hasRight && right && right.length > 0 ? (React.createElement("div", { className: rightHasShadow ? 'shadow' : '' }, right &&
                    right.map((section, index) => (React.createElement(Fragment, { key: index },
                        section.header,
                        section.contents))))) : null }))));
}

const listenerConfig = {
    capture: true,
    passive: false
};

function useEpicResizeListenerCleanup(resize, resizeEnd) {
    // Remove any event listeners still dangling when the EpicResize is
    // re-rendered to prevent memory leaks.
    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', resize, listenerConfig);
            window.removeEventListener('mouseup', resizeEnd, listenerConfig);
        };
    }, [resize, resizeEnd]);
}

/**
 * The EpicResize component is a part of the EpicHeader. It contains
 * all the logic for resizing the EpicHeader. The EpicResize itself
 * is a tiny block rendered next to the EpicHeader, when the user
 * hovers over it the mouse icon will become a `col-resize`.
 */
function EpicResize({ width, onResize, minWidth }) {
    // Throttle the onResize so the resizing becomes smooth. If we do
    // not throttle the resize will become very jarring / jittery.
    const throttledResize = useRef(throttle(onResize, 40));
    // Store the original width of when the EpicHeader was first rendered
    // when minWidth is not specified to prevent resizing only allowing
    // columns to grow.
    const minimumWidth = useRef(minWidth !== null && minWidth !== void 0 ? minWidth : width);
    // When the onResize changes re-init the throttle.
    useEffect(() => {
        throttledResize.current = throttle(onResize, 40);
    }, [onResize]);
    // Stores the width of the element when the resize first started.
    const widthOnResizeStart = useRef(0);
    // Store the x position of the mouse when the resize first started.
    const mouseXOnResizeStart = useRef(0);
    const resize = useCallback((event) => {
        const distance = event.clientX - mouseXOnResizeStart.current;
        const nextWidth = widthOnResizeStart.current + distance;
        const boundedWidth = Math.max(minimumWidth.current, nextWidth);
        throttledResize.current(boundedWidth);
        event.preventDefault();
    }, []);
    const resizeEnd = useCallback(() => {
        // Reset the cursor back to default
        document.body.style.cursor = 'default';
        // The page can now be selected again
        document.body.classList.remove('user-select-none');
        window.removeEventListener('mousemove', resize, listenerConfig);
    }, [resize]);
    const resizeStart = useCallback((event, width) => {
        widthOnResizeStart.current = width;
        mouseXOnResizeStart.current = event.clientX;
        // The user can very quickly mouse beyond the EpicResize element
        // when this happens the cursor will become normal again. To
        // prevent this we simply set the cursor for the entire page to
        // col-resize until the user is done resizing.
        document.body.style.cursor = 'col-resize';
        // Prevent selection of text for the entire page until the user is
        // done resizing.
        document.body.classList.add('user-select-none');
        window.addEventListener('mousemove', resize, listenerConfig);
        window.addEventListener('mouseup', resizeEnd, listenerConfig);
        // You might think, why not do the onMouseMove and onMouseUp events
        // on the <div> itself. The reason is that the user will move
        // mouse faster than the elements width can grow. Plus if the
        // user moves a little too much on the Y axis he no longer hovers
        // over the <div>. So we simply listen to mouse events on the
        // entire document instead until the mouse goes up. This way the
        // user doesn't have to have 100% accuracy to resize the element.
    }, [resize, resizeEnd]);
    useEpicResizeListenerCleanup(resize, resizeEnd);
    return (React.createElement("div", { className: "epic-table-header-resizeable me-n2 d-flex align-items-center h-100", onMouseDown: (e) => resizeStart(e, width), "data-testid": "epic-table-header-resize" },
        React.createElement(Icon, { icon: "more_vert" })));
}

/**
 * The EpicHeader is used inside a EpicRow to render headers with
 * It can be seen as the EpicTable's variant of the `<th>` element.
 *
 * It is resizable whenever the `onResize` callback is defined.
 */
function EpicHeader({ children, width, minWidth, height, onResize }) {
    return (React.createElement("div", { className: "epic-table-header d-flex align-items-center justify-content-between p-2", style: {
            minWidth: width,
            width,
            height
        } },
        children,
        onResize ? (React.createElement(EpicResize, { minWidth: minWidth, width: width, onResize: onResize })) : null));
}

/**
 * The EpicCellLayout is used inside a EpicHeader or EpicCell to
 * define the layout of that cell.
 *
 * It currently supports two modes: `vertical` and `horizontal`.
 *
 * The `vertical` mode will take the full height and width and
 * positions the children vertically whilst padding them in the top
 * and bottom.
 *
 * The `horizontal` mode will take the full width and positions the
 * children horizontally and places the maximum space in between
 * the children. This means that when two children are present one
 * will be on the utmost left and the other on the utmost right.
 *
 * Basically a small wrapper around the `d-flex` utilities of bootstrap.
 */
function EpicCellLayout({ children, mode }) {
    if (mode === 'vertical') {
        return (React.createElement("div", { className: "d-flex flex-column w-100 h-100 justify-content-around" }, children));
    }
    else {
        return (React.createElement("div", { className: "d-flex w-100 justify-content-between align-items-center" }, children));
    }
}

/**
 * The EpicForm is a wrapper around react-final-form's `Form` to
 * allow forms to be used inside an EpicRow.
 */
function EpicForm(props) {
    const { id, children, width, height, submitOnChange, odd } = props, formProps = __rest(props, ["id", "children", "width", "height", "submitOnChange", "odd"]);
    const classes = classNames('epic-table-form border-bottom p-1', {
        'epic-table-form--odd': odd
    });
    return (React.createElement(Form, Object.assign({}, formProps), ({ handleSubmit, form }) => (React.createElement("form", { id: id, onSubmit: handleSubmit, onReset: () => form.reset(), className: classes, style: {
            minWidth: width,
            width,
            height
        } },
        submitOnChange ? (React.createElement(AutoSave, { onSave: form.submit, initialValues: formProps.initialValues })) : null,
        children))));
}

/**
 * The EpicFormCell is used inside a EpicForm to render form inputs in.
 * It can be seen as the EpicForm's variant of the `<td>` element.
 */
function EpicFormCell({ children, width, height }) {
    return (React.createElement("div", { className: 'epic-table-form-cell border-bottom py-1 pe-1', style: {
            minWidth: width,
            width,
            height,
            paddingLeft: '0px'
        } }, children));
}

/**
 * The EpicRow is the EpicTable's version of a `<tr>`. It has no inherent
 * behavior / UI of its own but is used to by the EpicTable to divide
 * the table into rows.
 *
 * When the row is a header the EpicTable will collect al subsequent
 * EpicRow's which are not headers underneath the header, until it
 * finds another EpicRow which is a header. This creates so-called
 * sections.
 */
function EpicRow({ tabIndex, children }) {
    return React.createElement("span", { tabIndex: tabIndex }, children);
}

/**
 * EpicDetail is a white pane with a close button which can be used
 * to render in a EpicDetailRow as its content.
 */
function EpicDetail({ children, onClose, text = {} }) {
    return (React.createElement("div", { className: "bg-white h-100 border-top border-end border-bottom py-1 shadow" },
        React.createElement("div", { className: "d-flex align-items-center border-bottom" },
            React.createElement(Icon, { className: "text-muted py-2 px-1", onClick: onClose, icon: "close" }),
            React.createElement("span", { className: "text-muted" }, t({
                key: 'EpicTable.EpicDetail.CLOSE',
                fallback: 'Close',
                overrideText: text.close
            }))),
        React.createElement("div", { className: "p-2" }, children)));
}

/**
 * The EpicExpander is a button which can be used to open up an
 * EpicExpanderRow.
 */
function EpicExpander({ open, onChange }) {
    const icon = open ? 'expand_less' : 'expand_more';
    return React.createElement(Button, { color: "dark", icon: icon, onClick: () => onChange(!open) });
}

/**
 * The EpicSelection is basically a checkbox for the EpicTable.
 * It can be used to select rows inside the table or even cells.
 */
function EpicSelection({ children, checked, onChange }) {
    return (React.createElement(Label, { className: "d-flex align-items-center ps-2 py-1 my-n1 ms-n2 clickable h-auto" },
        React.createElement(Input$1, { type: "checkbox", className: "m-0 me-2 flex-shrink-0 flex-grow-0 d-inline-block position-static clickable", checked: checked, onChange: () => onChange(!checked) }),
        children));
}

function nextDirection(direction) {
    if (direction === 'ASC') {
        return 'DESC';
    }
    else if (direction === 'DESC') {
        return 'ASC';
    }
    else {
        return 'ASC';
    }
}

/**
 * The EpicSort is a button which shows the user a sort direction. When
 * the user clicks the button it will go to the next direction.
 *
 * The directions go from:
 *
 *  NONE -> ASC -> DESC
 *           ^       |
 *           |--------
 */
function EpicSort({ direction, onChange }) {
    return (React.createElement(Icon, { className: "py-1 ps-2", onClick: () => onChange(nextDirection(direction)), icon: iconForDirection(direction) }));
}
function iconForDirection(direction) {
    if (direction === 'ASC') {
        return 'arrow_drop_up';
    }
    else if (direction === 'DESC') {
        return 'arrow_drop_down';
    }
    else {
        return 'sort';
    }
}

/**
 * CrudTable is an EpicTable wrapper to make it easier to display a table
 * with CRUD options. If page is defined, it also includes pagination.
 * It should be filled with `EpicRow`s, where the header row should be filled
 * with `CrudHeader`s.
 *
 * It supports working with a `Page` from `@42.nl/spring-connect`.
 */
function CrudTable(props) {
    const { buttons, canSearch = () => true, searchValue = '', onSearch, hiddenSearchLabel = true, renderSelection, page, pageChanged, text = {}, children } = props;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "d-flex justify-content-end mb-2" }, buttons ? buttons() : null),
        canSearch() && onSearch ? (React.createElement(SearchInput, { label: t({
                key: 'CrudTable.SEARCH_LABEL',
                fallback: 'Start typing to search',
                overrideText: text.searchLabel
            }), hiddenLabel: hiddenSearchLabel, className: "mb-4", defaultValue: searchValue, onChange: onSearch })) : null,
        renderSelection ? renderSelection() : null,
        React.createElement(EpicTable, { minHeight: 400 }, children),
        page && pageChanged ? (React.createElement("div", { className: "d-flex justify-content-center" },
            React.createElement(Pagination, { className: "my-3", page: page, onChange: pageChanged }))) : null));
}

/**
 * CrudHeader is used to display a header in the EpicTable or CrudTable.
 * If onSort is defined, a sorting button is displayed next to the label.
 * If onSearch is defined, a search field is displayed below the label.
 */
function CrudHeader({ direction = 'NONE', onSort, onSearch, searchValue, options, labelForOption, width, height, onResize, searchLabel, children }) {
    const header = onSort ? (React.createElement(EpicCellLayout, { mode: "horizontal" },
        children,
        React.createElement(EpicSort, { direction: direction, onChange: onSort }))) : (React.createElement(React.Fragment, null, children));
    if (onSearch) {
        return (React.createElement(EpicHeader, { width: width, height: height !== null && height !== void 0 ? height : 88, onResize: onResize },
            React.createElement(EpicCellLayout, { mode: "vertical" },
                header,
                options && labelForOption ? (React.createElement(Select, { value: searchValue, options: options, labelForOption: labelForOption, onChange: onSearch, label: searchLabel, hiddenLabel: true, className: "mb-1" })) : (React.createElement(Input
                // @ts-expect-error Default value type is a string
                , { 
                    // @ts-expect-error Default value type is a string
                    value: searchValue, 
                    // @ts-expect-error Default value type is a string
                    onChange: onSearch, type: "search", label: searchLabel, hiddenLabel: true, className: "mb-1" })))));
    }
    return (React.createElement(EpicHeader, { width: width, height: height, onResize: onResize }, header));
}

/**
 * This hook returns the bootstrap size that is based on the screen width breakpoints.
 *
 * if a component needs to render differently on mobile in comparison to desktop,
 * this hook can help.
 *
 * @example:
 * ```tsx
 * function ComponentWithMobile() {
 *  const { isMobile } = useBootstrapSize();
 *
 *  // Other hooks/functions
 *
 *  if (isMobile) {
 *    return (
 *      // Render for mobile
 *    )
 *  }
 *
 *  return (
 *    // Render for desktop
 *  )
 * }
 * ```
 */
function useBootstrapSize() {
    const [bootstrapSize, setBootstrapSize] = useState(getBootstrapSize(window.innerWidth));
    useEffect(() => {
        window.addEventListener('resize', updateWindowDimensions);
        window.addEventListener('orientationchange', updateWindowDimensions);
        return () => {
            window.removeEventListener('resize', updateWindowDimensions);
            window.removeEventListener('orientationchange', updateWindowDimensions);
        };
    });
    function updateWindowDimensions() {
        setBootstrapSize(getBootstrapSize(window.innerWidth));
    }
    return {
        bootstrapSize,
        isMobile: isMobile(bootstrapSize),
        isTablet: isTablet(bootstrapSize),
        isDesktop: isDesktop(bootstrapSize)
    };
}
function isMobile(size) {
    return ['xs', 'sm'].includes(size);
}
function isTablet(size) {
    return ['md'].includes(size);
}
function isDesktop(size) {
    return ['lg', 'xl', 'xxl'].includes(size);
}
function getBootstrapSize(width) {
    if (width < 576) {
        return 'xs';
    }
    else if (width >= 576 && width < 768) {
        return 'sm';
    }
    else if (width >= 768 && width < 992) {
        return 'md';
    }
    else if (width >= 992 && width < 1200) {
        return 'lg';
    }
    else if (width >= 1200 && width < 1400) {
        return 'xl';
    }
    else {
        return 'xxl';
    }
}

export { AddonIcon, AsyncActionButton, AsyncContent, AsyncList, AsyncPage, AttributeList, AttributeView, AutoSave, Avatar, AvatarStack, BooleanIcon, Button, Card, CardOpenClose, Checkbox, CheckboxMultipleSelect, ColorPicker, ConfirmButton, ConfirmModal, ContentState, CopyToClipboard, CrudHeader, CrudTable, DateTimeInput, Debug, EpicCell, EpicCellLayout, EpicDetail, EpicDetailRow, EpicExpander, EpicExpanderRow, EpicForm, EpicFormCell, EpicHeader, EpicPagination, EpicRow, EpicSelection, EpicSort, EpicTable, ErrorBoundary, FavoriteIcon, FieldCheckbox, FieldCheckboxMultipleSelect, FieldColorPicker, FieldDateTimeInput, FieldFileInput, FieldIconPicker, FieldImageUpload, FieldInput, FieldModalPickerMultiple, FieldModalPickerSingle, FieldNewPasswordInput, FieldRadioGroup, FieldSelect, FieldTextEditor, FieldTextarea, FieldTypeaheadMultiple, FieldTypeaheadSingle, FieldValuePicker, FileInput, FlashMessage, FormButton, FormError, Icon, IconPicker, ImageUpload, InfoBadge, InfoTooltip, Input, JarbCheckbox, JarbCheckboxMultipleSelect, JarbColorPicker, JarbDateTimeInput, JarbFileInput, JarbIconPicker, JarbImageUpload, JarbInput, JarbModalPickerMultiple, JarbModalPickerSingle, JarbNewPasswordInput, JarbRadioGroup, JarbSelect, JarbTextEditor, JarbTextarea, JarbTypeaheadMultiple, JarbTypeaheadSingle, JarbValuePicker, Loading, LoadingPage, Modal, ModalPicker, ModalPickerMultiple, ModalPickerSingle, MoreOrLess, NavigationItem, NewPasswordInput, OpenClose, OpenCloseModal, OrSeparator, Pager, Pagination, PlainTextFormControl, Popover, ProgressStepper, RadioGroup, SearchInput, Select, Spinner, SubmitButton, SuccessIcon, Tab, Tabs, Tag, TextButton, TextEditor, Textarea, Toggle, Tooltip, TypeaheadMultiple, TypeaheadSingle, ValuePicker, configure, errorMessage, isDate, isDateAfter, isDateAfterValidator, isDateBefore, isDateBeforeValidator, isDateBetween, isDateBetweenValidator, isDateValidator, isStrongPassword, isTypeaheadCustomOption, limitFileSize, limitImageSize, requireFile, requireImage, setTranslator, t, useBootstrapSize, useHover, useShowAfter, useShowSpinner, withField, withJarb };
